import { fetchWmJson } from './wmReadRequest';
import { driverGpsTestHeaders } from './driverGpsTest';
import { bidBoardAuthHeaders, signedInBidUserId } from './bidBoardAuth';
/**
 * Waste Management — dumpster rental jobs linking crew lead, workers, and customers.
 */

import { hostWebApp } from './hostWebApp';
import { isUsableGps, usableGpsPair } from '../../shared/geo';
import { legacyAuthHeader, legacyHostName } from '../utils/legacyHostCompat';
import { getNativeIosLinkedUser, resolveNativeApiUrl } from './nativeApiRouting';
import {
  wmFormatLockedGateNoteLine,
  wmLockedGateCodeIsValid,
  wmNormalizeLockedGateCode,
} from '../../shared/wmLockedGate';

export {
  wmExtractLockedGateCode,
  wmFormatLockedGateNoteLine,
  wmJobRevealsLockedGate,
  wmLockedGateCodeIsValid,
  wmNormalizeLockedGateCode,
  wmRedactLockedGateInNotes,
  WM_LOCKED_GATE_MAX_DIGITS,
  WM_LOCKED_GATE_MIN_DIGITS,
} from '../../shared/wmLockedGate';

const API_BASE = '/api/waste-management';
const LEGACY_USER_ID = legacyAuthHeader('User-Id');
const LEGACY_USERNAME = legacyAuthHeader('Username');
const LEGACY_INIT_DATA = legacyAuthHeader('Init-Data');

/**
 * Dumpster box sizes (cubic yards). IDs keep historical `*ft` suffixes
 * used in D1 (`30ft` / `50ft`); labels show yards.
 * Thompson Hauling & Transport: **only 30 yd and 50 yd** offered for new orders.
 * Other size ids remain in the type for legacy jobs already in D1.
 */
export type WasteDumpsterSize =
  | '10ft'
  | '12ft'
  | '15ft'
  | '20ft'
  | '25ft'
  | '30ft'
  | '40ft'
  | '50ft';

/**
 * Active catalog — full industry roll-off range (10–50 yd).
 * Each hauler's rate card narrows what THEY stock via sizesOffered.
 */
export const WM_DUMPSTER_SIZES: WasteDumpsterSize[] = [
  '10ft', '12ft', '15ft', '20ft', '25ft', '30ft', '40ft', '50ft',
];
export type WasteJobRole = 'customer' | 'worker' | 'crew_lead';
export type WasteJobStatus =
  | 'requested'
  | 'assigned'
  | 'en_route_dropoff'
  | 'dropped_off'
  | 'pickup_requested'
  | 'en_route_pickup'
  | 'picked_up'
  | 'cancelled';

export interface WasteDumpsterJob {
  id: string;
  crew_id: string | null;
  company_name: string | null;
  customer_org: string | null;
  site_name: string | null;
  /** Preferred drop day (YYYY-MM-DD) — reminder / Accept clock. */
  delivery_date?: string | null;
  dumpster_size: WasteDumpsterSize;
  status: WasteJobStatus;
  requested_lat: number | null;
  requested_lng: number | null;
  requested_orientation: string | null;
  requested_notes: string | null;
  rental_days?: number | null;
  amount_cents?: number | null;
  tons_included?: number | null;
  payment_status?: 'unpaid' | 'pending' | 'paid' | 'failed' | string | null;
  stripe_checkout_session_id?: string | null;
  /** 12% Zia platform fee (bps, default 1200) */
  platform_fee_bps?: number | null;
  platform_fee_cents?: number | null;
  /** Stripe processing fee (hauler share absorbs this) */
  stripe_fee_cents?: number | null;
  /** 1 when stored processing remains an estimate; 0 after verified provider fee. */
  stripe_fee_estimated?: number | null;
  /** Stored hauler net under this job's fee policy; new work also deducts card processing. */
  contractor_payout_cents?: number | null;
  overage_platform_fee_cents?: number | null;
  overage_stripe_fee_cents?: number | null;
  overage_contractor_payout_cents?: number | null;
  /** Quote breakdown (cents) — set on create / hauler accept re-quote */
  haul_cents?: number | null;
  delivery_cents?: number | null;
  addl_usage_cents?: number | null;
  quote_locked_at?: string | null;
  hauler_quote_notes?: string | null;
  product_line?: string | null;
  waste_stream?: string | null;
  service_kind?: string | null;
  overage_per_ton_cents?: number | null;
  scale_weight_tons?: number | null;
  overage_tons?: number | null;
  overage_cents?: number | null;
  overage_payment_status?: 'unpaid' | 'pending' | 'paid' | 'disputed' | 'waived' | string | null;
  overage_stripe_session_id?: string | null;
  /** Last emailed / issued overage Checkout URL */
  overage_checkout_url?: string | null;
  overage_invoice_email?: string | null;
  overage_dispute_reason?: string | null;
  overage_dispute_at?: string | null;
  overage_dispute_by_user_id?: string | null;
  /** Canonical customer email for receipts + overage invoices */
  customer_contact_email?: string | null;
  customer_stripe_id?: string | null;
  overage_stripe_invoice_id?: string | null;
  overage_invoice_number?: string | null;
  overage_invoice_pdf_url?: string | null;
  /** Multi-dumpster cart: jobs sharing one Stripe checkout */
  order_group_id?: string | null;
  cart_line_index?: number | null;
  /** Optional customer draw path JSON for driver placement */
  highlight_path_json?: string | null;
  dropoff_lat: number | null;
  dropoff_lng: number | null;
  dropoff_orientation: string | null;
  dropoff_serial: string | null;
  dropoff_serial_photo_url: string | null;
  /**
   * Driver drop proof set (5 photos): full_box, doors_face, interior_empty,
   * access_path, serial_plate. JSON [{ kind, url, photoId? }] for galleries —
   * interior_empty proves the box was dropped empty for the customer.
   */
  dropoff_proof_photo_urls?: string | null;
  dropoff_at: string | null;
  dropoff_accuracy_m?: number | null;
  pickup_requested_at: string | null;
  pickup_at: string | null;
  pickup_lat?: number | null;
  pickup_lng?: number | null;
  pickup_accuracy_m?: number | null;
  pickup_serial_confirmed?: string | null;
  pickup_photo_url?: string | null;
  /**
   * Driver pickup proof: 4 exterior + 1 interior.
   * JSON [{ kind, url, photoId? }] for driver record + company gallery.
   */
  pickup_proof_photo_urls?: string | null;
  /**
   * Driver-reported dumpster/trailer issues (JSON array of WmEquipmentIssueReport).
   * Roll-off 30/50 yd: box damage + trailer hydraulics/brakes/tires/etc.
   */
  equipment_issues_json?: string | null;
  /**
   * Landfill proof JSON: string[] of urls (legacy) or
   * { kind: 'scale_ticket'|'payment_slip', url, photoId? }[]
   */
  landfill_receipt_photo_urls?: string | null;
  landfill_receipt_at?: string | null;
  landfill_receipt_by_user_id?: string | null;
  landfill_ticket_number?: string | null;
  landfill_receipt_notes?: string | null;
  /** Dumpster plate serial re-entered at landfill (required) */
  landfill_serial?: string | null;
  /** Payment Authorized confirmation # from debit slip (optional typed) */
  landfill_payment_confirmation?: string | null;
  /** Green scale slip: Manual Gross / Tare / Net (lb) + line items */
  landfill_gross_lb?: number | null;
  landfill_tare_lb?: number | null;
  landfill_net_lb?: number | null;
  landfill_unit?: string | null;
  landfill_description?: string | null;
  landfill_rate_cents?: number | null;
  landfill_extension_cents?: number | null;
  landfill_total_net_cents?: number | null;
  landfill_lat?: number | null;
  landfill_lng?: number | null;
  unpaid_drop_allowed?: number | null;
  /** pending_ops | paid_out | hold | null — Connect not shipped; ops pays out of band */
  hauler_payout_status?: string | null;
  hauler_payout_at?: string | null;
  hauler_payout_note?: string | null;
  hauler_payout_by_user_id?: string | null;
  drop_accepted_at?: string | null;
  drop_accepted_by_user_id?: string | null;
  drop_problem_at?: string | null;
  drop_problem_note?: string | null;
  drop_problem_by_user_id?: string | null;
  /** Driver rejected the haul — banned items in the box (ext table). */
  load_rejected_at?: string | null;
  load_rejected_by_user_id?: string | null;
  load_rejected_note?: string | null;
  load_rejected_items?: string | null;
  load_rejected_photo_url?: string | null;
  load_remediated_at?: string | null;
  load_remediated_by_user_id?: string | null;
  cancel_refund_status?: string | null;
  cancel_stripe_refund_id?: string | null;
  cancel_refund_cents?: number | null;
  cancel_refund_at?: string | null;
  cancel_tier?: string | null;
  /** Recurring service (weeks–months): cadence + calendar end date */
  service_frequency?: 'project' | 'recurring' | string | null;
  recurrence_cadence?: string | null;
  recurrence_end_date?: string | null;
  /** Hauler truck unit shown on customer Apple / Telegram status texts */
  truck_number?: string | null;
  /** Roll-off trailer unit paired with truck on this run */
  trailer_number?: string | null;
  /** Driver name shown on customer status texts (e.g. Shaun) */
  driver_display_name?: string | null;
  /** Live truck heartbeat while employee is en route in-app */
  driver_lat?: number | null;
  driver_lng?: number | null;
  driver_location_at?: string | null;
  driver_location_accuracy_m?: number | null;
  /**
   * Customer asked for a fresh dumpster when this one is picked up
   * (optional paid replacement week).
   */
  replacement_wanted?: number | boolean | null;
  replacement_rental_days?: number | null;
  replacement_job_id?: string | null;
  replacement_of_job_id?: string | null;
  replacement_checkout_session_id?: string | null;
  replacement_payment_status?: 'pending' | 'paid' | string | null;
  created_by_user_id: string;
  created_by_username: string | null;
  created_at: string;
  updated_at: string;
}

/** True when customer wants a re-drop after this pickup. */
export function wmJobWantsReplacement(
  job: Pick<WasteDumpsterJob, 'replacement_wanted' | 'replacement_payment_status' | 'replacement_job_id'> | null | undefined,
): boolean {
  if (!job) return false;
  if (job.replacement_job_id) return true;
  if (String(job.replacement_payment_status || '') === 'paid') return true;
  return Boolean(Number(job.replacement_wanted) || job.replacement_wanted === true);
}

/** True when customer has asked for pickup (or rental window is overdue on-site). */
export function wmJobNeedsPickupScream(job: Pick<WasteDumpsterJob, 'status' | 'dropoff_at' | 'rental_days'>): boolean {
  if (job.status === 'pickup_requested' || job.status === 'en_route_pickup') return true;
  if (job.status === 'dropped_off' && job.rental_days != null && job.dropoff_at) {
    const days = wmDaysOnSite(job.dropoff_at);
    return days != null && days >= Number(job.rental_days);
  }
  return false;
}

/** Pad recovered but landfill debit ticket not yet on file for company. */
export function wmNeedsLandfillReceipt(
  job: Pick<WasteDumpsterJob, 'status' | 'landfill_receipt_at'>,
): boolean {
  return String(job.status || '') === 'picked_up' && !job.landfill_receipt_at;
}

/**
 * Driver still owns this load — cannot claim another until free.
 * Free after empty drop (dropped_off) or landfill receipt filed.
 */
export function wmDriverJobBlocksNewClaim(
  job: Pick<WasteDumpsterJob, 'status' | 'landfill_receipt_at'> | null | undefined,
): boolean {
  if (!job) return false;
  const st = String(job.status || '');
  if (st === 'cancelled' || st === 'dropped_off') return false;
  if (st === 'picked_up') return wmNeedsLandfillReceipt(job);
  return ['assigned', 'en_route_dropoff', 'pickup_requested', 'en_route_pickup'].includes(st);
}

/**
 * After landfill with no later empty drop, driver has that size empty on the truck.
 * Next empty-drop claim must match (or they park the empty at the yard first).
 */
export function wmDriverRequiredEmptySizeFromRuns(
  runs: Array<Pick<WasteDumpsterJob, 'status' | 'dumpster_size' | 'landfill_receipt_at' | 'dropoff_at'>>,
): string | null {
  const landfilled = runs
    .filter((j) => j.status === 'picked_up' && j.landfill_receipt_at)
    .sort((a, b) => String(b.landfill_receipt_at).localeCompare(String(a.landfill_receipt_at)));
  const last = landfilled[0];
  if (!last?.dumpster_size || !last.landfill_receipt_at) return null;
  const laterDrop = runs.some((j) => (
    j.dropoff_at
    && String(j.dropoff_at) > String(last.landfill_receipt_at)
  ));
  if (laterDrop) return null;
  return String(last.dumpster_size);
}

export type WmLandfillPhotoKind = 'scale_ticket' | 'payment_slip' | string;

export function wmLandfillReceiptEntries(
  job: Pick<WasteDumpsterJob, 'landfill_receipt_photo_urls'>,
): Array<{ kind: WmLandfillPhotoKind; url: string }> {
  const raw = job.landfill_receipt_photo_urls;
  if (!raw) return [];
  const parseArr = (arr: unknown[]): Array<{ kind: WmLandfillPhotoKind; url: string }> => (
    arr.map((item, i) => {
      if (item && typeof item === 'object' && (item as { url?: string }).url) {
        const o = item as { kind?: string; url: string };
        return { kind: (o.kind || (i === 0 ? 'scale_ticket' : 'payment_slip')) as WmLandfillPhotoKind, url: String(o.url) };
      }
      return {
        kind: (i === 0 ? 'scale_ticket' : 'payment_slip') as WmLandfillPhotoKind,
        url: String(item),
      };
    }).filter((e) => e.url)
  );
  if (Array.isArray(raw)) return parseArr(raw);
  try {
    const parsed = JSON.parse(String(raw));
    if (Array.isArray(parsed)) return parseArr(parsed);
  } catch {
    /* plain url */
  }
  const one = String(raw).trim();
  return one ? [{ kind: 'scale_ticket', url: one }] : [];
}

export function wmLandfillReceiptUrls(job: Pick<WasteDumpsterJob, 'landfill_receipt_photo_urls'>): string[] {
  return wmLandfillReceiptEntries(job).map((e) => e.url);
}

/**
 * Company desk line: “Driver: Shaun · truck #12 · en route (live)”.
 * Uses status + optional fresh GPS heartbeat.
 */
export function wmCompanyDriverStatusLine(
  job: Pick<
    WasteDumpsterJob,
    | 'status'
    | 'driver_display_name'
    | 'truck_number'
    | 'trailer_number'
    | 'driver_lat'
    | 'driver_lng'
    | 'driver_location_at'
  >,
  nowMs = Date.now(),
): string | null {
  const name = String(job.driver_display_name || '').trim();
  const truck = String(job.truck_number || '').trim();
  const trailer = String(job.trailer_number || '').trim();
  const st = String(job.status || '');
  let phase: string | null = null;
  if (st === 'assigned') phase = 'assigned';
  else if (st === 'en_route_dropoff') phase = 'en route to drop';
  else if (st === 'en_route_pickup') phase = 'en route to pickup';
  else if (st === 'dropped_off') phase = 'box on site';
  else if (st === 'pickup_requested') phase = 'pickup needed';
  else if (st === 'picked_up') phase = 'pickup complete';
  else if (st === 'requested') phase = null;
  else return null;

  if (!name && !truck && !trailer && !phase) return null;

  const locAt = wmParseSqlTime(job.driver_location_at);
  const hasLiveGps = Number.isFinite(Number(job.driver_lat))
    && Number.isFinite(Number(job.driver_lng))
    && locAt != null
    && (nowMs - locAt) < 12 * 60 * 1000
    && (st === 'assigned' || st === 'en_route_dropoff' || st === 'en_route_pickup');

  const who = name || (truck ? `Truck #${truck}` : 'Driver');
  const truckBit = name && truck ? ` · truck #${truck}` : '';
  const trailerBit = trailer ? ` · trailer #${trailer}` : '';
  const phaseBit = phase ? ` · ${phase}` : '';
  const liveBit = hasLiveGps ? ' · live GPS' : '';
  return `${who}${truckBit}${trailerBit}${phaseBit}${liveBit}`;
}

/** Company desk: bucket a job into sorted boards. */
export type WmCompanyBoardLane =
  | 'customer'
  | 'deliveries'
  | 'pickups'
  | 'equipment'
  | 'other';

export function wmCompanyBoardLane(
  job: Pick<WasteDumpsterJob, 'status' | 'equipment_issues_json' | 'payment_status'>,
): WmCompanyBoardLane {
  if (wmParseEquipmentIssues(job).length > 0) {
    // Equipment issues get their own lane even if also on a run
    // (company asked to separate issues from pickup/delivery boards).
    // Still allow dual-list: primary lane for issues board is equipment.
  }
  const st = String(job.status || '');
  if (st === 'pickup_requested' || st === 'en_route_pickup') return 'pickups';
  if (st === 'assigned' || st === 'en_route_dropoff') return 'deliveries';
  if (st === 'requested' && String(job.payment_status || '') === 'paid') return 'customer';
  if (st === 'dropped_off') return 'customer'; // on-site waiting for customer pickup request
  if (wmParseEquipmentIssues(job).length) return 'equipment';
  return 'other';
}

/** Sort key for company boards: customer signals vs driver runs vs equipment. */
/** Terminal state: box recovered AND landfill receipt on file. */
export function wmJobIsDone(
  job: Pick<WasteDumpsterJob, 'status' | 'landfill_receipt_at'> | null | undefined,
): boolean {
  return String(job?.status || '') === 'picked_up' && Boolean(job?.landfill_receipt_at);
}

/**
 * Done-aware status label: once the landfill receipt is filed for the box's
 * serial, every board shows the ticket as Done instead of a lingering
 * "Picked up".
 */
export function wmJobStatusLabel(
  job: Pick<WasteDumpsterJob, 'status' | 'landfill_receipt_at'>,
): string {
  if (wmJobIsDone(job)) return 'Done ✓ · landfill filed';
  return (WM_STATUS_META[String(job.status || '')] || WM_STATUS_META.requested).label;
}

export function wmCompanyBoardGroups(jobs: WasteDumpsterJob[]): {
  customer: WasteDumpsterJob[];
  deliveries: WasteDumpsterJob[];
  pickups: WasteDumpsterJob[];
  equipment: WasteDumpsterJob[];
  other: WasteDumpsterJob[];
} {
  const customer: WasteDumpsterJob[] = [];
  const deliveries: WasteDumpsterJob[] = [];
  const pickups: WasteDumpsterJob[] = [];
  const equipment: WasteDumpsterJob[] = [];
  const other: WasteDumpsterJob[] = [];
  for (const j of jobs) {
    const hasEquip = wmParseEquipmentIssues(j).length > 0;
    if (hasEquip) equipment.push(j);
    const st = String(j.status || '');
    if (st === 'pickup_requested' || st === 'en_route_pickup') pickups.push(j);
    else if (st === 'assigned' || st === 'en_route_dropoff') deliveries.push(j);
    else if (
      st === 'requested'
      || st === 'dropped_off'
      || (st === 'picked_up' && !j.landfill_receipt_at)
    ) {
      customer.push(j);
    } else if (!hasEquip) {
      other.push(j);
    }
  }
  return { customer, deliveries, pickups, equipment, other };
}

export type WmCompanyDriver = {
  id: string;
  crew_id: string | null;
  created_by_user_id: string;
  driver_user_id: string | null;
  driver_name: string;
  username: string | null;
  email: string | null;
  truck_number: string | null;
  trailer_number: string | null;
  invite_last_sent_at: string | null;
  status: string;
  created_at: string;
  updated_at: string;
};

/** Yard inventory unit — one physical dumpster the hauler owns. */
/**
 * Yard inventory lifecycle (where is this physical box right now?).
 * Auto-synced from the job that owns the serial plate; shop is a manual hold.
 * - yard: empty at yard, ready to assign
 * - assigned: hauler assigned to a driver / en route empty drop (on truck)
 * - at_customer: dropped on customer site (rental active)
 * - recovering: pickup requested or driver en route to recover full box
 * - at_landfill: full box recovered / dumping (landfill ticket not done)
 * - shop: repair / out of rentable stock
 * - on_job: legacy alias for assigned (older clients)
 */
export type WmInventoryStatus =
  | 'yard'
  | 'assigned'
  | 'at_customer'
  | 'recovering'
  | 'at_landfill'
  | 'shop'
  | 'on_job';

/** Operational order for hauler inventory status chips (manual cycle skips on_job). */
export const WM_INVENTORY_STATUS_CYCLE: Exclude<WmInventoryStatus, 'on_job'>[] = [
  'yard',
  'assigned',
  'at_customer',
  'recovering',
  'at_landfill',
  'shop',
];

export const WM_INVENTORY_STATUS_META: Record<
  WmInventoryStatus,
  { label: string; color: string; hint: string }
> = {
  yard: { label: 'In yard', color: '#22c55e', hint: 'Empty · ready to assign' },
  assigned: { label: 'On truck', color: '#38bdf8', hint: 'Assigned · en route to drop' },
  at_customer: { label: 'Dropped', color: '#fbbf24', hint: 'On site · rental active' },
  recovering: { label: 'Recovering', color: '#f59e0b', hint: 'Pickup · full box coming back' },
  at_landfill: { label: 'At landfill', color: '#f87171', hint: 'Dumping · file landfill ticket' },
  shop: { label: 'In shop', color: '#a78bfa', hint: 'Repair · not rentable' },
  on_job: { label: 'On truck', color: '#38bdf8', hint: 'Assigned · en route to drop' },
};

export type WmInventoryUnit = {
  id: string;
  owner_user_id: string;
  size: WasteDumpsterSize;
  serial_number: string | null;
  status: WmInventoryStatus;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
  /** When live-synced from a job: customer site or status detail */
  location_label?: string | null;
  job_id?: string | null;
  job_status?: string | null;
};

/** Normalize legacy on_job → assigned for UI. */
export function wmNormalizeInventoryStatus(raw: string | null | undefined): WmInventoryStatus {
  const s = String(raw || 'yard');
  if (s === 'on_job') return 'assigned';
  if (
    s === 'yard'
    || s === 'assigned'
    || s === 'at_customer'
    || s === 'recovering'
    || s === 'at_landfill'
    || s === 'shop'
  ) {
    return s;
  }
  return 'yard';
}

export type WmFleetPairing = {
  id?: string;
  crew_id?: string | null;
  truck_number: string;
  trailer_number?: string | null;
  dumpster_serial?: string | null;
  job_id?: string | null;
  driver_user_id?: string | null;
  driver_name?: string | null;
  status?: string | null;
  updated_at?: string | null;
  job_status?: string | null;
};

export interface WasteJobMember {
  id: string;
  job_id: string;
  host_user_id: string;
  host_username: string | null;
  role: WasteJobRole;
  status: string;
  joined_at: string;
}

export interface WasteJobEvent {
  id: string;
  job_id: string;
  event_type: string;
  message: string | null;
  host_user_id: string | null;
  host_username: string | null;
  payload_json: string | null;
  created_at: string;
}

export interface WasteJobBundle {
  job: WasteDumpsterJob;
  members: WasteJobMember[];
  events: WasteJobEvent[];
}

export type WasteJobListView = 'all' | 'mine' | 'crew' | 'open' | 'action';

/**
 * Shared customer-request geofence (meters).
 * Drawn as a ring on the customer pin map; used for "I'm at the pad" + drop location proof.
 */
export const WM_DROP_GEOFENCE_RADIUS_M = 200;

/**
 * Escrow process labels (ledger — not a separate Stripe escrow account until Connect).
 * - held: customer paid; company not ready for payout
 * - ready_for_payout: landfill receipt filed + overage settled ($0 or paid)
 * - paid_out: ops/Connect paid company
 */
export type WmEscrowPhase =
  | 'unpaid'
  | 'checkout_pending'
  | 'held'
  | 'ready_for_payout'
  | 'paid_out'
  | 'hold'
  | 'cancelled';

/** All dumpsters: 1 ton free; overage is a second charge after landfill scale. */
export const WM_INCLUDED_TONS_FREE = 1;

/** Cancel fees (no free cancel). Late = within 24h of delivery OR en route+. */
export const WM_CANCEL_EARLY_USD = 20;
export const WM_CANCEL_LATE_USD = 50;
/** Hours before scheduled delivery when cancel becomes late. */
export const WM_CANCEL_LATE_WITHIN_HOURS = 24;

export type WmCancelTier = 'early' | 'late';

/** Customer-paid rental is in process escrow when payment_status is paid. */
export function wmPaymentIsEscrowHeld(status?: string | null): boolean {
  return String(status || '').toLowerCase() === 'paid';
}

export function wmHaulerPayoutStatus(job: {
  hauler_payout_status?: string | null;
  payment_status?: string | null;
} | null | undefined): string {
  const s = String(job?.hauler_payout_status || '').toLowerCase();
  if (s) return s;
  return wmPaymentIsEscrowHeld(job?.payment_status) ? 'pending_ops' : 'none';
}

/** True when overage is $0 / none, paid, or desk-waived after dispute. */
export function wmOverageSettled(job: {
  overage_cents?: number | null;
  overage_payment_status?: string | null;
} | null | undefined): boolean {
  const cents = Math.floor(Number(job?.overage_cents) || 0);
  if (cents < 100) return true;
  const st = String(job?.overage_payment_status || '').toLowerCase();
  return st === 'paid' || st === 'waived';
}

/** Customer rejected the scale overage (desk must resolve). */
export function wmJobOverageDisputed(
  job: Pick<WasteDumpsterJob, 'overage_payment_status'> | null | undefined,
): boolean {
  return String(job?.overage_payment_status || '').toLowerCase() === 'disputed';
}

/** True when customer still needs to pay or dispute is open (primary overage surface). */
export function wmJobOverageNeedsAttention(
  job: Pick<WasteDumpsterJob, 'overage_cents' | 'overage_payment_status' | 'status' | 'landfill_receipt_at'> | null | undefined,
): boolean {
  if (!job) return false;
  if (String(job.status || '') !== 'picked_up' && !job.landfill_receipt_at) return false;
  const st = String(job.overage_payment_status || '').toLowerCase();
  if (st === 'disputed') return true;
  if (st === 'paid' || st === 'waived') return false;
  return Math.floor(Number(job.overage_cents) || 0) >= 100;
}

/** Human label for timeline / receipts / cards. */
export function wmOverageStatusLabel(
  job: Pick<WasteDumpsterJob, 'overage_cents' | 'overage_payment_status'> | null | undefined,
): string {
  const st = String(job?.overage_payment_status || '').toLowerCase();
  const cents = Math.floor(Number(job?.overage_cents) || 0);
  if (st === 'paid') return 'Paid · Stripe';
  if (st === 'waived') return 'Waived · no charge';
  if (st === 'disputed') return 'Disputed · company reviewing';
  if (st === 'pending') return 'Invoice sent · pay or reject';
  if (st === 'unpaid' || cents >= 100) return 'Overage due';
  return 'No overage';
}

/** Landfill close-out + overage done → company can be paid. */
export function wmIsReadyForPayout(job: {
  payment_status?: string | null;
  landfill_receipt_at?: string | null;
  overage_cents?: number | null;
  overage_payment_status?: string | null;
  hauler_payout_status?: string | null;
  status?: string | null;
} | null | undefined): boolean {
  if (!job) return false;
  if (String(job.status || '') === 'cancelled') return false;
  if (!wmPaymentIsEscrowHeld(job.payment_status)) return false;
  if (String(job.hauler_payout_status || '').toLowerCase() === 'paid_out') return true;
  if (!job.landfill_receipt_at) return false;
  return wmOverageSettled(job);
}

export function wmEscrowPhase(job: {
  payment_status?: string | null;
  landfill_receipt_at?: string | null;
  overage_cents?: number | null;
  overage_payment_status?: string | null;
  hauler_payout_status?: string | null;
  status?: string | null;
} | null | undefined): WmEscrowPhase {
  if (!job) return 'unpaid';
  if (String(job.status || '') === 'cancelled') return 'cancelled';
  const pay = String(job.payment_status || '').toLowerCase();
  if (pay === 'pending') return 'checkout_pending';
  if (pay !== 'paid') return 'unpaid';
  const payout = String(job.hauler_payout_status || '').toLowerCase();
  if (payout === 'paid_out') return 'paid_out';
  if (payout === 'hold') return 'hold';
  if (payout === 'ready_for_payout' || wmIsReadyForPayout(job)) return 'ready_for_payout';
  return 'held';
}

export function wmEscrowPhaseLabel(phase: WmEscrowPhase): string {
  switch (phase) {
    case 'unpaid': return 'Not paid';
    case 'checkout_pending': return 'Checkout open';
    case 'held': return 'Held · until landfill receipt';
    case 'ready_for_payout': return 'Ready for company payout';
    case 'paid_out': return 'Company paid out';
    case 'hold': return 'On hold';
    case 'cancelled': return 'Cancelled';
    default: return phase;
  }
}

/**
 * Role-specific one-liner: who is paid / protected right now.
 * customer | company (hauler desk) | driver
 */
export function wmEscrowRoleStatusLine(
  phase: WmEscrowPhase,
  role: 'customer' | 'company' | 'driver',
): string {
  if (role === 'customer') {
    switch (phase) {
      case 'unpaid':
        return 'You have not paid yet. Pay to schedule delivery — funds are protected until the job is finished at the landfill.';
      case 'checkout_pending':
        return 'Checkout is open. Finish Stripe pay to lock your rental and hold funds for this job.';
      case 'held':
        return 'You are paid. Your money is held until the dumpster is recovered and the landfill receipt is filed (and any overage is paid).';
      case 'ready_for_payout':
        return 'You are paid in full for this rental. Landfill close-out is done — the hauler can be paid their share next.';
      case 'paid_out':
        return 'You paid · hauler has been paid out for this job. Any overage was a separate charge if the scale went over 1 free ton.';
      case 'hold':
        return 'Payment is on hold. Contact support if you need an update.';
      case 'cancelled':
        return 'Job cancelled. Cancel fee and any refund follow early/late rules (refunds exclude card fees).';
      default:
        return wmEscrowPhaseLabel(phase);
    }
  }
  if (role === 'driver') {
    switch (phase) {
      case 'unpaid':
        return 'Customer has not paid — do not drop unless lead forces a field exception.';
      case 'checkout_pending':
        return 'Customer checkout still open — wait for paid escrow before dropping.';
      case 'held':
        return 'Customer paid (escrow held). Drop with serial + photos. Company is paid only after landfill receipt — not when you drop.';
      case 'ready_for_payout':
        return 'Landfill receipt done · escrow ready for company payout. Your run is complete on the money side.';
      case 'paid_out':
        return 'Company share marked paid out for this job.';
      case 'hold':
        return 'Payout on hold — check with lead/ops.';
      case 'cancelled':
        return 'Job cancelled — do not deliver.';
      default:
        return wmEscrowPhaseLabel(phase);
    }
  }
  // company / crew lead
  switch (phase) {
    case 'unpaid':
      return 'Customer has not paid. No company earnings locked yet.';
    case 'checkout_pending':
      return 'Customer is in checkout. Wait for paid before treating funds as held.';
    case 'held':
      return 'Customer paid · your share is held. You get paid after landfill receipt (and overage if any) — not at pad drop.';
    case 'ready_for_payout':
      return 'Escrow released for payout. Your company share is ready — ops/ACH (or Connect later) can pay you.';
    case 'paid_out':
      return 'Your company has been marked paid out for this job.';
    case 'hold':
      return 'Payout on hold. Ops must clear before you are paid.';
    case 'cancelled':
      return `Cancelled. Early/late cancel fee may include your ${100 - WM_PLATFORM_FEE_BPS / 100}% share of the fee.`;
    default:
      return wmEscrowPhaseLabel(phase);
  }
}

/** Timeline steps for escrow transparency (customer + company + driver). */
export function wmEscrowTimelineSteps(job: {
  payment_status?: string | null;
  dropoff_at?: string | null;
  landfill_receipt_at?: string | null;
  overage_cents?: number | null;
  overage_payment_status?: string | null;
  hauler_payout_status?: string | null;
  status?: string | null;
} | null | undefined): Array<{
  id: string;
  label: string;
  detail: string;
  done: boolean;
  active: boolean;
}> {
  const phase = wmEscrowPhase(job);
  const paid = wmPaymentIsEscrowHeld(job?.payment_status);
  const dropped = Boolean(job?.dropoff_at) || ['dropped_off', 'pickup_requested', 'en_route_pickup', 'picked_up'].includes(String(job?.status || ''));
  const landfill = Boolean(job?.landfill_receipt_at);
  const overageCents = Math.floor(Number(job?.overage_cents) || 0);
  const overageNeeded = overageCents >= 100;
  const overageDone = wmOverageSettled(job);
  const paidOut = phase === 'paid_out' || String(job?.hauler_payout_status || '').toLowerCase() === 'paid_out';
  const ready = phase === 'ready_for_payout' || phase === 'paid_out' || wmIsReadyForPayout(job);

  const steps: Array<{ id: string; label: string; detail: string; done: boolean; active: boolean }> = [
    {
      id: 'customer_paid',
      label: '1 · Customer paid',
      detail: paid
        ? 'Stripe paid · funds held in escrow process'
        : 'Waiting for customer checkout',
      done: paid,
      active: !paid && phase !== 'cancelled',
    },
    {
      id: 'on_pad',
      label: '2 · Box on pad',
      detail: dropped
        ? 'Dropped with proof · still held (not company payout yet)'
        : 'Driver places box · serial + photos',
      done: dropped,
      active: paid && !dropped && phase !== 'cancelled',
    },
    {
      id: 'landfill',
      label: '3 · Landfill receipt',
      detail: landfill
        ? 'Scale ticket + payment slip on file'
        : 'After pickup · tip photos required to release company pay',
      done: landfill,
      active: dropped && !landfill && phase !== 'cancelled',
    },
  ];

  if (overageNeeded || (landfill && !overageDone && overageCents > 0)) {
    steps.push({
      id: 'overage',
      label: '4 · Overage (2nd charge)',
      detail: overageDone
        ? 'Overage paid · second charge complete'
        : `Customer pays weight over ${WM_INCLUDED_TONS_FREE} free tons`,
      done: overageDone,
      active: landfill && !overageDone && phase !== 'cancelled',
    });
  }

  steps.push({
    id: 'company_ready',
    label: `${overageNeeded ? '5' : '4'} · Company can be paid`,
    detail: ready
      ? 'Escrow ready · company share available'
      : 'After landfill' + (overageNeeded ? ' + overage paid' : ''),
    done: ready && !paidOut,
    active: landfill && overageDone && !ready && !paidOut && phase !== 'cancelled',
  });

  steps.push({
    id: 'company_paid',
    label: `${overageNeeded ? '6' : '5'} · Company paid out`,
    detail: paidOut
      ? 'Ops marked paid out (ACH/check until Connect)'
      : 'Final step · company receives their share',
    done: paidOut,
    active: ready && !paidOut && phase !== 'cancelled',
  });

  // Fix done flags: ready step should be done when paidOut too
  const readyStep = steps.find((s) => s.id === 'company_ready');
  if (readyStep && paidOut) readyStep.done = true;

  return steps;
}

/** Late cancel: within 24h of scheduled delivery OR en route / on site / later. */

export function wmCanCustomerReviewDrop(job: {
  status?: string | null;
  dropoff_at?: string | null;
} | null | undefined): boolean {
  if (!job) return false;
  const st = String(job.status || '');
  return Boolean(job.dropoff_at)
    || ['dropped_off', 'pickup_requested', 'en_route_pickup'].includes(st);
}

export function wmDropAccepted(job: { drop_accepted_at?: string | null } | null | undefined): boolean {
  return Boolean(job?.drop_accepted_at);
}

export function wmDropProblemOpen(job: {
  drop_problem_at?: string | null;
  drop_accepted_at?: string | null;
} | null | undefined): boolean {
  return Boolean(job?.drop_problem_at) && !job?.drop_accepted_at;
}

const WM_LOAD_REJECT_STATUSES = ['dropped_off', 'pickup_requested', 'en_route_pickup'] as const;

export function wmLoadRejectedItemIds(job: {
  load_rejected_items?: string | null;
} | null | undefined): string[] {
  const raw = job?.load_rejected_items;
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((v) => String(v || '').trim()).filter(Boolean);
  } catch {
    return [];
  }
}

/** Driver refused the haul; customer has not confirmed banned items are out. */
export function wmLoadRejectOpen(job: {
  load_rejected_at?: string | null;
  load_remediated_at?: string | null;
} | null | undefined): boolean {
  return Boolean(job?.load_rejected_at) && !job?.load_remediated_at;
}

export function wmCanRejectLoad(
  job: { status?: string | null } | null | undefined,
  role?: string | null,
): boolean {
  if (!job) return false;
  if (role !== 'worker' && role !== 'crew_lead') return false;
  return WM_LOAD_REJECT_STATUSES.includes(String(job.status || '') as typeof WM_LOAD_REJECT_STATUSES[number]);
}

export function wmCanRemediateLoad(
  job: {
    load_rejected_at?: string | null;
    load_remediated_at?: string | null;
  } | null | undefined,
  role?: string | null,
): boolean {
  if (!wmLoadRejectOpen(job)) return false;
  return role === 'customer' || role === 'crew_lead';
}

export function wmProhibitedCategoryLabel(id: string): string {
  const hit = WM_PROHIBITED_CATEGORIES.find((c) => c.id === id);
  return hit?.label || id;
}

/**
 * Pre-pay reassurance shown right on the WM request pay bar (before any job row exists).
 * WM has its own escrow voice (landfill-receipt release), so it does not use the shared
 * SERVICE_PAY_HOLD_LINE — keep this WM-specific.
 */
export const WM_PAY_ESCROW_TRACK_LINE =
  'Your payment is held in escrow until the dumpster job is verified done at the landfill. Track your hauler live and get arrival updates.';

export function wmCustomerEscrowBanner(job: {
  payment_status?: string | null;
  dropoff_at?: string | null;
  drop_accepted_at?: string | null;
  drop_problem_at?: string | null;
  load_rejected_at?: string | null;
  load_remediated_at?: string | null;
  status?: string | null;
  hauler_payout_status?: string | null;
} | null | undefined): string {
  if (!job) return '';
  if (String(job.status || '') === 'cancelled') {
    return 'Job cancelled. Any refund needs Zia approval before Stripe pays out.';
  }
  if (String(job.payment_status || '').toLowerCase() !== 'paid') {
    return 'Pay at checkout to hold funds for this dumpster job.';
  }
  if (wmLoadRejectOpen(job)) {
    return 'Driver rejected this load — banned items in the box. Remove them so pickup can continue. Funds stay held until landfill.';
  }
  if (wmDropProblemOpen(job)) {
    return 'You reported a placement problem. Hauler pay is on hold until Zia/crew fixes it.';
  }
  if (String(job.hauler_payout_status || '') === 'paid_out') {
    return 'You paid · hauler has been paid for this job.';
  }
  if (job.drop_accepted_at) {
    return 'Box on site looks good. Your money stays held until the box is recovered and the landfill receipt is filed (plus any overage).';
  }
  if (job.dropoff_at || job.status === 'dropped_off') {
    return 'Box is on site. Request pickup when you are done — or report a problem if placement is wrong. Funds stay held until landfill.';
  }
  return 'You paid. Funds are held by ZiaWellNav until landfill close-out (and overage if any).';
}

export function wmIsLateCancel(
  job: {
    status?: string | null;
    delivery_date?: string | null;
    deliveryDate?: string | null;
    rental_days?: number | null;
    dropoff_at?: string | null;
    created_at?: string | null;
  } | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!job) return false;
  const st = String(job.status || '');
  if (['en_route_dropoff', 'dropped_off', 'pickup_requested', 'en_route_pickup', 'picked_up'].includes(st)) {
    return true;
  }
  const deliveryIso = String(job.delivery_date || job.deliveryDate || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(deliveryIso)) {
    const day = deliveryIso.slice(0, 10);
    const deliveryStart = new Date(`${day}T00:00:00`).getTime();
    if (Number.isFinite(deliveryStart)) {
      const msBefore = deliveryStart - nowMs;
      if (msBefore <= WM_CANCEL_LATE_WITHIN_HOURS * 60 * 60 * 1000) return true;
    }
  }
  return false;
}

export function wmCancelTier(
  job: Parameters<typeof wmIsLateCancel>[0],
  nowMs?: number,
): WmCancelTier {
  return wmIsLateCancel(job, nowMs) ? 'late' : 'early';
}

export function wmCancelFeeUsd(tier: WmCancelTier): number {
  return tier === 'late' ? WM_CANCEL_LATE_USD : WM_CANCEL_EARLY_USD;
}

/**
 * Refund remaining after cancel fee, net of Stripe processing on original charge.
 * Protects platform from eating card fees on refund.
 */
export function wmCancelSettlementCents(input: {
  amountPaidCents: number;
  tier: WmCancelTier;
  stripeFeeCents?: number | null;
}): {
  tier: WmCancelTier;
  cancelFeeCents: number;
  platformFeeCents: number;
  contractorFeeCents: number;
  stripeFeeCents: number;
  refundCents: number;
  keptCents: number;
} {
  const gross = Math.max(0, Math.floor(Number(input.amountPaidCents) || 0));
  const cancelFeeCents = Math.min(gross, Math.round(wmCancelFeeUsd(input.tier) * 100));
  const cancelSplit = wmFeeSplitCents(cancelFeeCents, 0);
  // Stripe fee on original charge — do not refund this portion
  const stripeOnGross = input.stripeFeeCents != null && Number.isFinite(Number(input.stripeFeeCents))
    ? Math.max(0, Math.floor(Number(input.stripeFeeCents)))
    : wmEstimateStripeFeeCents(gross);
  // Cancel fee splits on the same platform rate; stripe reserved from refund separately
  const platformFeeCents = cancelSplit.platformFeeCents;
  const contractorFeeCents = Math.max(0, cancelFeeCents - platformFeeCents);
  const keptCents = Math.min(gross, cancelFeeCents + stripeOnGross);
  const refundCents = Math.max(0, gross - keptCents);
  return {
    tier: input.tier,
    cancelFeeCents,
    platformFeeCents,
    contractorFeeCents,
    stripeFeeCents: stripeOnGross,
    refundCents,
    keptCents,
  };
}

/** Named money buckets for UI (customer + company + ops). */
/** Customer-facing receipt line (no hauler/ops split). */
export type WmCustomerReceiptLine = {
  /** Primary row title, e.g. "Dumpster rental" */
  label: string;
  /** Secondary detail under the title, e.g. "30 yd · 7 days · Construction debris" */
  detail?: string;
  amountCents: number | null;
  note?: string;
  kind?: 'rental' | 'delivery' | 'extra' | 'overage' | 'other';
};

export type WmCustomerReceipt = {
  title: string;
  /** Short hero, e.g. "30 yd dumpster" */
  headline: string;
  site: string | null;
  hauler: string | null;
  serial: string | null;
  droppedLabel: string | null;
  pickedUpLabel: string | null;
  paid: boolean;
  /** Short reference for records (not the full DB id wall) */
  refId: string;
  fullJobId: string;
  tonsIncluded: number | null;
  lines: WmCustomerReceiptLine[];
  totalCents: number;
  /** Flat lines for email/download body */
  meta: string[];
  plainText: string;
};

function wmTitleCaseWords(raw: string): string {
  return String(raw || '')
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function wmReceiptWhenLabel(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return String(iso).slice(0, 16);
    // Prefer a calm date-first line; time only when not midnight-ish local.
    const datePart = d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const hours = d.getHours();
    const mins = d.getMinutes();
    if (hours === 0 && mins === 0) return datePart;
    const timePart = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${datePart} · ${timePart}`;
  } catch {
    return String(iso).slice(0, 16);
  }
}

function wmShortReceiptRef(jobId: string): string {
  const raw = String(jobId || '').replace(/^wdj[_-]?/i, '');
  if (raw.length <= 12) return raw.toUpperCase();
  return raw.slice(-10).toUpperCase();
}

/**
 * Itemized receipt for the customer's records (email / download / share).
 * Never includes company share, platform fee, or ops payout language.
 */
export function wmBuildCustomerReceipt(
  job: Pick<
    WasteDumpsterJob,
    | 'id'
    | 'site_name'
    | 'dumpster_size'
    | 'rental_days'
    | 'waste_stream'
    | 'product_line'
    | 'service_kind'
    | 'amount_cents'
    | 'haul_cents'
    | 'delivery_cents'
    | 'addl_usage_cents'
    | 'overage_cents'
    | 'overage_payment_status'
    | 'overage_tons'
    | 'tons_included'
    | 'payment_status'
    | 'dropoff_at'
    | 'pickup_at'
    | 'dropoff_serial'
    | 'company_name'
    | 'order_group_id'
    | 'cart_line_index'
    | 'created_at'
  > | null | undefined,
): WmCustomerReceipt {
  const empty: WmCustomerReceipt = {
    title: 'Receipt',
    headline: 'Dumpster rental',
    site: null,
    hauler: null,
    serial: null,
    droppedLabel: null,
    pickedUpLabel: null,
    paid: false,
    refId: '—',
    fullJobId: '',
    tonsIncluded: null,
    lines: [],
    totalCents: 0,
    meta: [],
    plainText: 'No receipt available.',
  };
  if (!job) return empty;

  const sizeLabel = wmDumpsterYardsLabel(job.dumpster_size);
  const days = job.rental_days != null ? Number(job.rental_days) : null;
  const stream = wmTitleCaseWords(String(job.waste_stream || job.product_line || '')) || null;
  const haul = job.haul_cents != null ? Math.max(0, Math.floor(Number(job.haul_cents))) : null;
  const delivery = job.delivery_cents != null ? Math.max(0, Math.floor(Number(job.delivery_cents))) : null;
  const addl = job.addl_usage_cents != null ? Math.max(0, Math.floor(Number(job.addl_usage_cents))) : null;
  const overage = job.overage_cents != null ? Math.max(0, Math.floor(Number(job.overage_cents))) : 0;
  const overPaid = String(job.overage_payment_status || '').toLowerCase() === 'paid';
  const gross = Math.max(0, Math.floor(Number(job.amount_cents) || 0));
  const tonsIncluded = job.tons_included != null ? Number(job.tons_included) : null;
  const rentalDetail = [
    sizeLabel,
    days != null && Number.isFinite(days) ? `${days}-day rental` : null,
    stream,
  ].filter(Boolean).join(' · ');

  const pushRental = (amountCents: number) => {
    lines.push({
      label: 'Dumpster rental',
      detail: rentalDetail || undefined,
      amountCents,
      kind: 'rental',
    });
  };

  const lines: WmCustomerReceiptLine[] = [];

  if (haul != null || delivery != null || addl != null) {
    if (haul != null && haul > 0) pushRental(haul);
    if (delivery != null && delivery > 0) {
      lines.push({ label: 'Delivery & placement', amountCents: delivery, kind: 'delivery' });
    }
    if (addl != null && addl > 0) {
      lines.push({ label: 'Extra on-pad days', amountCents: addl, kind: 'extra' });
    }
    const sumParts = (haul || 0) + (delivery || 0) + (addl || 0);
    if (gross > 0 && sumParts > 0 && sumParts !== gross) {
      // Breakdown does not match total — one rental line at total.
      lines.length = 0;
      pushRental(gross);
    } else if (lines.length === 0 && gross > 0) {
      pushRental(gross);
    }
  } else if (gross > 0) {
    pushRental(gross);
  }

  if (overage >= 100 && overPaid) {
    const overTons = job.overage_tons != null ? Number(job.overage_tons) : null;
    const overDetail = overTons != null && Number.isFinite(overTons)
      ? `${overTons % 1 === 0 ? overTons.toFixed(0) : overTons.toFixed(1)} ton${overTons === 1 ? '' : 's'} over the ${
        tonsIncluded != null ? `${tonsIncluded} included` : 'included allowance'
      }`
      : (tonsIncluded != null ? `Weight over ${tonsIncluded} included tons` : 'Weight over included allowance');
    lines.push({
      label: 'Scale overage',
      detail: overDetail,
      amountCents: overage,
      note: 'Second charge after landfill scale',
      kind: 'overage',
    });
  }

  const totalCents = lines.reduce((s, l) => s + (l.amountCents || 0), 0)
    || gross
    || (overPaid ? overage : 0);

  const fmtUsd = (c: number | null | undefined) => {
    if (c == null || !Number.isFinite(Number(c))) return '—';
    return `$${(Number(c) / 100).toFixed(2)}`;
  };

  const site = job.site_name ? String(job.site_name).trim() : null;
  const hauler = job.company_name ? String(job.company_name).trim() : null;
  const serial = job.dropoff_serial ? String(job.dropoff_serial).trim() : null;
  const droppedLabel = wmReceiptWhenLabel(job.dropoff_at);
  const pickedUpLabel = wmReceiptWhenLabel(job.pickup_at);
  const paid = String(job.payment_status || '').toLowerCase() === 'paid';
  const refId = wmShortReceiptRef(job.id);
  const headline = `${sizeLabel} dumpster`;

  const meta = [
    site ? `Site: ${site}` : null,
    hauler ? `Hauler: ${hauler}` : null,
    serial ? `Serial: #${serial}` : null,
    droppedLabel ? `Dropped: ${droppedLabel}` : null,
    pickedUpLabel ? `Picked up: ${pickedUpLabel}` : null,
    tonsIncluded != null ? `Tons included: ${tonsIncluded}` : null,
    `Payment: ${paid ? 'Paid' : String(job.payment_status || 'unknown')}`,
    `Reference: ${refId}`,
  ].filter(Boolean) as string[];

  const title = `${headline}${site ? ` · ${site}` : ''}`;
  const plainLines = [
    'ZiaWellNav · Dumpster rental receipt',
    title,
    '',
    ...meta,
    '',
    'Charges',
    ...lines.map((l) => {
      const left = [l.label, l.detail, l.note].filter(Boolean).join(' — ');
      return `  ${left}: ${fmtUsd(l.amountCents)}`;
    }),
    '',
    `Total paid: ${fmtUsd(totalCents)}`,
    '',
    'Keep this for your records.',
  ];

  return {
    title,
    headline,
    site,
    hauler,
    serial,
    droppedLabel,
    pickedUpLabel,
    paid,
    refId,
    fullJobId: String(job.id || ''),
    tonsIncluded,
    lines,
    totalCents,
    meta,
    plainText: plainLines.join('\n'),
  };
}

export function wmMoneyBuckets(job: {
  amount_cents?: number | null;
  platform_fee_cents?: number | null;
  stripe_fee_cents?: number | null;
  contractor_payout_cents?: number | null;
  overage_cents?: number | null;
  overage_platform_fee_cents?: number | null;
  overage_stripe_fee_cents?: number | null;
  overage_contractor_payout_cents?: number | null;
  overage_payment_status?: string | null;
  payment_status?: string | null;
} | null | undefined): {
  customerTotalCents: number;
  platformFeeCents: number;
  stripeFeeCents: number;
  companyShareCents: number;
  overageCents: number;
  overagePaid: boolean;
  labels: { customer: string; platform: string; stripe: string; company: string; hold: string; overage: string };
} {
  const gross = Math.max(0, Math.floor(Number(job?.amount_cents) || 0));
  const split = (job?.platform_fee_cents != null || job?.contractor_payout_cents != null)
    ? {
        platformFeeCents: Math.floor(Number(job?.platform_fee_cents) || 0),
        stripeFeeCents: Math.floor(Number(job?.stripe_fee_cents) || 0),
        contractorPayoutCents: Math.floor(Number(job?.contractor_payout_cents) || 0),
      }
    : wmFeeSplitCents(gross, job?.stripe_fee_cents ?? null);
  const overageCents = Math.floor(Number(job?.overage_cents) || 0);
  const overSt = String(job?.overage_payment_status || '').toLowerCase();
  const overagePaid = overSt === 'paid' || overSt === 'waived';
  const overageLabel = overSt === 'paid'
    ? 'Scale overage · paid'
    : overSt === 'waived'
      ? 'Scale overage · waived'
      : overSt === 'disputed'
        ? 'Scale overage · disputed'
        : overageCents >= 100
          ? 'Scale overage · due'
          : 'Overage (second charge · weight over free tons)';
  return {
    customerTotalCents: gross,
    platformFeeCents: split.platformFeeCents,
    stripeFeeCents: split.stripeFeeCents,
    companyShareCents: split.contractorPayoutCents,
    overageCents,
    overagePaid,
    labels: {
      customer: 'Customer total (what you paid)',
      /*
       * The rate THIS job was charged, not today's.
       *
       * Derived from the row's own fee and gross so a job billed at 12% still
       * reads 12% after the platform moved to 15%. Labelling old money with a
       * new rate is the same class of lie the fee change was made to remove.
       */
      platform: `Zia platform fee (${
        gross > 0 ? Math.round((split.platformFeeCents / gross) * 100) : WM_PLATFORM_FEE_BPS / 100
      }%)`,
      stripe: 'Card processing (Stripe)',
      company: `Company earnings (${
        gross > 0 ? 100 - Math.round((split.platformFeeCents / gross) * 100) : 100 - WM_PLATFORM_FEE_BPS / 100
      }%)`,
      hold: overSt === 'disputed'
        ? 'Held · overage dispute open'
        : 'Held until landfill receipt + overage settled (paid or waived)',
      overage: overageLabel,
    },
  };
}

export function wmOverageCustomerCopy(tonsIncluded: number = WM_INCLUDED_TONS_FREE): string {
  return (
    `Up to ${tonsIncluded} tons included. Extra weight after the scale is a second charge — not in your first payment.`
  );
}

export interface WasteMapPin {
  id: string;
  /** Explicit billing status from the scoped map API; never infer payment from placement. */
  paymentStatus?: string | null;
  lat: number;
  lng: number;
  kind: 'dropoff' | 'request';
  status: string;
  size: string;
  site: string;
  serial: string | null;
  orientation: string | null;
  photoUrl: string | null;
  /** Hauler / waste company name on the job */
  companyName?: string | null;
  /** Customer operator / org */
  customerOrg?: string | null;
  /** Oilfield API # when present in request notes */
  apiNumber?: string | null;
  /** Street service address when present */
  serviceAddress?: string | null;
  contactName?: string | null;
  /** True drop-off GPS when box is on site (nav target preferred) */
  dropoffLat?: number | null;
  dropoffLng?: number | null;
  requestedLat?: number | null;
  requestedLng?: number | null;
  /** ISO / SQLite timestamp when box was left on pad */
  dropoffAt?: string | null;
  pickupRequestedAt?: string | null;
  /** Paid rental window in days (if known) */
  rentalDays?: number | null;
  /** Whole days since drop-off while box is still on site */
  daysOnSite?: number | null;
  /**
   * True when status is pickup_requested, or rental days elapsed while still dropped_off.
   * Map pins use amber/orange for ready-for-pickup.
   */
  readyForPickup?: boolean;
  truckNumber?: string | null;
  driverName?: string | null;
  /** Live truck GPS while employee is en route in-app (fresh only) */
  driverLat?: number | null;
  driverLng?: number | null;
  driverLocationAt?: string | null;
  driverLocationAccuracyM?: number | null;
  /** Heartbeat older than the LIVE window — render dimmed "last seen" pin. */
  driverLocationStale?: boolean | null;
  driverLastSeenMinutes?: number | null;
}

/**
 * Straight-line ETA estimate for the live truck → its dumpster pin, in minutes.
 * Deliberately approximate (no routing API): blended field speeds — short hops
 * crawl lease roads (~25 mph), longer legs run highway (~45 mph). Returns null
 * when the truck is stale or coords are missing. Label it "≈" in UI.
 */
export function wmTruckEtaMinutes(pin: WasteMapPin): number | null {
  if (pin.driverLocationStale) return null;
  // Both ends must be real fixes. Number() alone turns a missing coordinate
  // into 0 and yields a confident ETA measured from the middle of the Atlantic.
  const truck = usableGpsPair(pin.driverLat, pin.driverLng);
  const drop = usableGpsPair(pin.lat, pin.lng);
  if (!truck || !drop) return null;
  const tLat = truck.lat;
  const tLng = truck.lng;
  const dLat = drop.lat;
  const dLng = drop.lng;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dphi = toRad(dLat - tLat);
  const dlambda = toRad(dLng - tLng);
  const a = Math.sin(dphi / 2) ** 2
    + Math.cos(toRad(tLat)) * Math.cos(toRad(dLat)) * Math.sin(dlambda / 2) ** 2;
  const meters = 2 * R * Math.asin(Math.sqrt(a));
  if (!Number.isFinite(meters)) return null;
  if (meters < 150) return 0; // effectively on site
  const miles = meters / 1609.344;
  const mph = miles <= 3 ? 25 : 45;
  // Roads aren't straight lines — pad the crow-flies distance by ~25%.
  return Math.max(1, Math.round((miles * 1.25 / mph) * 60));
}

/** Prefer drop-off coordinates for navigation when the dumpster is on site. */
export function wmMapPinNavCoords(pin: WasteMapPin): { lat: number; lng: number; kind: 'dropoff' | 'request' } {
  if (
    pin.dropoffLat != null
    && pin.dropoffLng != null
    && Number.isFinite(pin.dropoffLat)
    && Number.isFinite(pin.dropoffLng)
  ) {
    return { lat: pin.dropoffLat, lng: pin.dropoffLng, kind: 'dropoff' };
  }
  return { lat: pin.lat, lng: pin.lng, kind: pin.kind };
}

/** Parse SQLite `datetime('now')` / ISO timestamps to ms. */
export function wmParseSqlTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  const direct = Date.parse(s);
  if (Number.isFinite(direct)) return direct;
  const asUtc = Date.parse(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`);
  return Number.isFinite(asUtc) ? asUtc : null;
}

/** Whole days a dumpster has been on location since drop-off (0 = first day). */
export function wmDaysOnSite(dropoffAt: string | null | undefined, nowMs = Date.now()): number | null {
  const start = wmParseSqlTime(dropoffAt);
  if (start == null) return null;
  return Math.max(0, Math.floor((nowMs - start) / (24 * 60 * 60 * 1000)));
}

/** Pin needs loud amber treatment on the map (customer wants removal / en route for it). */
export function wmMapPinNeedsPickup(pin: Pick<WasteMapPin, 'status' | 'readyForPickup' | 'daysOnSite' | 'rentalDays'>): boolean {
  if (pin.readyForPickup || pin.status === 'pickup_requested' || pin.status === 'en_route_pickup') return true;
  if (
    pin.status === 'dropped_off'
    && pin.rentalDays != null
    && pin.daysOnSite != null
    && pin.daysOnSite >= pin.rentalDays
  ) {
    return true;
  }
  return false;
}

/** Pin color: amber when ready for pickup; green dropped; slate picked up. */
export function wmMapPinColor(pin: Pick<WasteMapPin, 'status' | 'readyForPickup' | 'daysOnSite' | 'rentalDays'>): string {
  const st = String(pin.status || '');
  // Dropped stays green until pickup is requested / en route / recovered
  if (st === 'picked_up') return '#64748b';
  if (st === 'dropped_off' && !wmMapPinNeedsPickup(pin)) return '#22c55e';
  if (wmMapPinNeedsPickup(pin)) return '#f59e0b';
  return WM_STATUS_META[pin.status]?.color || '#84cc16';
}

export function wmMapPinHasLiveTruck(pin: WasteMapPin): boolean {
  /*
   * isUsableGps, not Number.isFinite(Number(...)).
   *
   * The server sends driverLat: null when a driver has no fix, and Number(null)
   * is 0 — which is finite. That put a truck marker at 0N 0E, in the Gulf of
   * Guinea, on the hauler's driver map, with an ETA of ~11,600 minutes because
   * it was measuring Null Island to the Permian Basin.
   */
  return isUsableGps(pin.driverLat, pin.driverLng);
}

export const DUMPSTER_ORIENTATIONS = [
  'north', 'south', 'east', 'west',
  'northeast', 'northwest', 'southeast', 'southwest',
] as const;

/** Default hauler shown on customer dumpster requests when no crew is linked yet */
export const DEFAULT_WM_HAULER_NAME = 'Thompson Hauling and Transport';
export const DEFAULT_WM_HAULER_ID = 'local:thompson-hauling';

/**
 * Default hauler yard / dispatch origin for delivery-mile estimates.
 * Thompson service area is Hobbs / Lea County — yard pin is Hobbs city center
 * until a contractor-specific depot is stored on the crew record.
 * Destination is always exact: Google street address (residential/commercial)
 * or oilfield API/facility pad GPS / drop pin.
 */
export const DEFAULT_WM_HAULER_YARD = {
  lat: 32.7026,
  lng: -103.1360,
  label: 'Hobbs, NM (Thompson service area)',
  name: DEFAULT_WM_HAULER_NAME,
} as const;

/** One-way road miles at/above this → non-blocking long-haul warning (not a pay block). */
export const WM_LONG_HAUL_WARN_ONE_WAY_MI = 75;

export type WmHaulerYard = {
  lat: number;
  lng: number;
  label: string;
};

/**
 * Depot for mile quotes: company rate-card pin when set, else platform Hobbs yard.
 * Customer miles must follow the selected hauler, not always default Hobbs.
 */
export function wmYardForRateCard(
  rateCard?: Partial<WmRateCard> | null,
): WmHaulerYard {
  const card = rateCard && typeof rateCard === 'object' ? rateCard : null;
  const lat = Number(card?.yardLat);
  const lng = Number(card?.yardLng);
  if (isUsableGps(lat, lng)) {
    const label = card?.yardLabel != null && String(card.yardLabel).trim()
      ? String(card.yardLabel).trim().slice(0, 80)
      : `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
    return { lat, lng, label };
  }
  return {
    lat: DEFAULT_WM_HAULER_YARD.lat,
    lng: DEFAULT_WM_HAULER_YARD.lng,
    label: DEFAULT_WM_HAULER_YARD.label,
  };
}

export function wmIsLongHaulOneWayMiles(oneWayMiles: number | null | undefined): boolean {
  return oneWayMiles != null
    && Number.isFinite(oneWayMiles)
    && oneWayMiles >= WM_LONG_HAUL_WARN_ONE_WAY_MI;
}

/** Road vs straight-line factor for rural NM truck routes (estimate). */
export const WM_ROAD_MILES_FACTOR = 1.25;

/**
 * Selectable rental length (calendar days on pad).
 * Includes 1-day for short loans under **daily** pricing.
 * Package mode: trip + included window, then $ / add'l day.
 */
export const WM_RENTAL_DAY_OPTIONS = [1, 3, 7, 10, 14, 21, 28] as const;
export type WmRentalDays = (typeof WM_RENTAL_DAY_OPTIONS)[number];

/**
 * Dual billing modes (customer-facing):
 * - package = Flat rate: min 7 days (trip + included window) · add'l day after
 * - daily = Custom: pick your length · short stays scale fairly under the weekly flat
 *   (never more for fewer days than the 7-day flat package)
 */
export type WmPricingMode = 'package' | 'daily';
/** Minimum days allowed on the Flat rate package card. */
export const WM_FLAT_RATE_MIN_DAYS = 7;
/**
 * Short custom (1 day) floor as a share of the 7-day flat package.
 * Keeps 1–2 day loans below weekly while covering a fair share of the trip.
 */
export const WM_CUSTOM_SHORT_FLOOR_RATIO = 0.55;

export const WM_PRICING_MODES: Array<{ id: WmPricingMode; label: string; subtitle: string }> = [
  {
    id: 'package',
    label: 'Flat rate',
    subtitle: 'Min 7 days · best value week · extra days after',
  },
  {
    id: 'daily',
    label: 'Custom days',
    subtitle: '1–90 days · fair short-stay pricing · never more than the week flat',
  },
];

/**
 * Product lines we sell (industry-standard split):
 * Residential · Commercial · Construction roll-offs.
 * Site logistics (temp vs oilfield API pad) stay on WmServiceKind.
 */
export type WmProductLine = 'residential' | 'commercial' | 'construction';

/**
 * Waste stream drives box rules + tonnage (TRO / MyWM style).
 * - trash: household / business refuse
 * - construction_debris (CD): demo / remodel — concrete/rock often NOT allowed
 * - cardboard_recycling (CR): cardboard only
 */
export type WmWasteStream =
  | 'trash'
  | 'construction_debris'
  | 'cardboard_recycling'
  | 'heavy_material'
  | 'roofing'
  | 'wood'
  | 'drywall'
  | 'dirt_concrete';

/**
 * How the customer is using the roll-off (site logistics):
 * - temporary: short in-town / remodel style rental
 * - business_long_term: shop / yard / multi-week business account
 * - oilfield_job: pad / lease / API-number site (often long miles for the hauler)
 */
export type WmServiceKind = 'temporary' | 'business_long_term' | 'oilfield_job';

/**
 * Platform guide defaults (Hobbs MyWM haul anchors + dual pricing for small fleets).
 * - Delivery: $234.64 · Trash 30 YD haul: $578.36 · total package $813 within include window
 * - Package: $30 / add'l day after N included days (default N = 7)
 * - Daily: full trip + per-day rate so 1-day loans stay profitable
 * Company rate cards override these per hauler.
 */
export const WM_ADDL_DAY_USD = 30;
export const WM_DELIVERY_BASE_USD = 234.64;
/** Package included on-pad days before add'l day charges (platform default). */
export const WM_DEFAULT_DAYS_INCLUDED = 7;
/** Days at or below this → smart-default to daily mode. */
export const WM_DAILY_MODE_DEFAULT_MAX_DAYS = 3;
export const WM_DAILY_RATE_USD: Partial<Record<WasteDumpsterSize, number>> = {
  '10ft': 85,
  '12ft': 90,
  '15ft': 95,
  '20ft': 100,
  '25ft': 105,
  '30ft': 110,
  '40ft': 130,
  '50ft': 145,
};
export const WM_DAILY_RATE_DEFAULT_USD = 110;
/** Landfill tip closed (JS getDay): 0 = Sunday. */
export const WM_LANDFILL_CLOSED_WEEKDAYS = [0] as const;
export const WM_LANDFILL_CLOSED_LABEL = 'Sunday';

/**
 * Suggested one-way local radius a hauler may publish. Never applied unless
 * their rate card sets `deliveryFreeMiles` — book contractors have not agreed
 * to give 15 mi free each way.
 */
export const WM_DELIVERY_FREE_MILES = 15;
export const WM_DELIVERY_PER_MILE_USD = 4.5;

/** Hauler-published one-way included miles, or null when they did not set one. */
export function wmResolveDeliveryFreeMiles(card?: Partial<WmRateCard> | null): number | null {
  if (!card || card.deliveryFreeMiles == null || card.deliveryFreeMiles === ('' as unknown)) {
    return null;
  }
  const n = Number(card.deliveryFreeMiles);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(80, Math.round(n * 10) / 10));
}

/**
 * Customer-facing mile math from the hauler yard.
 * Free zone is applied only when the hauler published `freeOneWayMiles`.
 * Otherwise the whole round-trip is billed at $/mi.
 */
export function wmBillableMilesBreakdown(
  oneWayMiles: number | null | undefined,
  freeOneWayMiles?: number | null,
): {
  oneWay: number;
  roundTrip: number;
  freeOneWay: number;
  freeRoundTrip: number;
  hasFreeZone: boolean;
  billable: number;
  perMileUsd: number;
  milesUsd: number;
} | null {
  const oneWayRaw = Number(oneWayMiles);
  if (!Number.isFinite(oneWayRaw) || oneWayRaw <= 0) return null;
  const oneWay = Math.round(oneWayRaw * 10) / 10;
  const roundTrip = Math.round(oneWay * 2 * 10) / 10;
  const freeRaw = Number(freeOneWayMiles);
  const hasFreeZone = Number.isFinite(freeRaw) && freeRaw > 0;
  const freeOneWay = hasFreeZone ? Math.round(freeRaw * 10) / 10 : 0;
  const freeRoundTrip = freeOneWay * 2;
  const billable = Math.max(0, Math.round((roundTrip - freeRoundTrip) * 10) / 10);
  const perMileUsd = WM_DELIVERY_PER_MILE_USD;
  const milesUsd = Math.round(billable * perMileUsd * 100) / 100;
  return { oneWay, roundTrip, freeOneWay, freeRoundTrip, hasFreeZone, billable, perMileUsd, milesUsd };
}

/** Short line-item label with mile formula + $/mi (fits quote card). */
export function wmFormatBillableMilesLineLabel(
  oneWayMiles: number | null | undefined,
  freeOneWayMiles?: number | null,
): string {
  const b = wmBillableMilesBreakdown(oneWayMiles, freeOneWayMiles);
  if (!b) return 'Billable delivery miles';
  if (b.hasFreeZone && b.billable <= 0) {
    return `Delivery miles · ${b.oneWay} mi one-way (within ${b.freeOneWay} mi included each way)`;
  }
  if (!b.hasFreeZone) {
    return (
      `Delivery miles · ${b.oneWay} mi one-way × 2 = ${b.roundTrip} mi RT`
      + ` @ $${b.perMileUsd.toFixed(2)}/mi`
    );
  }
  return (
    `Billable miles · ${b.oneWay} mi one-way × 2 = ${b.roundTrip} mi RT`
    + ` − ${b.freeRoundTrip} included (${b.freeOneWay} mi each way)`
    + ` = +${Math.round(b.billable)} mi @ $${b.perMileUsd.toFixed(2)}/mi`
  );
}

/** Multi-line customer explanation for quote banners / help. */
export function wmFormatBillableMilesExplain(
  oneWayMiles: number | null | undefined,
  yardLabel?: string | null,
  freeOneWayMiles?: number | null,
): string {
  const b = wmBillableMilesBreakdown(oneWayMiles, freeOneWayMiles);
  if (!b) {
    return 'Delivery miles · set API / address / pin to price the trip from the hauler yard';
  }
  const from = yardLabel ? ` from ${yardLabel}` : '';
  if (b.hasFreeZone && b.billable <= 0) {
    return (
      `Delivery · ${b.oneWay} mi one-way${from} · within this hauler's included radius`
      + ` (${b.freeOneWay} mi each way) · $0 extra miles`
    );
  }
  if (!b.hasFreeZone) {
    return (
      `Miles${from}: ${b.oneWay} mi one-way × 2 = ${b.roundTrip} mi round-trip`
      + ` × $${b.perMileUsd.toFixed(2)}/mi = $${b.milesUsd.toFixed(2)}`
    );
  }
  return (
    `Miles${from}: ${b.oneWay} mi one-way × 2 = ${b.roundTrip} mi round-trip`
    + ` − ${b.freeRoundTrip} included (${b.freeOneWay} mi each way)`
    + ` = ${Math.round(b.billable)} billable mi × $${b.perMileUsd.toFixed(2)}/mi`
    + ` = $${b.milesUsd.toFixed(2)}`
  );
}

/**
 * Per-hauler rate card — overrides platform guide when set.
 *
 * Customer-facing idea (not “package vs daily” jargon):
 * - weekPackageUsdBySize = “my normal week price for a 30yd / 50yd”
 * - promoPercentOff = temporary sale when you have extra boxes
 * Internally the engine still uses week flat + short-stay fair curve for 1–6 day rentals.
 */
export interface WmRateCard {
  packageEnabled: boolean;
  dailyEnabled: boolean;
  daysIncluded: number;
  addlDayUsd: number;
  dailyModeMaxDays: number;
  /** Daily $ by size id (30ft, 50ft, …). Missing sizes fall back to platform. */
  dailyRateBySize: Partial<Record<WasteDumpsterSize, number>>;
  /** Optional delivery base override (null = use guide). */
  deliveryBaseUsd: number | null;
  defaultMode: 'auto' | WmPricingMode;
  /**
   * Hauler’s stated week price by size (30ft/50ft). When set, replaces platform
   * haul+delivery week total so they can undercut or raise for inventory.
   */
  weekPackageUsdBySize: Partial<Record<WasteDumpsterSize, number>>;
  /** Temporary sale 0–40% off the whole quote (extra dumpsters / weekend special). */
  promoPercentOff: number;
  /** Short customer-facing note, e.g. "Extra 30yds this week". */
  promoLabel: string | null;
  /** Sizes this hauler stocks (null = full standard 10–50 yd range). */
  sizesOffered: WasteDumpsterSize[] | null;
  /** When true, company appears on the customer multi-hauler list. */
  listOnMarketplace: boolean;
  /**
   * Hauler yard / depot GPS — customer delivery miles are measured from here.
   * null = use platform default (Hobbs Thompson yard).
   */
  yardLat: number | null;
  yardLng: number | null;
  /** Short place name shown in mile copy, e.g. "Farmington, NM". */
  yardLabel: string | null;
  /**
   * One-way miles this hauler includes in the drop (from their yard).
   * null = they did not publish a local radius — bill the whole trip.
   */
  deliveryFreeMiles: number | null;
}

export function wmPlatformRateCard(): WmRateCard {
  return {
    packageEnabled: true,
    dailyEnabled: true,
    daysIncluded: WM_DEFAULT_DAYS_INCLUDED,
    addlDayUsd: WM_ADDL_DAY_USD,
    dailyModeMaxDays: WM_DAILY_MODE_DEFAULT_MAX_DAYS,
    dailyRateBySize: { ...WM_DAILY_RATE_USD },
    deliveryBaseUsd: null,
    defaultMode: 'auto',
    weekPackageUsdBySize: {},
    promoPercentOff: 0,
    promoLabel: null,
    sizesOffered: null,
    listOnMarketplace: true,
    yardLat: null,
    yardLng: null,
    yardLabel: null,
    deliveryFreeMiles: null,
  };
}

export function wmNormalizeRateCard(raw?: Partial<WmRateCard> | null): WmRateCard {
  const base = wmPlatformRateCard();
  if (!raw || typeof raw !== 'object') return base;
  const daysIncluded = Math.max(1, Math.min(30, Math.round(Number(raw.daysIncluded) || base.daysIncluded)));
  const addlDayUsd = Math.max(0, Math.min(500, Number(raw.addlDayUsd)));
  const dailyModeMaxDays = Math.max(1, Math.min(14, Math.round(Number(raw.dailyModeMaxDays) || base.dailyModeMaxDays)));
  const dailyRateBySize: Partial<Record<WasteDumpsterSize, number>> = { ...base.dailyRateBySize };
  if (raw.dailyRateBySize && typeof raw.dailyRateBySize === 'object') {
    for (const [k, v] of Object.entries(raw.dailyRateBySize)) {
      const n = Number(v);
      if (Number.isFinite(n) && n >= 0 && n <= 2000) {
        dailyRateBySize[normalizeWmDumpsterSize(k)] = Math.round(n * 100) / 100;
      }
    }
  }
  const weekPackageUsdBySize: Partial<Record<WasteDumpsterSize, number>> = {};
  if (raw.weekPackageUsdBySize && typeof raw.weekPackageUsdBySize === 'object') {
    for (const [k, v] of Object.entries(raw.weekPackageUsdBySize)) {
      const n = Number(v);
      if (Number.isFinite(n) && n >= 50 && n <= 20000) {
        weekPackageUsdBySize[normalizeWmDumpsterSize(k)] = Math.round(n * 100) / 100;
      }
    }
  }
  const promoRaw = Number(raw.promoPercentOff);
  const promoPercentOff = Number.isFinite(promoRaw)
    ? Math.max(0, Math.min(40, Math.round(promoRaw)))
    : 0;
  const promoLabel = raw.promoLabel != null && String(raw.promoLabel).trim()
    ? String(raw.promoLabel).trim().slice(0, 80)
    : null;
  const deliv = raw.deliveryBaseUsd == null || raw.deliveryBaseUsd === ('' as unknown)
    ? null
    : Number(raw.deliveryBaseUsd);
  let sizesOffered: WasteDumpsterSize[] | null = null;
  if (Array.isArray(raw.sizesOffered) && raw.sizesOffered.length > 0) {
    const valid = new Set(WM_DUMPSTER_SIZES);
    const clean = raw.sizesOffered
      .map((s) => normalizeWmDumpsterSize(s))
      .filter((s) => valid.has(s));
    if (clean.length > 0) sizesOffered = Array.from(new Set(clean));
  }
  // Yard pin: both coords must be usable GPS (reject Null Island / garbage).
  const yardLatRaw = Number((raw as { yardLat?: unknown }).yardLat);
  const yardLngRaw = Number((raw as { yardLng?: unknown }).yardLng);
  const yardOk = isUsableGps(yardLatRaw, yardLngRaw);
  const yardLat = yardOk ? Math.round(yardLatRaw * 1e6) / 1e6 : null;
  const yardLng = yardOk ? Math.round(yardLngRaw * 1e6) / 1e6 : null;
  const yardLabel = yardOk && raw.yardLabel != null && String(raw.yardLabel).trim()
    ? String(raw.yardLabel).trim().slice(0, 80)
    : null;
  return {
    packageEnabled: raw.packageEnabled !== false,
    dailyEnabled: raw.dailyEnabled !== false,
    daysIncluded,
    addlDayUsd: Number.isFinite(addlDayUsd) ? Math.round(addlDayUsd * 100) / 100 : base.addlDayUsd,
    dailyModeMaxDays,
    dailyRateBySize,
    deliveryBaseUsd: deliv != null && Number.isFinite(deliv) && deliv >= 0 ? Math.round(deliv * 100) / 100 : null,
    defaultMode: raw.defaultMode === 'package' || raw.defaultMode === 'daily' ? raw.defaultMode : 'auto',
    weekPackageUsdBySize,
    promoPercentOff,
    promoLabel,
    sizesOffered,
    listOnMarketplace: raw.listOnMarketplace !== false,
    yardLat,
    yardLng,
    yardLabel,
    deliveryFreeMiles: wmResolveDeliveryFreeMiles(raw),
  };
}

/** Bookable hauler on the customer multi-company list. */
export type WmBookableHauler = {
  id: string;
  crewId: string | null;
  companyName: string;
  source: 'platform' | 'company';
  rateCard: WmRateCard;
  /** Quote for the request params (size/days) when the list was loaded. */
  quotedTotalUsd: number;
  /** Sizes this hauler stocks (server-resolved; full range when card is open). */
  sizesOffered: WasteDumpsterSize[];
  promoLabel?: string | null;
  promoPercentOff?: number;
  isDefault?: boolean;
};

export function wmNormalizePricingMode(mode?: WmPricingMode | string | null): WmPricingMode {
  return mode === 'daily' ? 'daily' : 'package';
}

export function wmSuggestedPricingMode(
  days: number,
  rateCard?: Partial<WmRateCard> | null,
): WmPricingMode {
  const card = wmNormalizeRateCard(rateCard);
  void days;
  // Week is the product. Auto-flipping to the short-stay curve when someone
  // nudged deliver/remove by a day made checkout jump hundreds of dollars.
  // Daily stays available only when a hauler's card opts into it.
  if (card.defaultMode === 'daily' && card.dailyEnabled) return 'daily';
  if (!card.packageEnabled && card.dailyEnabled) return 'daily';
  return 'package';
}

/**
 * Fair custom total for short stays (days ≤ flat week):
 * linear from floor% of week at day 1 → 100% of week at day N.
 * Never exceeds the flat package for that week.
 */
export function wmFairCustomShortTotalUsd(flatWeekUsd: number, days: number, flatDays: number): number {
  const week = Math.max(0, Number(flatWeekUsd) || 0);
  const d = Math.max(1, Math.min(Math.round(Number(days) || 1), Math.max(1, flatDays)));
  const n = Math.max(1, Math.round(Number(flatDays) || WM_FLAT_RATE_MIN_DAYS));
  if (d >= n) return Math.round(week * 100) / 100;
  // day 1 → floor ratio, day n → 1.0
  const t = n <= 1 ? 1 : (d - 1) / (n - 1);
  const ratio = WM_CUSTOM_SHORT_FLOOR_RATIO + (1 - WM_CUSTOM_SHORT_FLOOR_RATIO) * t;
  return Math.round(week * ratio * 100) / 100;
}

export function wmDailyRateUsd(
  size?: WasteDumpsterSize | string | null,
  rateCard?: Partial<WmRateCard> | null,
): number {
  const card = wmNormalizeRateCard(rateCard);
  const key = normalizeWmDumpsterSize(size || '30ft');
  return card.dailyRateBySize[key] ?? WM_DAILY_RATE_USD[key] ?? WM_DAILY_RATE_DEFAULT_USD;
}

export function wmParseLocalNoon(iso?: string | null): Date | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso).trim())) return null;
  const d = new Date(`${String(iso).trim()}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function wmFormatIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function wmIsLandfillClosedDate(iso?: string | null): boolean {
  const d = wmParseLocalNoon(iso);
  if (!d) return false;
  return (WM_LANDFILL_CLOSED_WEEKDAYS as readonly number[]).includes(d.getDay());
}

export function wmNextLandfillOpenDate(iso: string): string {
  const start = wmParseLocalNoon(iso);
  if (!start) return iso;
  const d = new Date(start.getTime());
  for (let i = 0; i < 14; i += 1) {
    if (!(WM_LANDFILL_CLOSED_WEEKDAYS as readonly number[]).includes(d.getDay())) {
      return wmFormatIsoDate(d);
    }
    d.setDate(d.getDate() + 1);
  }
  return wmFormatIsoDate(d);
}

export function wmAdjustRemovalForLandfill(removalIso?: string | null): {
  removalDate: string | null;
  adjusted: boolean;
  reason: string | null;
} {
  if (!removalIso) return { removalDate: null, adjusted: false, reason: null };
  if (!wmIsLandfillClosedDate(removalIso)) {
    return { removalDate: removalIso, adjusted: false, reason: null };
  }
  const next = wmNextLandfillOpenDate(removalIso);
  return {
    removalDate: next,
    adjusted: next !== removalIso,
    reason:
      `Landfill is closed on ${WM_LANDFILL_CLOSED_LABEL}s — removal moved to ${next} `
      + '(extra on-pad day(s) are billed).',
  };
}

export function wmLandfillScheduleNotes(
  deliveryIso?: string | null,
  removalIso?: string | null,
): string[] {
  const notes: string[] = [];
  if (wmIsLandfillClosedDate(deliveryIso)) {
    notes.push(
      `Delivery on ${WM_LANDFILL_CLOSED_LABEL} is OK (drop only). `
      + `Haul / dump cannot run until the landfill reopens (closed ${WM_LANDFILL_CLOSED_LABEL}s).`,
    );
  }
  if (removalIso && wmIsLandfillClosedDate(removalIso)) {
    const adj = wmAdjustRemovalForLandfill(removalIso);
    notes.push(adj.reason || `Landfill closed ${WM_LANDFILL_CLOSED_LABEL}s — pick Mon–Sat removal.`);
  }
  return notes;
}

export const WM_PRODUCT_LINES: Array<{
  id: WmProductLine;
  label: string;
  subtitle: string;
  idealFor: string[];
  included: string[];
  defaultWasteStream: WmWasteStream;
  /** Allowed box sizes for this line */
  sizes: WasteDumpsterSize[];
  /** Days typically included before add'l day charges (guide-style) */
  daysIncluded: number;
  /** Suggested service kind when this line is picked */
  defaultServiceKind: WmServiceKind;
}> = [
  {
    id: 'residential',
    label: 'Residential dumpster',
    subtitle: 'Home cleanouts · remodel · garage · roofing trash',
    idealFor: [
      'Home cleanouts',
      'Garage / attic',
      'Residential remodel construction trash',
      'Roofing tear-off (shingles)',
    ],
    included: ['Delivery and pickup', 'Convenient scheduling', 'Open-gate pin for the driver'],
    defaultWasteStream: 'trash',
    sizes: [...WM_DUMPSTER_SIZES],
    daysIncluded: WM_DEFAULT_DAYS_INCLUDED,
    defaultServiceKind: 'temporary',
  },
  {
    id: 'commercial',
    label: 'Commercial dumpster',
    subtitle: 'Business · shop · tenant finish · cardboard',
    idealFor: [
      'Business / office trash',
      'Warehouse or shop cleanouts',
      'Commercial construction / tenant finish',
      'Cardboard recycling accounts',
    ],
    included: ['Delivery and pickup', 'Account-style scheduling', 'Haul when full via in-app pickup request'],
    defaultWasteStream: 'trash',
    sizes: [...WM_DUMPSTER_SIZES],
    daysIncluded: WM_DEFAULT_DAYS_INCLUDED,
    defaultServiceKind: 'business_long_term',
  },
  {
    id: 'construction',
    label: 'Construction dumpster',
    subtitle: 'Jobsite · roofing · commercial GC · oilfield pad',
    idealFor: [
      'Large construction projects',
      'Commercial / industrial build-out',
      'Roofing jobs',
      'Oilfield pad / lease construction trash',
    ],
    included: ['Delivery and pickup', 'Pin + gate orientation for drivers', 'Pickup request when the box is full'],
    defaultWasteStream: 'construction_debris',
    sizes: [...WM_DUMPSTER_SIZES],
    daysIncluded: WM_DEFAULT_DAYS_INCLUDED,
    defaultServiceKind: 'temporary',
  },
];

export const WM_WASTE_STREAMS: Array<{
  id: WmWasteStream;
  label: string;
  /** CD / CR style short code for size chips */
  shortCode: string;
  tonsIncluded: number;
  /** Overage hint $/ton (catalog guide — hauler may adjust) */
  overagePerTonUsd: number;
  sizes: WasteDumpsterSize[];
  productLines: WmProductLine[];
  notAllowedNote: string;
  description: string;
}> = [
  {
    id: 'trash',
    label: 'Trash / mixed refuse',
    shortCode: '',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 54.15,
    sizes: [...WM_DUMPSTER_SIZES],
    productLines: ['residential', 'commercial', 'construction'],
    notAllowedNote: 'Concrete, rock, and bricks NOT allowed',
    description:
      'Household, office, shop, and light mixed refuse — including residential construction trash when not full demo CD',
  },
  {
    id: 'construction_debris',
    label: 'Construction debris',
    shortCode: 'CD',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 54.15,
    sizes: [...WM_DUMPSTER_SIZES],
    // Residential remodel / commercial tenant finish / oilfield pad construction all use CD
    productLines: ['residential', 'commercial', 'construction'],
    notAllowedNote: 'Concrete, rock, and bricks NOT allowed in standard CD boxes',
    description:
      'Demo, remodel, roofing, commercial build-out, and oilfield pad construction debris (non-dirt, non-hazmat)',
  },
  {
    id: 'cardboard_recycling',
    label: 'Cardboard recycling',
    shortCode: 'CR',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 252.69,
    sizes: [...WM_DUMPSTER_SIZES],
    productLines: ['commercial', 'construction'],
    notAllowedNote: 'Cardboard only, no mixed-use recycling',
    description: 'Corrugated cardboard only for recycle haul (warehouses, stores, staging yards)',
  },
  {
    id: 'heavy_material',
    label: 'Heavy material (pre-approved)',
    shortCode: 'HV',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 85,
    sizes: [...WM_DUMPSTER_SIZES],
    productLines: ['commercial', 'construction'],
    notAllowedNote: 'Dirt, concrete, rock, brick only when pre-approved — never mixed with trash/CD',
    description: 'Inert/heavy loads when hauler and landfill allow (driveways, pad base, masonry)',
  },
  {
    id: 'roofing',
    label: 'Roofing / shingles',
    shortCode: 'RF',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 54.15,
    sizes: [...WM_DUMPSTER_SIZES],
    productLines: ['residential', 'commercial', 'construction'],
    notAllowedNote: 'No asbestos shingles. Concrete and dirt stay out of this box.',
    description: 'Tear-off shingles and roofing felt — priced as construction debris',
  },
  {
    id: 'wood',
    label: 'Wood / lumber / pallets',
    shortCode: 'WD',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 54.15,
    sizes: [...WM_DUMPSTER_SIZES],
    productLines: ['residential', 'commercial', 'construction'],
    notAllowedNote: 'No treated railroad ties or painted marine lumber unless the hauler allows',
    description: 'Clean wood, framing, and pallets — priced as construction debris',
  },
  {
    id: 'drywall',
    label: 'Drywall / demo',
    shortCode: 'DW',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 54.15,
    sizes: [...WM_DUMPSTER_SIZES],
    productLines: ['residential', 'commercial', 'construction'],
    notAllowedNote: 'No asbestos. Concrete and brick stay in a heavy box.',
    description: 'Sheetrock and interior demo — priced as construction debris',
  },
  {
    id: 'dirt_concrete',
    label: 'Dirt / concrete / caliche',
    shortCode: 'HV',
    tonsIncluded: WM_INCLUDED_TONS_FREE,
    overagePerTonUsd: 85,
    sizes: [...WM_DUMPSTER_SIZES],
    productLines: ['commercial', 'construction'],
    notAllowedNote: 'Inert only — never mix with trash or CD. Hauler must pre-approve.',
    description: 'Dirt, concrete, caliche, brick — priced as heavy material',
  },
];

/**
 * Different haulers offer different box inventories.
 * Use wmSizesForHaulerProfile to filter chips so we don't quote a size they don't run.
 */
export type WmHaulerSizeProfileId = 'wm_style' | 'oilfield_extended' | 'compact';

/** Full standard roll-off range; selected hauler's rate card narrows further. */
const ALL_STANDARD_SIZES: WasteDumpsterSize[] = [...WM_DUMPSTER_SIZES];
const COMPACT_SIZES: WasteDumpsterSize[] = ['10ft', '12ft', '15ft', '20ft'];

export const WM_HAULER_SIZE_PROFILES: Array<{
  id: WmHaulerSizeProfileId;
  label: string;
  /** Per-stream size inventory (empty stream = not offered) */
  sizesByStream: Record<WmWasteStream, WasteDumpsterSize[]>;
}> = [
  {
    id: 'wm_style',
    label: 'Standard roll-off range (10–50 yd)',
    sizesByStream: {
      trash: [...ALL_STANDARD_SIZES],
      construction_debris: [...ALL_STANDARD_SIZES],
      cardboard_recycling: [...ALL_STANDARD_SIZES],
      heavy_material: [...ALL_STANDARD_SIZES],
      roofing: [...ALL_STANDARD_SIZES],
      wood: [...ALL_STANDARD_SIZES],
      drywall: [...ALL_STANDARD_SIZES],
      dirt_concrete: [...ALL_STANDARD_SIZES],
    },
  },
  {
    id: 'oilfield_extended',
    label: 'Oilfield / construction full range',
    sizesByStream: {
      trash: [...ALL_STANDARD_SIZES],
      construction_debris: [...ALL_STANDARD_SIZES],
      cardboard_recycling: [...ALL_STANDARD_SIZES],
      heavy_material: [...ALL_STANDARD_SIZES],
      roofing: [...ALL_STANDARD_SIZES],
      wood: [...ALL_STANDARD_SIZES],
      drywall: [...ALL_STANDARD_SIZES],
      dirt_concrete: [...ALL_STANDARD_SIZES],
    },
  },
  {
    id: 'compact',
    label: 'Compact boxes (10–20 yd)',
    sizesByStream: {
      trash: [...COMPACT_SIZES],
      construction_debris: [...COMPACT_SIZES],
      cardboard_recycling: [...COMPACT_SIZES],
      heavy_material: [...COMPACT_SIZES],
      roofing: [...COMPACT_SIZES],
      wood: [...COMPACT_SIZES],
      drywall: [...COMPACT_SIZES],
      dirt_concrete: [...COMPACT_SIZES],
    },
  },
];

export function wmHaulerSizeProfile(id?: WmHaulerSizeProfileId | string | null) {
  return WM_HAULER_SIZE_PROFILES.find((p) => p.id === id) || WM_HAULER_SIZE_PROFILES[0];
}

export const WM_SERVICE_KIND_OPTIONS: Array<{
  id: WmServiceKind;
  label: string;
  subtitle: string;
  /** Typical haul profile for pricing honesty */
  haulProfile: 'in_town' | 'mixed' | 'oilfield_miles';
  requiresApiOrLease: boolean;
  hint: string;
}> = [
  {
    id: 'temporary',
    label: 'Temporary roll-off',
    subtitle: 'Short rental · remodel / cleanout',
    haulProfile: 'in_town',
    requiresApiOrLease: false,
    hint: 'Best match for low-mileage in-town drops. Day packages are close; overage tons still apply.',
  },
  {
    id: 'business_long_term',
    label: 'Business / contractor long-term',
    subtitle: 'Shop, yard, or multi-week pad',
    haulProfile: 'mixed',
    requiresApiOrLease: false,
    hint: 'Use longer day packages or re-order when full. Driver still needs a clear pin + gate direction.',
  },
  {
    id: 'oilfield_job',
    label: 'Oilfield job / lease',
    subtitle: 'API # or facility when no street address',
    haulProfile: 'oilfield_miles',
    requiresApiOrLease: true,
    hint:
      'Oilfield only: if the pad has no street address, add an API # or facility/lease name so the driver can navigate the lease roads. '
      + 'Residential and commercial buildings use a normal street address — no API required. '
      + 'Long hauls may need a mileage surcharge beyond the day package.',
  },
];

/**
 * True when text looks like a full, verifiable US street address — not a partial
 * type-ahead fragment like "926 east". Prefer Google Places formatted_address.
 */
export function wmIsFullServiceAddress(address?: string | null): boolean {
  const a = String(address || '').trim().replace(/\s+/g, ' ');
  if (a.length < 12) return false;
  // Need a house/building number (or leading number in unit-style)
  if (!/\d/.test(a)) return false;

  // Google / geocoder style: "123 Main St, Hobbs, NM 88240"
  if (a.includes(',')) {
    const parts = a.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2 && parts[0].length >= 5) return true;
  }

  // No comma: street designator + enough tokens for city/state
  const hasStreetType = /\b(st|street|ave|avenue|rd|road|dr|drive|blvd|ln|lane|ct|court|way|hwy|highway|pkwy|pl|place|cir|circle|trl|trail|loop|ter|terrace)\b\.?/i.test(a);
  const tokens = a.split(' ').filter(Boolean);
  if (hasStreetType && tokens.length >= 4) return true;

  // State abbreviation near end (e.g. "123 Main Street Hobbs NM 88240")
  if (/\b[A-Z]{2}\b/.test(a) && tokens.length >= 4 && a.length >= 16) return true;

  return false;
}

/** True when customer has a usable physical/street service address. */
export function wmHasPhysicalServiceAddress(address?: string | null): boolean {
  return wmIsFullServiceAddress(address);
}

/**
 * Oilfield contractors need API # or facility/lease name only when there is no street address.
 * Residential homes and commercial buildings never require an API number.
 */
export function wmRequiresOilfieldPadLocate(
  serviceKind?: WmServiceKind | string | null,
  serviceAddress?: string | null,
): boolean {
  if (String(serviceKind || '') !== 'oilfield_job') return false;
  return !wmHasPhysicalServiceAddress(serviceAddress);
}

export function wmProductLineMeta(id?: WmProductLine | string | null) {
  return WM_PRODUCT_LINES.find((p) => p.id === id) || WM_PRODUCT_LINES[0];
}

export function wmWasteStreamMeta(id?: WmWasteStream | string | null) {
  return WM_WASTE_STREAMS.find((w) => w.id === id) || WM_WASTE_STREAMS[0];
}

/**
 * Sizes valid for a product line + waste stream intersection, narrowed to the
 * selected hauler's actual inventory when their rate card lists sizesOffered.
 */
export function wmSizesForSelection(
  productLine?: WmProductLine | string | null,
  wasteStream?: WmWasteStream | string | null,
  haulerProfileId?: WmHaulerSizeProfileId | string | null,
  haulerSizesOffered?: Array<WasteDumpsterSize | string> | null,
): WasteDumpsterSize[] {
  const line = wmProductLineMeta(productLine);
  const stream = wmWasteStreamMeta(wasteStream);
  const lineSet = new Set(line.sizes);
  const streamSizes = stream.sizes.filter((s) => lineSet.has(s));
  const profile = wmHaulerSizeProfile(haulerProfileId);
  const haulerSet = new Set(profile.sizesByStream[stream.id] || []);
  let filtered = streamSizes.filter((s) => haulerSet.has(s));
  if (!filtered.length) filtered = streamSizes;
  if (Array.isArray(haulerSizesOffered) && haulerSizesOffered.length > 0) {
    const offered = new Set(haulerSizesOffered.map((s) => normalizeWmDumpsterSize(s)));
    const stocked = filtered.filter((s) => offered.has(s));
    if (stocked.length) filtered = stocked;
  }
  return filtered;
}

/** Size chip label like MyWM: "30 YD" / "40 YD CD" / "10 YD CR". */
export function wmSizeChipLabel(
  size: WasteDumpsterSize | string,
  wasteStream?: WmWasteStream | string | null,
): string {
  const yards = normalizeWmDumpsterSize(size).replace('ft', '');
  const code = wmWasteStreamMeta(wasteStream).shortCode;
  return code ? `${yards} YD ${code}` : `${yards} YD`;
}

/** Inclusive calendar days between delivery and removal (min 1). */
export function wmRentalDaysFromDates(deliveryIso: string, removalIso?: string | null): number {
  if (!deliveryIso) return WM_DEFAULT_DAYS_INCLUDED;
  const start = new Date(`${deliveryIso}T12:00:00`);
  if (Number.isNaN(start.getTime())) return WM_DEFAULT_DAYS_INCLUDED;
  if (!removalIso) return WM_DEFAULT_DAYS_INCLUDED;
  const end = new Date(`${removalIso}T12:00:00`);
  if (Number.isNaN(end.getTime()) || end < start) return 1;
  const ms = end.getTime() - start.getTime();
  return Math.max(1, Math.round(ms / (24 * 60 * 60 * 1000)) + 1);
}

export function wmWasteStreamsForProductLine(productLine?: WmProductLine | string | null) {
  const lineId = wmProductLineMeta(productLine).id;
  return WM_WASTE_STREAMS.filter((w) => w.productLines.includes(lineId));
}

/**
 * Factors that may alter final rental cost (shown to customers — matches industry guides).
 */
export const WM_COST_ALTERING_FACTORS = [
  {
    id: 'excess_weight',
    label: 'Excess weight',
    hint: 'Loads over included tons are billed at the overage $/ton rate for that waste stream.',
  },
  {
    id: 'fluctuating_tonnage',
    label: 'Fluctuating tonnage',
    hint: 'Dense materials (shingles, dirt if pre-approved, wet loads) raise weight faster than volume.',
  },
  {
    id: 'extended_rental',
    label: 'Extended rental period',
    hint:
      `After the included week, extra on-pad days are about $${WM_ADDL_DAY_USD}/day. `
      + 'Your quote total already reflects the days you picked. Hauler rates can differ slightly.',
  },
  {
    id: 'landfill_sunday',
    label: 'Landfill closed Sundays',
    hint:
      `Tip is closed on ${WM_LANDFILL_CLOSED_LABEL}s — haul/removal cannot dump that day. `
      + 'Removal auto-moves to the next open day and extra on-pad time is billed.',
  },
] as const;

/**
 * Rough delivery surcharge vs 30-yd in-town day package.
 * Not a locked catalog — signals that oilfield miles are not free.
 * In-town ≈ 1.0; regional ≈ +12%; oilfield long haul ≈ +25% until hauler quotes true miles.
 */
export const WM_HAUL_PRICE_MULTIPLIER: Record<'in_town' | 'mixed' | 'oilfield_miles', number> = {
  in_town: 1.0,
  mixed: 1.08,
  oilfield_miles: 1.25,
};

export function wmServiceKindMeta(kind?: WmServiceKind | string | null) {
  return WM_SERVICE_KIND_OPTIONS.find((o) => o.id === kind) || WM_SERVICE_KIND_OPTIONS[0];
}

export function wmRentalPriceWithHaulUsd(
  days: number,
  size: WasteDumpsterSize | string = '30ft',
  serviceKind: WmServiceKind | string = 'temporary',
): number {
  return wmRentalPriceUsd(days, size, serviceKind);
}

/**
 * Legacy fallback when waste stream is unknown (policy: 1 free ton).
 * Prefer wmWasteStreamMeta(stream).tonsIncluded for real quotes.
 */
export const WM_TONS_INCLUDED = 1;

/**
 * Zia's cut on dumpster rentals. 1000 bps = 10%.
 *
 * Same number as haul and water. Documentation is included; businesses cover
 * card processing separately on new work. Old jobs keep the
 * platform_fee_bps stored on the row.
 */
export const WM_PLATFORM_FEE_BPS = 1000;

/** Estimate US card Stripe fee (2.9% + $0.30). Actual fee locked on webhook when available. */
export function wmEstimateStripeFeeCents(amountCents: number): number {
  const a = Math.max(0, Math.floor(Number(amountCents) || 0));
  if (a <= 0) return 0;
  return Math.round(a * 0.029) + 30;
}

/**
 * Customer gross split for dumpster rental / overage.
 * - Zia: 12% platform fee
 * - Hauler: 88% of gross minus Stripe fees (hauler absorbs processing)
 */
export function wmFeeSplitCents(
  grossCents: number | null | undefined,
  stripeFeeCents: number | null = null,
  savedFeeBps: number | null = WM_PLATFORM_FEE_BPS,
): {
  grossCents: number;
  platformFeeBps: number;
  platformFeeCents: number;
  stripeFeeCents: number;
  stripeFeeEstimated: boolean;
  contractorPayoutCents: number;
} {
  const gross = Math.max(0, Math.floor(Number(grossCents) || 0));
  const feeBps = savedFeeBps != null && Number.isFinite(Number(savedFeeBps)) && Number(savedFeeBps) >= 0
    ? Math.floor(Number(savedFeeBps)) : WM_PLATFORM_FEE_BPS;
  const platformFeeCents = Math.floor((gross * feeBps) / 10000);
  const estimated = stripeFeeCents == null || !Number.isFinite(Number(stripeFeeCents));
  const stripeFee = estimated
    ? wmEstimateStripeFeeCents(gross)
    : Math.max(0, Math.floor(Number(stripeFeeCents)));
  const contractorPayoutCents = Math.max(0, gross - platformFeeCents - stripeFee);
  return {
    grossCents: gross,
    platformFeeBps: feeBps,
    platformFeeCents,
    stripeFeeCents: stripeFee,
    stripeFeeEstimated: estimated,
    contractorPayoutCents,
  };
}

/**
 * Per-job money lines for hauler card $ fold.
 * Prefers stored fee columns when present; otherwise estimates Stripe from gross.
 */
export function wmJobHaulerMoneySplit(
  job: Pick<
    WasteDumpsterJob,
    'amount_cents' | 'platform_fee_cents' | 'platform_fee_bps' | 'stripe_fee_cents' | 'stripe_fee_estimated' | 'contractor_payout_cents'
  > | null | undefined,
): {
  grossCents: number;
  platformFeeCents: number;
  stripeFeeCents: number;
  contractorPayoutCents: number;
  stripeEstimated: boolean;
} | null {
  if (!job) return null;
  const gross = Math.floor(Number(job.amount_cents) || 0);
  if (gross <= 0) return null;

  const hasPlatform = job.platform_fee_cents != null && Number.isFinite(Number(job.platform_fee_cents));
  const hasStripe = job.stripe_fee_cents != null && Number.isFinite(Number(job.stripe_fee_cents));
  const hasContractor = job.contractor_payout_cents != null
    && Number.isFinite(Number(job.contractor_payout_cents))
    && Number(job.contractor_payout_cents) >= 0;

  if (hasPlatform) {
    const platformFeeCents = Math.max(0, Math.floor(Number(job.platform_fee_cents)));
    const stripeEstimated = !hasStripe || job.stripe_fee_estimated === 1;
    const stripeFeeCents = hasStripe
      ? Math.max(0, Math.floor(Number(job.stripe_fee_cents)))
      : wmEstimateStripeFeeCents(gross);
    /*
     * Stored value first; the subtraction is a LEGACY reconstruction.
     *
     * A row without contractor_payout_cents predates that column, and those
     * jobs really were paid gross minus platform minus Stripe — the hauler
     * bore processing then. Recomputing them the new way would retroactively
     * inflate what a completed job says it paid, which is the opposite of what
     * a money line is for. New jobs always store the value, so this branch
     * only ever runs for old ones.
     */
    const contractorPayoutCents = hasContractor
      ? Math.max(0, Math.floor(Number(job.contractor_payout_cents)))
      : Math.max(0, gross - platformFeeCents - stripeFeeCents);
    return {
      grossCents: gross,
      platformFeeCents,
      stripeFeeCents,
      contractorPayoutCents,
      stripeEstimated,
    };
  }

  const split = wmFeeSplitCents(gross, hasStripe ? Number(job.stripe_fee_cents) : null, job.platform_fee_bps ?? WM_PLATFORM_FEE_BPS);
  return {
    grossCents: split.grossCents,
    platformFeeCents: split.platformFeeCents,
    stripeFeeCents: split.stripeFeeCents,
    contractorPayoutCents: split.contractorPayoutCents,
    stripeEstimated: split.stripeFeeEstimated,
  };
}

/** Default support phone for the default hauler (customer call / text) */
export const DEFAULT_WM_SUPPORT_PHONE = '';

/**
 * Bottom-up pricing (WM / MyWM model) — NOT a linear day ladder.
 *
 * Customer pays:
 *   1. Haul (disposal / processing) — scales with size + waste stream
 *   2. Delivery (drop-off trip) — scales with size + zone + optional miles
 *   3. Add'l usage days beyond product-line included window
 *   4. Optional overage tons preview (not always collected up-front)
 *
 * Calibrated to public MyWM-style line items (~$235 delivery; ~$540 haul on 40yd trash;
 * cardboard haul much higher). Hauler can still override at Accept.
 */

/** @deprecated legacy constant — use wmEstimateQuote.haulUsd */
export const WM_PRICE_30FT_SIX_DAY_USD = 500;
/** @deprecated legacy constant — weekly package no longer cheaper-than-6-day */
export const WM_PRICE_30FT_SEVEN_DAY_USD = 735;
/** @deprecated Use size haul tables */
export const WM_SIZE_50FT_PRICE_MULTIPLIER = 1.4;

/** Relative size weight for delivery / haul scaling (30 yd = 1.0). */
export const WM_SIZE_PRICE_MULTIPLIER: Record<WasteDumpsterSize, number> = {
  '10ft': 0.72,
  '12ft': 0.78,
  '15ft': 0.82,
  '20ft': 0.86,
  '25ft': 0.93,
  '30ft': 1.0,
  '40ft': 1.12,
  '50ft': 1.28,
};

/**
 * Exact / interpolated haul USD by stream × size (MyWM screenshots as anchors).
 * Hobbs NM trash compare (2026): 10=$594.04 · 20=$503.25 · 30=$578.36 · deliv=$234.64
 * Other streams: CD 40=$555.12 · CR = trash haul × 1.10 (dedicated recycle, not a 3× screenshot)
 * Note: trash haul is not monotonic by size at this market (10yd can cost more than 20yd).
 */
const WM_HAUL_CD: Partial<Record<WasteDumpsterSize, number>> = {
  '10ft': 488.51,
  '12ft': 500.0,
  '15ft': 508.0,
  '20ft': 516.26,
  '25ft': 530.14,
  '30ft': 538.46,
  '40ft': 555.12,
  '50ft': 599.53,
};
const WM_HAUL_HV: Partial<Record<WasteDumpsterSize, number>> = {
  '10ft': 620.0,
  '12ft': 680.0,
  '15ft': 740.0,
  '20ft': 820.0,
  '30ft': 980.0,
  '50ft': 1180.0,
};

export const WM_HAUL_USD_BY_STREAM: Record<WmWasteStream, Partial<Record<WasteDumpsterSize, number>>> = {
  trash: {
    '10ft': 594.04, // Hobbs compare table
    '12ft': 564.0,
    '15ft': 534.0,
    '20ft': 503.25, // Hobbs compare table
    '25ft': 540.8,
    '30ft': 578.36, // Hobbs 30 YD quote · total $813 w/ delivery
    '40ft': 620.0,
    '50ft': 665.0,
  },
  construction_debris: WM_HAUL_CD,
  cardboard_recycling: {
    '10ft': 653.44, // trash × 1.10
    '12ft': 620.40,
    '15ft': 587.40,
    '20ft': 553.58,
    '25ft': 594.88,
    '30ft': 636.20,
    '40ft': 682.00,
    '50ft': 731.50,
  },
  heavy_material: WM_HAUL_HV,
  roofing: WM_HAUL_CD,
  wood: WM_HAUL_CD,
  drywall: WM_HAUL_CD,
  dirt_concrete: WM_HAUL_HV,
};

/**
 * Included tons by stream × size.
 * Policy (2026-08): **1 free ton on every box** — size table kept empty so
 * wmTonsIncluded always falls back to WM_INCLUDED_TONS_FREE / stream default.
 * (Older Hobbs compare rows lived here; do not reintroduce multi-ton free.)
 */
export const WM_TONS_INCLUDED_BY_STREAM_SIZE: Partial<
  Record<WmWasteStream, Partial<Record<WasteDumpsterSize, number>>>
> = {};

/** @deprecated use WM_HAUL_USD_BY_STREAM.trash */
export const WM_HAUL_BASE_TRASH_USD: Record<WasteDumpsterSize, number> = {
  '10ft': 594.04,
  '12ft': 564.0,
  '15ft': 534.0,
  '20ft': 503.25,
  '25ft': 540.8,
  '30ft': 578.36,
  '40ft': 620.0,
  '50ft': 665.0,
};

/** Zone mult on delivery. MyWM base delivery is flat; oilfield uses miles adder only. */
export const WM_ZONE_DELIVERY_MULT: Record<'in_town' | 'mixed' | 'oilfield_miles', number> = {
  in_town: 1.0,
  mixed: 1.0,
  oilfield_miles: 1.0,
};
export const WM_ZONE_HAUL_MULT: Record<'in_town' | 'mixed' | 'oilfield_miles', number> = {
  in_town: 1.0,
  mixed: 1.0,
  oilfield_miles: 1.0,
};

export function wmHaulUsd(
  size: WasteDumpsterSize | string,
  wasteStream?: WmWasteStream | string | null,
): number {
  const s = normalizeWmDumpsterSize(size);
  const stream = wmWasteStreamMeta(wasteStream);
  const table = WM_HAUL_USD_BY_STREAM[stream.id] || WM_HAUL_USD_BY_STREAM.trash;
  if (table[s] != null) return Number(table[s]);
  // Fallback: nearest known trash cell (Hobbs 30 yd anchor)
  return WM_HAUL_USD_BY_STREAM.trash[s] ?? 578.36;
}

/** Included tons for quote + settlement — always 1 free ton (policy). */
export function wmTonsIncluded(
  wasteStream?: WmWasteStream | string | null,
  size?: WasteDumpsterSize | string | null,
): number {
  const stream = wmWasteStreamMeta(wasteStream);
  const s = normalizeWmDumpsterSize(size || '30ft');
  const bySize = WM_TONS_INCLUDED_BY_STREAM_SIZE[stream.id];
  if (bySize && bySize[s] != null) return Number(bySize[s]);
  // Cap / default: never advertise more free tons than policy allows.
  return Math.min(Number(stream.tonsIncluded) || WM_INCLUDED_TONS_FREE, WM_INCLUDED_TONS_FREE);
}

export function normalizeWmDumpsterSize(size?: WasteDumpsterSize | string | null): WasteDumpsterSize {
  const raw = String(size || '').trim().toLowerCase();
  // Accept "10yd" / "10" aliases from older clients or notes
  const aliases: Record<string, WasteDumpsterSize> = {
    '10ft': '10ft',
    '10yd': '10ft',
    '10': '10ft',
    '12ft': '12ft',
    '12yd': '12ft',
    '12': '12ft',
    '15ft': '15ft',
    '15yd': '15ft',
    '15': '15ft',
    '20ft': '20ft',
    '20yd': '20ft',
    '20': '20ft',
    '25ft': '25ft',
    '25yd': '25ft',
    '25': '25ft',
    '30ft': '30ft',
    '30yd': '30ft',
    '30': '30ft',
    '40ft': '40ft',
    '40yd': '40ft',
    '40': '40ft',
    '50ft': '50ft',
    '50yd': '50ft',
    '50': '50ft',
  };
  if (aliases[raw]) return aliases[raw];
  // Tolerate labels like "30 YD", "30-yard", "30 ft construction"
  const match = raw.match(/\b(10|12|15|20|25|30|40|50)\b/);
  if (match && aliases[match[1]]) return aliases[match[1]];
  return '30ft';
}

/** Round USD money to cents (avoids $2612.2799999999997 in pay UI). */
export function wmRoundUsd(amount: number): number {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/** Display helper — always two decimal places. */
export function wmFormatUsd(amount: number, opts?: { dollarsOnly?: boolean }): string {
  const rounded = wmRoundUsd(amount);
  if (opts?.dollarsOnly) return `$${Math.round(rounded)}`;
  return `$${rounded.toFixed(2)}`;
}

export function wmDumpsterYardsLabel(size: WasteDumpsterSize | string): string {
  const n = normalizeWmDumpsterSize(size);
  return `${n.replace('ft', '')} yd`;
}

/**
 * Customer charge for a rental window — bottom-up estimate total.
 * Prefer wmEstimateQuote when you need line items.
 */
export function wmRentalPriceUsd(
  days: number,
  size: WasteDumpsterSize | string = '30ft',
  serviceKind?: WmServiceKind | string | null,
  opts?: {
    productLine?: WmProductLine | string | null;
    wasteStream?: WmWasteStream | string | null;
    oneWayMiles?: number | null;
    expectedTons?: number | null;
    pricingMode?: WmPricingMode | string | null;
    rateCard?: Partial<WmRateCard> | null;
  },
): number {
  const d = Math.round(Number(days));
  if (!Number.isFinite(d) || d < 1 || d > 90) {
    throw new Error('Rental days must be 1–90');
  }
  return wmEstimateQuote({
    days: d,
    size,
    serviceKind,
    productLine: opts?.productLine,
    wasteStream: opts?.wasteStream,
    oneWayMiles: opts?.oneWayMiles,
    expectedTons: opts?.expectedTons,
    pricingMode: opts?.pricingMode,
    rateCard: opts?.rateCard,
  }).totalUsd;
}

export function wmRentalPriceCents(
  days: number,
  size: WasteDumpsterSize | string = '30ft',
  serviceKind?: WmServiceKind | string | null,
  opts?: {
    productLine?: WmProductLine | string | null;
    wasteStream?: WmWasteStream | string | null;
    oneWayMiles?: number | null;
    expectedTons?: number | null;
    pricingMode?: WmPricingMode | string | null;
    rateCard?: Partial<WmRateCard> | null;
  },
): number {
  return Math.round(wmRentalPriceUsd(days, size, serviceKind, opts) * 100);
}

export function wmRentalPackageLabel(
  days: number,
  size: WasteDumpsterSize | string = '30ft',
  opts?: {
    productLine?: WmProductLine | string | null;
    wasteStream?: WmWasteStream | string | null;
    serviceKind?: WmServiceKind | string | null;
    pricingMode?: WmPricingMode | string | null;
    rateCard?: Partial<WmRateCard> | null;
  },
): string {
  const q = wmEstimateQuote({
    days,
    size,
    productLine: opts?.productLine,
    wasteStream: opts?.wasteStream,
    serviceKind: opts?.serviceKind,
    pricingMode: opts?.pricingMode,
    rateCard: opts?.rateCard,
  });
  const sizeLabel = wmDumpsterYardsLabel(q.size);
  const stream = wmWasteStreamMeta(q.wasteStream);
  const line = wmProductLineMeta(q.productLine);
  const modeTag = q.pricingMode === 'daily' ? 'custom days' : 'flat rate';
  return (
    `${q.days}-day ${sizeLabel} · ${stream.shortCode} · ${line.label} · $${q.totalUsd}`
    + ` · up to ${q.tonsIncluded} tons · ${modeTag}`
  );
}

/** Lowest multi-day package price for a size (7-day residential trash package). */
export function wmStartingPriceUsd(size: WasteDumpsterSize | string = '30ft'): number {
  return wmEstimateQuote({
    days: 7,
    size,
    productLine: 'residential',
    wasteStream: 'trash',
    serviceKind: 'temporary',
    pricingMode: 'package',
  }).totalUsd;
}

/**
 * Transparent quote — dual modes (package | daily) with optional company rate card.
 * Haul + Delivery + usage (+ optional ton overage preview).
 */
export interface WmQuoteBreakdown {
  productLine: WmProductLine;
  wasteStream: WmWasteStream;
  size: WasteDumpsterSize;
  days: number;
  pricingMode: WmPricingMode;
  suggestedPricingMode: WmPricingMode;
  daysIncluded: number;
  dailyRateUsd: number;
  tonsIncluded: number;
  overagePerTonUsd: number;
  expectedTons: number | null;
  overageTons: number;
  overageUsd: number;
  haulUsd: number;
  deliveryUsd: number;
  /** One-way miles yard → drop (null when pin/address not set yet). */
  deliveryMilesOneWay: number | null;
  deliveryMilesBillable: number;
  addlUsageDays: number;
  addlUsageUsd: number;
  usageDays: number;
  usageUsd: number;
  zone: 'in_town' | 'mixed' | 'oilfield_miles';
  subtotalUsd: number;
  /** Amount to charge at checkout (haul + delivery + usage; overage often settled after weigh) */
  totalUsd: number;
  /** totalUsd + projected overage if expectedTons provided */
  totalWithOverageUsd: number;
  /** Flat 7-day package total used as fairness cap for short custom. */
  flatWeekUsd: number;
  /** True when custom short-stay fair pricing was applied (not raw daily×days). */
  fairCustomApplied: boolean;
  /** Same-day / tomorrow divert fee. 0 for standard lead times. */
  asapSurchargeUsd: number;
  notAllowedNote: string;
  disclaimer: string;
  lineItems: Array<{ label: string; amountUsd: number }>;
}

export function wmEstimateQuote(input: {
  days: number;
  size?: WasteDumpsterSize | string | null;
  productLine?: WmProductLine | string | null;
  wasteStream?: WmWasteStream | string | null;
  serviceKind?: WmServiceKind | string | null;
  pricingMode?: WmPricingMode | string | null;
  /** Company rate card overrides platform guide. */
  rateCard?: Partial<WmRateCard> | null;
  /** One-way road miles from hauler yard / landfill to drop pin (optional). */
  oneWayMiles?: number | null;
  /** Customer-estimated load weight in tons (optional overage preview). */
  expectedTons?: number | null;
  /** Hauler inventory profile — different contractors run different sizes. */
  haulerProfileId?: WmHaulerSizeProfileId | string | null;
  /** When the truck must roll — ASAP / tomorrow add a divert surcharge. */
  deliveryLead?: string | null;
}): WmQuoteBreakdown {
  const card = wmNormalizeRateCard(input.rateCard);
  const line = wmProductLineMeta(input.productLine);
  const stream = wmWasteStreamMeta(input.wasteStream || line.defaultWasteStream);
  const size = normalizeWmDumpsterSize(input.size || line.sizes[0]);
  const flatMinDays = Math.max(WM_FLAT_RATE_MIN_DAYS, card.daysIncluded);
  let days = Math.max(1, Math.min(90, Math.round(Number(input.days) || card.daysIncluded)));
  const suggestedPricingMode = wmSuggestedPricingMode(days, card);
  let pricingMode: WmPricingMode = input.pricingMode != null && String(input.pricingMode).trim() !== ''
    ? wmNormalizePricingMode(input.pricingMode)
    : suggestedPricingMode;
  if (pricingMode === 'daily' && !card.dailyEnabled) pricingMode = 'package';
  if (pricingMode === 'package' && !card.packageEnabled) pricingMode = 'daily';

  // Flat rate package: enforce min week so we never sell a "package" under 7 days.
  if (pricingMode === 'package' && days < flatMinDays) {
    days = flatMinDays;
  }

  const daysIncluded = pricingMode === 'package' ? Math.max(card.daysIncluded, flatMinDays) : 0;
  const dailyRateUsd = wmDailyRateUsd(size, card);
  const zone = wmServiceKindMeta(input.serviceKind || line.defaultServiceKind).haulProfile;

  let haulUsd = Math.round(wmHaulUsd(size, stream.id) * 100) / 100;
  haulUsd = Math.round(haulUsd * (WM_ZONE_HAUL_MULT[zone] ?? 1) * 100) / 100;

  // Site drop is included — do not bill deliveryBaseUsd / WM_DELIVERY_BASE_USD.
  // Only billable miles (yard → pin) and extra days add to haul.
  const deliveryBaseUsd = 0;

  const oneWayRaw = Number(input.oneWayMiles);
  const deliveryMilesOneWay =
    Number.isFinite(oneWayRaw) && oneWayRaw > 0
      ? Math.round(oneWayRaw * 10) / 10
      : null;
  const deliveryFreeMiles = wmResolveDeliveryFreeMiles(card);
  let deliveryMilesBillable = 0;
  let milesUsd = 0;
  if (deliveryMilesOneWay != null) {
    const miles = wmBillableMilesBreakdown(deliveryMilesOneWay, deliveryFreeMiles);
    deliveryMilesBillable = miles?.billable ?? 0;
    milesUsd = miles?.milesUsd ?? 0;
  }
  const deliveryUsd = Math.round((deliveryBaseUsd + milesUsd) * 100) / 100;

  // Flat week CORE = haul + base delivery only (miles always added after fair curve).
  // Hauler can override with their own week price for this size (inventory / competition).
  const weekOverride = card.weekPackageUsdBySize[size];
  let flatWeekCoreUsd = Math.round((haulUsd + deliveryBaseUsd) * 100) / 100;
  let usedHaulerWeekPrice = false;
  if (weekOverride != null && Number.isFinite(weekOverride) && weekOverride >= 50) {
    flatWeekCoreUsd = Math.round(Number(weekOverride) * 100) / 100;
    usedHaulerWeekPrice = true;
  }
  // Display / API field: week package customers see = core + full miles (trip to pad).
  const flatWeekUsd = Math.round((flatWeekCoreUsd + milesUsd) * 100) / 100;

  let addlUsageDays = 0;
  let addlUsageUsd = 0;
  let usageDays = 0;
  let usageUsd = 0;
  let fairCustomApplied = false;
  let totalUsd = 0;
  /** Fair short-stay rental line (core only, before miles). */
  let fairRentalUsd = 0;

  if (pricingMode === 'daily') {
    if (days <= flatMinDays) {
      // Custom short: fair curve on week CORE only — miles stay full trip cost.
      fairRentalUsd = wmFairCustomShortTotalUsd(flatWeekCoreUsd, days, flatMinDays);
      totalUsd = Math.round((fairRentalUsd + milesUsd) * 100) / 100;
      fairCustomApplied = true;
      usageDays = days;
      usageUsd = Math.max(
        0,
        Math.round((fairRentalUsd - flatWeekCoreUsd * WM_CUSTOM_SHORT_FLOOR_RATIO) * 100) / 100,
      );
    } else {
      // Custom longer than a week: flat week core + miles + add'l days.
      addlUsageDays = days - flatMinDays;
      addlUsageUsd = Math.round(addlUsageDays * card.addlDayUsd * 100) / 100;
      usageDays = addlUsageDays;
      usageUsd = addlUsageUsd;
      totalUsd = Math.round((flatWeekCoreUsd + milesUsd + addlUsageUsd) * 100) / 100;
    }
  } else {
    // Flat week: hauler week core when set, else haul + delivery base + miles + add'l days.
    addlUsageDays = Math.max(0, days - daysIncluded);
    addlUsageUsd = Math.round(addlUsageDays * card.addlDayUsd * 100) / 100;
    usageDays = addlUsageDays;
    usageUsd = addlUsageUsd;
    totalUsd = usedHaulerWeekPrice
      ? Math.round((flatWeekCoreUsd + milesUsd + addlUsageUsd) * 100) / 100
      : Math.round((haulUsd + deliveryUsd + usageUsd) * 100) / 100;
  }

  // Temporary sale when hauler has extra boxes / weekend special.
  if (card.promoPercentOff > 0 && totalUsd > 0) {
    totalUsd = Math.round(totalUsd * (1 - card.promoPercentOff / 100) * 100) / 100;
  }

  // Divert fee is not on sale — the truck has to leave now.
  const asapSurchargeUsd = wmDeliveryLeadSurchargeUsd(input.deliveryLead);
  if (asapSurchargeUsd > 0) {
    totalUsd = Math.round((totalUsd + asapSurchargeUsd) * 100) / 100;
  }

  const tonsIncluded = wmTonsIncluded(stream.id, size);
  const expectedTons =
    input.expectedTons != null && Number.isFinite(Number(input.expectedTons))
      ? Math.max(0, Number(input.expectedTons))
      : null;
  const overageTons =
    expectedTons != null ? Math.max(0, expectedTons - tonsIncluded) : 0;
  const overageUsd = Math.round(overageTons * stream.overagePerTonUsd * 100) / 100;

  const subtotalUsd = totalUsd;

  const milesLineLabel = wmFormatBillableMilesLineLabel(deliveryMilesOneWay, deliveryFreeMiles);

  const lineItems: Array<{ label: string; amountUsd: number }> = [];
  // Pre-promo total for display split when promo applied
  const prePromoTotal = card.promoPercentOff > 0
    ? Math.round((totalUsd / (1 - card.promoPercentOff / 100)) * 100) / 100
    : totalUsd;
  if (pricingMode === 'daily' && fairCustomApplied) {
    // Show rental curve separate from trip miles so day changes don't hide miles.
    const rentalLine = Math.round((fairRentalUsd || Math.max(0, totalUsd - milesUsd)) * 100) / 100;
    lineItems.push({
      label: `${days}-day rental`,
      amountUsd: card.promoPercentOff > 0 && milesUsd <= 0 ? prePromoTotal : rentalLine,
    });
    if (milesUsd > 0 || deliveryMilesOneWay != null) {
      lineItems.push({
        label: milesLineLabel,
        amountUsd: milesUsd,
      });
    }
  } else if (usedHaulerWeekPrice) {
    lineItems.push({
      label: `Hauler week price · ${size.replace('ft', ' yd')}`,
      amountUsd: flatWeekCoreUsd,
    });
    if (milesUsd > 0 || deliveryMilesOneWay != null) {
      lineItems.push({
        label: milesLineLabel,
        amountUsd: milesUsd,
      });
    }
    if (addlUsageUsd > 0) {
      lineItems.push({
        label: `Extra days (${addlUsageDays} × $${card.addlDayUsd.toFixed(0)})`,
        amountUsd: addlUsageUsd,
      });
    }
  } else {
    lineItems.push({ label: 'Haul', amountUsd: haulUsd });
    if (milesUsd > 0 || deliveryMilesOneWay != null) {
      lineItems.push({
        label: milesLineLabel,
        amountUsd: milesUsd,
      });
    }
    if (addlUsageUsd > 0) {
      lineItems.push({
        label: `Extra days after week (${addlUsageDays} × $${card.addlDayUsd.toFixed(0)})`,
        amountUsd: addlUsageUsd,
      });
    }
  }
  if (card.promoPercentOff > 0) {
    const savings = Math.round((prePromoTotal - totalUsd) * 100) / 100;
    lineItems.push({
      label: card.promoLabel
        ? `${card.promoLabel} (−${card.promoPercentOff}%)`
        : `Temporary sale (−${card.promoPercentOff}%)`,
      amountUsd: -savings,
    });
  }
  if (asapSurchargeUsd > 0) {
    const lead = WM_DELIVERY_WINDOWS.find((w) => w.id === input.deliveryLead);
    lineItems.push({
      label: lead?.id === 'asap' ? 'ASAP same-day delivery' : 'Tomorrow delivery',
      amountUsd: asapSurchargeUsd,
    });
  }
  if (overageUsd > 0) {
    lineItems.push({
      label: `Projected overage (${overageTons.toFixed(1)} t)`,
      amountUsd: overageUsd,
    });
  }

  const modeDisclaimer = pricingMode === 'daily'
    ? (fairCustomApplied
      ? `Short stay: priced under the $${flatWeekUsd.toFixed(0)} week rate (never more than a full week for ≤${flatMinDays} days). `
      : `Week rate + $${card.addlDayUsd.toFixed(0)}/day after. `)
    : `Week rate (min ${flatMinDays} days)${usedHaulerWeekPrice ? ' set by hauler' : ''} · $${card.addlDayUsd.toFixed(0)}/day after week. `;
  const promoDisclaimer = card.promoPercentOff > 0
    ? `Includes temporary ${card.promoPercentOff}% off${card.promoLabel ? ` (${card.promoLabel})` : ''}. `
    : '';

  return {
    productLine: line.id,
    wasteStream: stream.id,
    size,
    days,
    pricingMode,
    suggestedPricingMode,
    daysIncluded: pricingMode === 'package' ? daysIncluded : flatMinDays,
    dailyRateUsd,
    tonsIncluded,
    overagePerTonUsd: stream.overagePerTonUsd,
    expectedTons,
    overageTons,
    overageUsd,
    haulUsd,
    deliveryUsd,
    deliveryMilesOneWay,
    deliveryMilesBillable,
    addlUsageDays,
    addlUsageUsd,
    usageDays,
    usageUsd,
    zone,
    subtotalUsd,
    totalUsd,
    totalWithOverageUsd: Math.round((totalUsd + overageUsd) * 100) / 100,
    flatWeekUsd,
    fairCustomApplied,
    asapSurchargeUsd,
    notAllowedNote: stream.notAllowedNote,
    disclaimer:
      'Prices include Energy Surcharge. Local tax may apply. '
      + `$${stream.overagePerTonUsd.toFixed(2)}/add'l ton (${tonsIncluded} tons included). `
      + modeDisclaimer
      + promoDisclaimer
      + `Landfill closed ${WM_LANDFILL_CLOSED_LABEL}s — haul/removal shifts to next open day. `
      + 'Overage settles after weigh-in.',
    lineItems,
  };
}

/** Size catalog — Thompson Hauling & Transport: 30 yd + 50 yd only. */
export const WM_SIZE_CATALOG: Array<{
  size: WasteDumpsterSize;
  label: string;
  subtitle: string;
  bestFor: string;
  capacityHint: string;
  dims: string;
  priceNote: string;
}> = [
  // Full standard roll-off range — hauler rate cards narrow what each company stocks.
  {
    size: '10ft',
    label: '10 yd dumpster',
    subtitle: '13′ L × 8′ W × 3.5′ H',
    bestFor: 'Small cleanouts, single-room remodel, heavy material loads',
    capacityHint: '~3 pickup loads',
    dims: "13' × 8' × 3.5'",
    priceNote: 'Compact',
  },
  {
    size: '12ft',
    label: '12 yd dumpster',
    subtitle: '14′ L × 8′ W × 3.5′ H',
    bestFor: 'Small cleanouts with a little extra room',
    capacityHint: '~3–4 pickup loads',
    dims: "14' × 8' × 3.5'",
    priceNote: 'Compact',
  },
  {
    size: '15ft',
    label: '15 yd dumpster',
    subtitle: '16′ L × 8′ W × 4.5′ H',
    bestFor: 'Garage cleanouts, small roofing jobs',
    capacityHint: '~4 pickup loads',
    dims: "16' × 8' × 4.5'",
    priceNote: 'Compact plus',
  },
  {
    size: '20ft',
    label: '20 yd dumpster',
    subtitle: '22′ L × 8′ W × 4′ H',
    bestFor: 'Most common size — mid remodels, roofing, medium cleanouts',
    capacityHint: '~6 pickup loads',
    dims: "22' × 8' × 4'",
    priceNote: 'Most popular nationwide',
  },
  {
    size: '25ft',
    label: '25 yd dumpster',
    subtitle: '22′ L × 8′ W × 5′ H',
    bestFor: 'Larger remodels between 20 and 30 yd',
    capacityHint: '~7 pickup loads',
    dims: "22' × 8' × 5'",
    priceNote: 'In-between size',
  },
  {
    size: '30ft',
    label: '30 yd dumpster',
    subtitle: '18–20′ L × 8′ W × 6–6.5′ H',
    bestFor: 'Pad trash, light demo, tank battery cleanups',
    capacityHint: '~4–5 pickup loads · up to ~5,000 lb',
    dims: "18–20' × 8' × 6.5'",
    priceNote: 'Construction standard',
  },
  {
    size: '40ft',
    label: '40 yd dumpster',
    subtitle: '22′ L × 8′ W × 7′ H',
    bestFor: 'Large construction / commercial demo, bulky light debris',
    capacityHint: '~12 pickup loads',
    dims: "22' × 8' × 7'",
    priceNote: 'Large box',
  },
  {
    size: '50ft',
    label: '50 yd dumpster',
    subtitle: '24′ L × 8′ W × 8′ H',
    bestFor: 'Largest jobs, multi-day pads, long rentals',
    capacityHint: '~7–8 pickup loads',
    dims: "24' × 8' × 8'",
    priceNote: 'Largest box',
  },
];

/**
 * Secondary multi-select load notes (what is going in the box).
 * Primary billing stream remains WmWasteStream (Trash / CD / CR / Heavy).
 * Dirt, caliche, and fill stay excluded from standard boxes.
 */
export const WM_WASTE_TYPES = [
  // Residential
  'Household / garage trash',
  'Residential remodel / construction trash',
  'Roofing tear-off (shingles)',
  'Kitchen / bath demo (non-concrete)',
  'Yard cleanout (where allowed — not restricted states)',
  // Commercial / business
  'Office / retail trash',
  'Warehouse / shop floor trash',
  'Commercial remodel / tenant finish',
  'Store fixtures & packaging',
  'Cardboard (recycling stream only)',
  // Construction / contractor
  'General construction debris (CD)',
  'Wood / lumber / pallets',
  'Drywall / sheetrock',
  'Metal scrap (non-hazmat)',
  'Insulation / wrap (non-asbestos)',
  // Oilfield
  'Pad / lease trash (non-hazmat)',
  'Tank battery / production pad cleanout',
  'Rig / frac crew camp trash',
  'Mixed oilfield waste (non-hazardous only)',
  'Pipe / poly / containment (empty, dry)',
  'Empty drums / totes (triple-rinsed)',
  'Plastic / poly / liner',
  'Furniture / bulky',
  'Filters / rags (non-haz)',
  // Catch-all
  'Other (describe in notes)',
] as const;

/** Suggested load chips by product line + optional oilfield site kind. */
export function wmWasteTypesForContext(opts?: {
  productLine?: WmProductLine | string | null;
  serviceKind?: WmServiceKind | string | null;
}): string[] {
  const line = String(opts?.productLine || 'construction');
  const oilfield = String(opts?.serviceKind || '') === 'oilfield_job';
  const all = [...WM_WASTE_TYPES];

  if (oilfield) {
    return all.filter((t) =>
      /oilfield|pad|lease|tank battery|rig|frac|camp|pipe|poly|construction debris|wood|metal|other/i.test(t),
    );
  }
  if (line === 'residential') {
    return all.filter((t) =>
      /household|residential|roofing|kitchen|yard|wood|drywall|construction debris|other/i.test(t),
    );
  }
  if (line === 'commercial') {
    return all.filter((t) =>
      /office|warehouse|shop|commercial|cardboard|fixtures|packaging|construction debris|wood|drywall|metal|other/i.test(t),
    );
  }
  // Construction product line — broad contractor set + oilfield tags available
  return all.filter((t) =>
    /construction|wood|drywall|metal|insulation|roofing|remodel|commercial|oilfield|pad|lease|tank|rig|pipe|cardboard|drum|plastic|poly|furniture|filter|other/i.test(t),
  );
}

/**
 * Materials not allowed in temporary roll-off dumpsters.
 * Aligned with WM.com temporary dumpster FAQ (general guidelines;
 * landfill rules can still vary by location — always show disclaimer).
 * @see https://www.wm.com/us/en/support/faqs/products-and-services/what-materials-are-not-allowed-in-my-dumpster
 */
export const WM_PROHIBITED_CATEGORIES: Array<{
  id: string;
  label: string;
  items: string[];
}> = [
  {
    id: 'hazmat',
    label: 'Hazardous & chemicals',
    items: [
      'Hazardous waste (any)',
      'Chemical products',
      'Herbicides and pesticides',
      'Solvents',
      'Flammable liquids',
      'Asbestos',
      'PCB materials',
      'Radioactive material',
      'Aerosol cans',
    ],
  },
  {
    id: 'oils',
    label: 'Oils & auto fluids',
    items: [
      'Motor oil',
      'Antifreeze',
      'Transmission oil',
      'Lubricating / hydraulic oil',
      'Contaminated oils (mixed with solvents, gasoline, etc.)',
      'Oil filters',
      'Petroleum-contaminated soil / lead paint chips',
    ],
  },
  {
    id: 'gases',
    label: 'Gases & pressure vessels',
    items: ['Propane tanks', 'Pressurized cylinders'],
  },
  {
    id: 'electronics',
    label: 'Electronics & appliances',
    items: [
      'Televisions',
      'Computers & monitors',
      'Microwaves',
      'Appliances (incl. freon units: fridges, ACs, freezers)',
      'Batteries',
    ],
  },
  {
    id: 'auto',
    label: 'Auto & rubber',
    items: ['Tires', 'Auto parts'],
  },
  {
    id: 'liquids',
    label: 'Liquids & containers',
    items: [
      'All free liquids',
      'Barrels / drums',
      'Paint with free liquid (dried latex residue only may be OK)',
    ],
  },
  {
    id: 'soil',
    label: 'Soil & fill (standard boxes)',
    items: [
      'Dirt, soil, fill dirt',
      'Caliche (any amount)',
      'Yard waste (restricted in some states)',
    ],
  },
  {
    id: 'medical',
    label: 'Medical & biological',
    items: ['Medical / biohazard waste', 'Animals'],
  },
  {
    id: 'other',
    label: 'Other restricted',
    items: [
      'Fluorescent tubes / bulbs',
      'Railroad ties',
      'Industrial waste (unless pre-approved)',
      'Mattresses (restricted in some states, e.g. MA)',
    ],
  },
  {
    id: 'large_box',
    label: 'Often banned in larger (30+ yd) standard boxes',
    items: [
      'Concrete',
      'Bricks / rock',
      'Heavy demolition material (unless on a pre-approved heavy-material stream)',
    ],
  },
];

/** Flat list for terms, notes, and simple chips. */
export const WM_PROHIBITED_CATEGORY_IDS: string[] = WM_PROHIBITED_CATEGORIES.map((c) => c.id);

export const WM_WASTE_NOT_ACCEPTED: string[] = WM_PROHIBITED_CATEGORIES.flatMap((c) => c.items);

export const WM_PROHIBITED_DISCLAIMER =
  'These are general roll-off rules (aligned with industry / WM temporary dumpster guidance). '
  + 'Landfill and hauler rules can vary by location. Prohibited loads may be refused, re-routed, or billed as a special trip. '
  + 'Dirt, concrete, brick, and rock only go in a pre-approved heavy-material stream when the hauler allows.';

/** Short headline items for collapsed UI (always visible). */
export const WM_PROHIBITED_HEADLINE = [
  'No batteries or auto parts',
  'No tires',
  'No drums, liquids, or chemicals',
  'No appliances or electronics',
  'No hazardous waste',
  'No asbestos',
  'No dirt',
  'No PCB',
] as const;

export const WM_TERMS_ACK_TEXT =
  'I confirm this is a physical dumpster rental (delivery & pickup). I will not load prohibited materials '
  + '(hazardous waste, liquids, oils, batteries, tires, appliances/electronics, asbestos, medical waste, '
  + 'propane tanks, free paint/solvents, dirt/caliche in standard boxes, or concrete/brick unless on a '
  + 'pre-approved heavy stream). I understand landfill rules may vary and refused loads can incur extra fees.';

/**
 * When the box must roll — not a time-of-day picker.
 * ASAP / tomorrow cost extra because the hauler has to divert a truck now.
 */
export const WM_ASAP_SURCHARGE_USD = 125;
export const WM_TOMORROW_SURCHARGE_USD = 50;

export const WM_DELIVERY_WINDOWS = [
  { id: 'asap', label: 'ASAP · same day', surchargeUsd: WM_ASAP_SURCHARGE_USD },
  { id: 'tomorrow', label: 'Tomorrow', surchargeUsd: WM_TOMORROW_SURCHARGE_USD },
  { id: 'within_3_days', label: 'Within 3 days', surchargeUsd: 0 },
  { id: 'this_week', label: 'This week', surchargeUsd: 0 },
] as const;

export type WmDeliveryWindow = (typeof WM_DELIVERY_WINDOWS)[number]['id'];

export function wmDeliveryLeadSurchargeUsd(lead?: string | null): number {
  const hit = WM_DELIVERY_WINDOWS.find((w) => w.id === lead);
  return hit ? hit.surchargeUsd : 0;
}

export function wmDeliveryDateForLead(lead: string, now: Date = new Date()): string {
  const today = wmFormatIsoDate(now);
  if (lead === 'asap') return today;
  if (lead === 'tomorrow') {
    const d = wmParseLocalNoon(today) || now;
    d.setDate(d.getDate() + 1);
    return wmFormatIsoDate(d);
  }
  if (lead === 'within_3_days') {
    const d = wmParseLocalNoon(today) || now;
    d.setDate(d.getDate() + 3);
    return wmFormatIsoDate(d);
  }
  const d = wmParseLocalNoon(today) || now;
  d.setDate(d.getDate() + 7);
  return wmFormatIsoDate(d);
}

export function wmLeadFromDeliveryDate(iso?: string | null, now: Date = new Date()): WmDeliveryWindow {
  const start = wmParseLocalNoon(iso);
  if (!start) return 'within_3_days';
  const today = wmParseLocalNoon(wmFormatIsoDate(now));
  if (!today) return 'within_3_days';
  const delta = Math.round((start.getTime() - today.getTime()) / 86_400_000);
  if (delta <= 0) return 'asap';
  if (delta === 1) return 'tomorrow';
  if (delta <= 3) return 'within_3_days';
  return 'this_week';
}

/** Same-day / next-day truck-roll fee for a calendar date (0 if later). */
export function wmDeliveryDaySurchargeUsd(iso?: string | null, now: Date = new Date()): number {
  if (!String(iso || '').trim()) return 0;
  return wmDeliveryLeadSurchargeUsd(wmLeadFromDeliveryDate(iso, now));
}

export function wmDeliveryDaySurchargeLabel(iso?: string | null, now: Date = new Date()): string {
  const usd = wmDeliveryDaySurchargeUsd(iso, now);
  return usd > 0 ? `+$${usd}` : '';
}

export const WM_INCLUDED_BULLETS = [
  'Residential, commercial, and construction roll-offs',
  'Delivery to your pin + pickup when you request it',
  'Haul + delivery + 10 usage days included (MyWM-style)',
  'Tons included by size/stream (e.g. trash 10yd 4t · 20yd 2t · 30yd 3t)',
  'Serial + GPS proof on drop · job group stays in the loop',
] as const;

/**
 * Customer FAQ — aligned with public MyWM roll-off FAQ topics (usage days,
 * tons / overage after weigh, max truck load, permits, yard waste, exchange vs
 * permanent remove, ETA / en-route) plus ZiaWellNav app mechanics.
 * Pricing estimates are catalog guides; hauler may re-quote.
 */
export const WM_CUSTOMER_FAQ: Array<{ q: string; a: string }> = [
  {
    q: 'What dumpsters do you offer?',
    a: 'Residential home, business, or oilfield. Sizes depend on what you load — the quote shows available options.',
  },
  {
    q: 'What is included in the price?',
    a: 'Delivery, pickup, and the tons listed on your quote. Extra weight after the scale is a separate charge.',
  },
  {
    q: 'What are “usage days”?',
    a: 'How long the box stays on site. Flat rate = about a week. Custom = the exact days you pick. Extra days after that cost more.',
  },
  {
    q: 'What is the haul charge vs overage tons?',
    a: 'Your pay covers delivery + the included tons. Weight over that is billed after the landfill scale ticket.',
  },
  {
    q: 'How much is 1 ton? (rough guide)',
    a: 'Rough guide only: concrete ~½ yd/ton; heavy loads hit the limit faster than bulky trash. The scale is final.',
  },
  {
    q: 'Is there a maximum weight?',
    a: 'Plan for about 10 tons max — rock and brick fill weight fast even if the box looks open.',
  },
  {
    q: 'What can I put in the dumpster?',
    a: 'Match the waste type you ordered (trash, construction, cardboard, etc.). Check “Not allowed” on the order form.',
  },
  {
    q: 'What materials are not allowed?',
    a: 'No hazmat, liquids, batteries, tires, appliances, asbestos, medical waste, or free paint. Wrong loads can cost extra.',
  },
  {
    q: 'Is yard waste allowed?',
    a: 'Usually no. Ask the hauler before loading brush or green waste.',
  },
  {
    q: 'Do I need a permit?',
    a: 'Sometimes — HOAs and cities vary. Check local rules before delivery.',
  },
  {
    q: 'Why are size prices different (not always “bigger = more”)?',
    a: 'Price follows load type and market, not just box size. Trust the quote card for your selection.',
  },
  {
    q: 'How do I get the dumpster removed or emptied?',
    a: 'Tap Request pickup on your rental or map pin. For a swap (empty box back), say “exchange” when you request.',
  },
  {
    q: 'What if the box is full before my removal date?',
    a: 'Request pickup anytime — you do not have to wait for the end date.',
  },
  {
    q: 'When will the driver arrive?',
    a: 'Pick a date and window. You get a notice when the driver is en route.',
  },
  {
    q: 'In-town vs oilfield pricing?',
    a: 'In-town uses base delivery. Oilfield often adds miles — enter one-way distance if you know it.',
  },
  {
    q: 'Do I need an API number?',
    a: 'Only oilfield/lease jobs without a street address. Homes and businesses just use the street + pin.',
  },
  {
    q: 'Where should I place the pin?',
    a: 'Pin where the box sits — firm ground, truck access, doors clear. Wrong pin delays delivery.',
  },
  {
    q: 'Is payment secure?',
    a: 'Yes — Stripe for physical dumpster service. Extra tons after scale may be a second charge.',
  },
];

/** Structured customer fields packed into request notes for drivers / ops */
export interface WmCustomerRequestDetails {
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  preferredDeliveryDate?: string;
  deliveryWindow?: string;
  wasteTypes?: string[];
  accessNotes?: string;
  /** Digit PIN for a locked site gate. Shown to hauler only after accept. */
  lockedGateCode?: string;
  poNumber?: string;
  /** temporary | business_long_term | oilfield_job */
  serviceKind?: string;
  /** residential | commercial | construction */
  productLine?: string;
  /** trash | construction_debris | cardboard_recycling */
  wasteStream?: string;
  /** Well API # for oilfield / lease drops */
  apiNumber?: string;
}

export function buildWmCustomerNotesBlock(details: WmCustomerRequestDetails): string {
  const lines: string[] = ['—— Customer service details ——'];
  if (details.productLine) {
    lines.push(`Product line: ${wmProductLineMeta(details.productLine).label}`);
  }
  if (details.wasteStream) {
    const s = wmWasteStreamMeta(details.wasteStream);
    lines.push(`Waste stream: ${s.label} (${s.shortCode})`);
  }
  if (details.serviceKind) {
    const meta = wmServiceKindMeta(details.serviceKind);
    lines.push(`Site use: ${meta.label}`);
  }
  if (details.apiNumber) lines.push(`API #: ${details.apiNumber}`);
  if (details.contactName) lines.push(`Contact: ${details.contactName}`);
  if (details.contactPhone) lines.push(`Phone: ${details.contactPhone}`);
  if (details.contactEmail) lines.push(`Email: ${details.contactEmail}`);
  if (details.preferredDeliveryDate) lines.push(`Preferred delivery date: ${details.preferredDeliveryDate}`);
  if (details.deliveryWindow) lines.push(`Delivery window: ${details.deliveryWindow}`);
  if (details.wasteTypes?.length) lines.push(`Waste types: ${details.wasteTypes.join(', ')}`);
  if (details.poNumber) lines.push(`PO / ticket: ${details.poNumber}`);
  if (details.accessNotes) lines.push(`Access: ${details.accessNotes}`);
  const gateLine = wmFormatLockedGateNoteLine(details.lockedGateCode);
  if (gateLine) lines.push(gateLine);
  return lines.length > 1 ? lines.join('\n') : '';
}

export function parseWmCustomerNotes(notes: string | null | undefined): Partial<WmCustomerRequestDetails> & { freeform?: string } {
  if (!notes) return {};
  const out: Partial<WmCustomerRequestDetails> & { freeform?: string } = {};
  const free: string[] = [];
  for (const raw of notes.split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('——')) continue;
    const m = line.match(/^(Product line|Waste stream|Site use|Service|API #|Contact|Phone|Email|Preferred delivery date|Delivery window|Waste types|PO \/ ticket|Access|Locked gate):\s*(.+)$/i);
    if (!m) {
      free.push(line);
      continue;
    }
    const key = m[1].toLowerCase();
    const val = m[2].trim();
    if (key === 'product line') out.productLine = val;
    else if (key === 'waste stream') out.wasteStream = val;
    else if (key === 'site use' || key === 'service') out.serviceKind = val;
    else if (key === 'api #') out.apiNumber = val;
    else if (key === 'contact') out.contactName = val;
    else if (key === 'phone') out.contactPhone = val;
    else if (key === 'email') out.contactEmail = val;
    else if (key.startsWith('preferred')) out.preferredDeliveryDate = val;
    else if (key.startsWith('delivery')) out.deliveryWindow = val;
    else if (key.startsWith('waste')) out.wasteTypes = val.split(',').map((s) => s.trim()).filter(Boolean);
    else if (key.startsWith('po')) out.poNumber = val;
    else if (key === 'access') out.accessNotes = val;
    else if (key === 'locked gate') {
      const digits = wmNormalizeLockedGateCode(val);
      if (digits) out.lockedGateCode = digits;
    }
  }
  if (free.length) out.freeform = free.join('\n');
  return out;
}

/**
 * One short customer status line — same vocabulary as pills / next-hint.
 * Never a tutorial paragraph (Grade A+ sparse).
 */
export function customerStatusHint(status: WasteJobStatus | string): string {
  switch (status) {
    case 'requested':
      return 'Waiting for a driver.';
    case 'assigned':
      return 'Driver assigned.';
    case 'en_route_dropoff':
      return 'On the way.';
    case 'dropped_off':
      return 'On your pad — request pickup when full.';
    case 'pickup_requested':
      return 'Pickup scheduled.';
    case 'en_route_pickup':
      return 'Pickup en route.';
    case 'picked_up':
      return 'Complete.';
    case 'cancelled':
      return 'Cancelled.';
    default:
      return '';
  }
}

export function isDefaultWmHaulerId(crewId: string | null | undefined): boolean {
  return !crewId || crewId === DEFAULT_WM_HAULER_ID || crewId === '';
}

/** Customer picked a contractor-book company that is not a registered Zia crew. */
export function isBookContractorCrewId(crewId: string | null | undefined): boolean {
  return Boolean(crewId && String(crewId).startsWith('book:'));
}

/** Stable crew/slot id for a book pick (uses real crew when linked). */
export function bookContractorSlotId(entry: {
  id: string;
  crewId?: string | null;
}): string {
  const real = String(entry.crewId || '').trim();
  if (real && !isDefaultWmHaulerId(real) && !isBookContractorCrewId(real)) return real;
  return `book:${String(entry.id || '').trim()}`;
}

export const WM_STATUS_META: Record<string, { label: string; color: string; step: number; customerLabel?: string }> = {
  requested: { label: 'Requested', customerLabel: 'Order received', color: '#38bdf8', step: 0 },
  assigned: { label: 'Assigned', customerLabel: 'Driver assigned', color: '#a78bfa', step: 1 },
  en_route_dropoff: { label: 'En route drop-off', customerLabel: 'On the way', color: '#818cf8', step: 2 },
  dropped_off: { label: 'On site', customerLabel: 'On your pad', color: '#22c55e', step: 3 },
  pickup_requested: { label: 'Pickup requested', customerLabel: 'Pickup scheduled', color: '#f59e0b', step: 4 },
  en_route_pickup: { label: 'En route pickup', customerLabel: 'Pickup en route', color: '#fb923c', step: 5 },
  picked_up: { label: 'Picked up', customerLabel: 'Complete', color: '#94a3b8', step: 6 },
  cancelled: { label: 'Cancelled', customerLabel: 'Cancelled', color: '#ef4444', step: -1 },
};

export function wmCustomerStatusLabel(status: string): string {
  const meta = WM_STATUS_META[status];
  return meta?.customerLabel || meta?.label || status;
}

/**
 * Customer-facing state for nav pills / request strip.
 * Includes payment when not paid yet so unpaid orders don't look "live" with hauler.
 */
export function wmCustomerRequestStateLabel(job: {
  status?: string | null;
  payment_status?: string | null;
} | null | undefined): string {
  if (!job) return 'No request';
  const pay = String(job.payment_status || '').toLowerCase();
  if (pay === 'pending') return 'Checkout open';
  if (pay === 'failed') return 'Pay failed';
  if (pay && pay !== 'paid' && pay !== 'refunded') return 'Needs payment';
  return wmCustomerStatusLabel(String(job.status || 'requested'));
}

/** Status color for customer request pills. */
export function wmCustomerRequestStateColor(job: {
  status?: string | null;
  payment_status?: string | null;
} | null | undefined): string {
  if (!job) return '#94a3b8';
  const pay = String(job.payment_status || '').toLowerCase();
  if (pay === 'pending' || pay === 'failed' || (pay && pay !== 'paid' && pay !== 'refunded')) {
    return '#f59e0b';
  }
  return WM_STATUS_META[String(job.status || 'requested')]?.color || '#38bdf8';
}

/** Lifecycle step index 0–6 for mini progress on customer request pills (−1 cancelled / unpaid). */
export function wmCustomerRequestStep(job: {
  status?: string | null;
  payment_status?: string | null;
} | null | undefined): number {
  if (!job) return -1;
  const pay = String(job.payment_status || '').toLowerCase();
  if (pay && pay !== 'paid' && pay !== 'refunded') return -1;
  const step = WM_STATUS_META[String(job.status || 'requested')]?.step;
  return typeof step === 'number' ? step : 0;
}

/**
 * Next action under status on customer cards — one line, same words as status.
 * Features unchanged; copy only.
 */
export function wmCustomerRequestNextHint(job: {
  status?: string | null;
  payment_status?: string | null;
} | null | undefined): string {
  if (!job) return '';
  const pay = String(job.payment_status || '').toLowerCase();
  if (pay === 'pending') return 'Finish checkout';
  if (pay === 'failed') return 'Try pay again';
  if (pay && pay !== 'paid' && pay !== 'refunded') return 'Pay to lock delivery';
  // Paid path: reuse status vocabulary (no second dialect)
  return customerStatusHint(String(job.status || ''));
}

/** Digits-only mobile for guest orders (US 10 only; optional leading 1). */
export function normalizeWmGuestPhone(raw?: string | null): string | null {
  // Shared US NANP rules — reject 12–20 digit pastes and junk area codes.
  // Lazy import avoided: keep this file free of circular deps with utils.
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  if (digits[0] === '0' || digits[0] === '1') return null;
  if (digits[3] === '0' || digits[3] === '1') return null;
  return digits;
}

export function wmGuestUserIdFromPhone(phoneDigits: string): string {
  return `guest_ph_${phoneDigits}`;
}

export function wmIsGuestUserId(userId?: string | null): boolean {
  return String(userId || '').startsWith('guest_ph_');
}

/**
 * Resolve customer identity for WM API calls.
 * Prefer Telegram / linked iOS; otherwise guest identity keyed by mobile phone
 * (or a stable device-local guest id only when phone not yet known).
 */
export function resolveWmCustomerIdentity(opts?: {
  guestPhone?: string | null;
  contactName?: string | null;
}): { userId: string; username: string; guest: boolean; guestPhone: string | null } {
  let userId = hostWebApp.getUserId?.() || '';
  let username = (window as any)[legacyHostName()]?.WebApp?.initDataUnsafe?.user?.username || '';

  const linked = getNativeIosLinkedUser();
  if (linked?.userId) {
    userId = linked.userId;
    if (linked.username) username = linked.username;
  }

  // Real host identities (Telegram numeric ids, linked company users) win.
  if (userId && !wmIsGuestUserId(userId) && !String(userId).startsWith('dev-')) {
    return {
      userId,
      username: username || 'customer',
      guest: false,
      guestPhone: normalizeWmGuestPhone(opts?.guestPhone),
    };
  }

  const phone = normalizeWmGuestPhone(opts?.guestPhone);
  if (phone) {
    const guestId = wmGuestUserIdFromPhone(phone);
    try {
      localStorage.setItem('wm-guest-user-id', guestId);
      localStorage.setItem('wm-contact-phone', phone);
    } catch { /* private mode */ }
    return {
      userId: guestId,
      username: String(opts?.contactName || localStorage.getItem('wm-contact-name') || 'Guest').slice(0, 80) || 'Guest',
      guest: true,
      guestPhone: phone,
    };
  }

  // Fallback: stable device guest (checkout will still require phone on server)
  try {
    const stored = localStorage.getItem('wm-guest-user-id');
    if (stored && wmIsGuestUserId(stored)) {
      return { userId: stored, username: 'Guest', guest: true, guestPhone: null };
    }
  } catch { /* ignore */ }

  const fallback = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? `guest_dev_${crypto.randomUUID().slice(0, 12)}`
    : `guest_dev_${Date.now().toString(36)}`;
  try { localStorage.setItem('wm-guest-user-id', fallback); } catch { /* ignore */ }
  return { userId: fallback, username: 'Guest', guest: true, guestPhone: null };
}

function getHeaders(opts?: { guestPhone?: string | null; contactName?: string | null }): HeadersInit {
  const id = resolveWmCustomerIdentity(opts);

  return {
    'Content-Type': 'application/json',
    ...driverGpsTestHeaders(),
    'X-Telegram-User-Id': id.userId,
    'X-Telegram-Username': id.username || '',
    'X-Web-User-Id': id.userId,
    'X-Web-Username': id.username || '',
    [LEGACY_USER_ID]: id.userId,
    [LEGACY_USERNAME]: id.username || '',
    [LEGACY_INIT_DATA]: hostWebApp.getInitData?.() || '',
    ...(signedInBidUserId() ? bidBoardAuthHeaders() : {}),
  };
}

/** Plain-language fallback when the API does not return an error string. Never "HTTP 400". */
function wmFriendlyStatusMessage(status: number): string {
  if (status === 401 || status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'That item was not found.';
  if (status === 409) return 'That conflicts with something already on file.';
  if (status === 429) return 'Too many requests. Wait a moment and try again.';
  if (status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return 'Something went wrong. Try again.';
}

/**
 * Strip developer/HTTP codes from user-visible copy so banners stay readable.
 * e.g. "Serial #5 is already in your yard — (HTTP 409) — Already listed…"
 *   → "Serial #5 is already in your yard — Already listed…"
 */
export function wmUserFacingErrorMessage(raw: string | null | undefined): string {
  return String(raw || '')
    .replace(/\s*[—–-]\s*\(?\s*HTTP\s*\d{3}\s*\)?/gi, '')
    .replace(/\(\s*HTTP\s*\d{3}\s*\)/gi, '')
    .replace(/\bHTTP\s*\d{3}\b/gi, '')
    .replace(/\s*[—–-]\s*[—–-]/g, ' — ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s*[—–-]\s*|\s*[—–-]\s*$/g, '')
    .trim()
    || 'Something went wrong. Try again.';
}

async function requestJson<T>(
  path: string,
  init?: RequestInit,
  identity?: { guestPhone?: string | null; contactName?: string | null },
): Promise<T> {
  const method = (init?.method || 'GET').toUpperCase();
  const headers = new Headers(getHeaders(identity));
  new Headers(init?.headers).forEach((value, name) => headers.set(name, value));
  const { response: res, data } = await fetchWmJson(`${API_BASE}${path}`, { ...init, method, headers });
  if (!res.ok) {
    const bodyErr = (data as { error?: string; message?: string; hint?: string }).error
      || (data as { message?: string }).message;
    const hint = (data as { hint?: string }).hint;
    // End users get the API message + hint only — never "(HTTP 409)" style codes.
    const parts = [
      bodyErr || wmFriendlyStatusMessage(res.status),
      hint || null,
    ].filter(Boolean);
    throw new Error(wmUserFacingErrorMessage(parts.join(' — ')));
  }
  return data as T;
}

/** Compress a serial-plate photo to a reasonable data URL for API storage. */
/** Compress serial-plate photo; enforces a hard size ceiling for API storage. */
export async function compressSerialPhotoToDataUrl(file: File, maxEdge = 1024, quality = 0.65): Promise<string> {
  const MAX_CHARS = 400_000; // ~300KB binary-ish ceiling after base64
  const bitmap = await createImageBitmap(file).catch(async () => {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = url;
      });
      return img as unknown as ImageBitmap;
    } finally {
      URL.revokeObjectURL(url);
    }
  });

  const w = 'width' in bitmap ? (bitmap as ImageBitmap).width : (bitmap as HTMLImageElement).width;
  const h = 'height' in bitmap ? (bitmap as ImageBitmap).height : (bitmap as HTMLImageElement).height;
  let edge = maxEdge;
  let q = quality;
  let dataUrl = '';
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const scale = Math.min(1, edge / Math.max(w, h, 1));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.drawImage(bitmap as CanvasImageSource, 0, 0, cw, ch);
    dataUrl = canvas.toDataURL('image/jpeg', q);
    if (dataUrl.length <= MAX_CHARS) break;
    edge = Math.round(edge * 0.75);
    q = Math.max(0.45, q - 0.1);
  }
  if ('close' in bitmap && typeof (bitmap as ImageBitmap).close === 'function') {
    (bitmap as ImageBitmap).close();
  }
  if (!dataUrl || dataUrl.length > MAX_CHARS) {
    throw new Error('Serial photo still too large after compression — retake closer');
  }
  return dataUrl;
}

/** Google Maps driving directions to a dumpster drop / request pin. */
export function mapsUrl(lat: number, lng: number, _label = 'Dumpster'): string {
  const dest = `${lat},${lng}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;
}

export function formatGps(lat: number | null | undefined, lng: number | null | undefined): string {
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return '—';
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return '—';
  // Null Island / default zeros are not a field pin (same rule as bot Telegram/email)
  if (Math.abs(lat) < 1e-5 && Math.abs(lng) < 1e-5) return '—';
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

/** Haversine distance in meters. */
export function distanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * True GPS great-circle miles between two pins (no road fudge).
 * Returns null if coords are missing / invalid / Null Island.
 */
export function wmGpsMilesBetween(
  a: { lat: number; lng: number } | null | undefined,
  b: { lat: number; lng: number } | null | undefined,
): number | null {
  if (
    !a || !b
    || !Number.isFinite(Number(a.lat)) || !Number.isFinite(Number(a.lng))
    || !Number.isFinite(Number(b.lat)) || !Number.isFinite(Number(b.lng))
  ) {
    return null;
  }
  const lat1 = Number(a.lat);
  const lng1 = Number(a.lng);
  const lat2 = Number(b.lat);
  const lng2 = Number(b.lng);
  if (Math.abs(lat1) > 90 || Math.abs(lat2) > 90 || Math.abs(lng1) > 180 || Math.abs(lng2) > 180) {
    return null;
  }
  if (Math.abs(lat1) < 1e-5 && Math.abs(lng1) < 1e-5) return null;
  if (Math.abs(lat2) < 1e-5 && Math.abs(lng2) < 1e-5) return null;
  const mi = distanceMeters(lat1, lng1, lat2, lng2) / 1609.344;
  if (!Number.isFinite(mi) || mi < 0) return null;
  if (mi < 0.05) return 0;
  // One decimal under 100 mi; whole miles for long hauls (chip-friendly, never negative).
  return mi < 100 ? Math.round(mi * 10) / 10 : Math.round(mi);
}

/**
 * Estimated road miles between two GPS points (straight-line × rural road factor).
 * Destinations come from Google address (residential/commercial) or oilfield API/pad pin.
 */
export function wmMilesBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
  opts?: { roadFactor?: number },
): number {
  const straightMi = wmGpsMilesBetween(a, b);
  if (straightMi == null) return 0;
  const factor = opts?.roadFactor != null && Number.isFinite(opts.roadFactor)
    ? Math.max(1, Number(opts.roadFactor))
    : WM_ROAD_MILES_FACTOR;
  return Math.round(straightMi * factor * 10) / 10;
}

/**
 * One-way delivery miles from hauler yard → drop destination.
 * Pass exact coords from: Google Places street pin, commercial geocode, or API/facility/pad pin.
 * Prefer {@link wmDriverMilesToJob} when live GPS is available (remaining miles to pad).
 */
export function wmDeliveryMilesFromHaulerYard(
  dest: { lat: number; lng: number } | null | undefined,
  yard: { lat: number; lng: number } | Partial<WmRateCard> | null | undefined = DEFAULT_WM_HAULER_YARD,
): number | null {
  // Accept a rate card or explicit {lat,lng} — companies with a saved depot
  // must not inherit the Hobbs default when quoting customer miles.
  const resolved = yard && typeof yard === 'object' && 'yardLat' in yard
    ? wmYardForRateCard(yard as Partial<WmRateCard>)
    : (yard && isUsableGps(Number((yard as { lat?: number }).lat), Number((yard as { lng?: number }).lng))
      ? { lat: Number((yard as { lat: number }).lat), lng: Number((yard as { lng: number }).lng) }
      : wmYardForRateCard(null));
  const miles = wmGpsMilesBetween(
    { lat: resolved.lat, lng: resolved.lng },
    dest ? { lat: Number(dest.lat), lng: Number(dest.lng) } : null,
  );
  if (miles == null || miles <= 0) return null;
  // Apply rural road factor only for yard→pad planning estimates (not live GPS remaining).
  return Math.round(miles * WM_ROAD_MILES_FACTOR * 10) / 10;
}

/** ~35 mph oilfield/local mix; always ≥ 1 minute, never negative. */
export function wmEtaMinutesFromMiles(miles: number | null | undefined, mph = 35): number | null {
  if (miles == null || !Number.isFinite(miles) || miles < 0) return null;
  if (miles === 0) return 1;
  const speed = Number.isFinite(mph) && mph > 5 ? mph : 35;
  return Math.max(1, Math.round((miles / speed) * 60));
}

/** Chip-safe ETA: `26 min` or `2h 4m` (no ambiguous ~ / - glyphs). */
export function wmFormatEtaLabel(minutes: number | null | undefined): string | null {
  if (minutes == null || !Number.isFinite(minutes)) return null;
  const m = Math.max(1, Math.round(Math.abs(minutes)));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r > 0 ? `${h}h ${r}m` : `${h}h`;
}

/**
 * Driver miles chip from live GPS (preferred) or yard fallback.
 * Live GPS uses true haversine miles to the job pin (dropoff when set, else request).
 */
export function wmDriverMilesToJob(
  job: {
    status?: string | null;
    requested_lat?: number | null;
    requested_lng?: number | null;
    dropoff_lat?: number | null;
    dropoff_lng?: number | null;
    driver_lat?: number | null;
    driver_lng?: number | null;
    driver_location_at?: string | null;
  },
  liveGps?: { lat: number; lng: number } | null,
  opts?: { maxDriverFixAgeMs?: number; nowMs?: number },
): {
  miles: number;
  /** remaining = live/truck GPS → pin; from_yard = depot estimate */
  source: 'live_gps' | 'truck_gps' | 'from_yard';
  dest: { lat: number; lng: number };
} | null {
  const st = String(job.status || '');
  const pickupLeg = st === 'pickup_requested' || st === 'en_route_pickup' || st === 'picked_up';
  const dest = pickupLeg && job.dropoff_lat != null && job.dropoff_lng != null
    ? { lat: Number(job.dropoff_lat), lng: Number(job.dropoff_lng) }
    : (job.dropoff_lat != null && job.dropoff_lng != null
      ? { lat: Number(job.dropoff_lat), lng: Number(job.dropoff_lng) }
      : (job.requested_lat != null && job.requested_lng != null
        ? { lat: Number(job.requested_lat), lng: Number(job.requested_lng) }
        : null));
  if (!dest || !Number.isFinite(dest.lat) || !Number.isFinite(dest.lng)) return null;

  if (liveGps && Number.isFinite(liveGps.lat) && Number.isFinite(liveGps.lng)) {
    const mi = wmGpsMilesBetween(liveGps, dest);
    if (mi != null) return { miles: mi, source: 'live_gps', dest };
  }

  const maxAge = opts?.maxDriverFixAgeMs ?? 5 * 60 * 1000;
  const now = opts?.nowMs ?? Date.now();
  if (
    job.driver_lat != null && job.driver_lng != null
    && Number.isFinite(Number(job.driver_lat)) && Number.isFinite(Number(job.driver_lng))
  ) {
    let fresh = true;
    if (job.driver_location_at) {
      const t = Date.parse(String(job.driver_location_at).includes('T')
        ? String(job.driver_location_at)
        : `${job.driver_location_at}Z`);
      if (Number.isFinite(t) && now - t > maxAge) fresh = false;
    }
    if (fresh) {
      const mi = wmGpsMilesBetween(
        { lat: Number(job.driver_lat), lng: Number(job.driver_lng) },
        dest,
      );
      if (mi != null) return { miles: mi, source: 'truck_gps', dest };
    }
  }

  const fromYard = wmDeliveryMilesFromHaulerYard(dest);
  if (fromYard == null) return null;
  return { miles: fromYard, source: 'from_yard', dest };
}

/** Chip label: `12.4 mi left · 26 min` or `72 mi from yard · 2h 4m` (never negative). */
export function wmFormatMilesEtaChip(
  miles: number | null | undefined,
  opts?: { source?: 'live_gps' | 'truck_gps' | 'from_yard'; etaMinutes?: number | null },
): string | null {
  if (miles == null || !Number.isFinite(miles) || miles < 0) return null;
  const mi = miles === 0 ? 0 : (miles < 100 ? Math.round(miles * 10) / 10 : Math.round(miles));
  const miLabel = Number.isInteger(mi) ? `${mi}` : mi.toFixed(1);
  const eta = opts?.etaMinutes != null
    ? opts.etaMinutes
    : wmEtaMinutesFromMiles(miles);
  const etaLabel = wmFormatEtaLabel(eta);
  const prefix = opts?.source === 'from_yard'
    ? `${miLabel} mi from yard`
    : opts?.source === 'live_gps' || opts?.source === 'truck_gps'
      ? `${miLabel} mi left`
      : `${miLabel} mi`;
  if (!etaLabel) return prefix;
  // Middle-dot separator; no tilde (looks like minus on small chips).
  return `${prefix} · ${etaLabel}`;
}

/** Resolve best delivery destination pin from cart line / wizard fields. */
export function wmDeliveryDestinationCoords(input: {
  dropPin?: { lat: number; lng: number } | null;
  facilityLat?: number | null;
  facilityLng?: number | null;
}): { lat: number; lng: number } | null {
  if (
    input.dropPin
    && Number.isFinite(input.dropPin.lat)
    && Number.isFinite(input.dropPin.lng)
  ) {
    return { lat: input.dropPin.lat, lng: input.dropPin.lng };
  }
  if (
    input.facilityLat != null
    && input.facilityLng != null
    && Number.isFinite(Number(input.facilityLat))
    && Number.isFinite(Number(input.facilityLng))
  ) {
    return { lat: Number(input.facilityLat), lng: Number(input.facilityLng) };
  }
  return null;
}

/**
 * True when the dumpster pin is meaningfully away from the API/facility well GPS.
 * Oilfield jobs often reference an API while the box sits on a street yard / different pad.
 */
export function wmDropPinDiffersFromFacility(
  dropPin: { lat: number; lng: number } | null | undefined,
  facilityLat?: number | null,
  facilityLng?: number | null,
  thresholdM: number = WM_DROP_GEOFENCE_RADIUS_M,
): boolean {
  if (
    !dropPin
    || !Number.isFinite(dropPin.lat)
    || !Number.isFinite(dropPin.lng)
    || facilityLat == null
    || facilityLng == null
    || !Number.isFinite(Number(facilityLat))
    || !Number.isFinite(Number(facilityLng))
  ) {
    return false;
  }
  return distanceMeters(dropPin.lat, dropPin.lng, Number(facilityLat), Number(facilityLng)) > thresholdM;
}

/**
 * When selecting a new API well, reseed the drop pin only if the user has not already
 * moved it (e.g. Santa Fe St yard while API is a Jal well ~40 mi away).
 * Glue distance: still following the previous facility → follow the new well.
 */
export function wmShouldReseedDropPinFromFacility(input: {
  prevPin?: { lat: number; lng: number } | null;
  prevFacilityLat?: number | null;
  prevFacilityLng?: number | null;
  nextFacilityLat: number;
  nextFacilityLng: number;
  glueM?: number;
}): boolean {
  const glue = input.glueM != null && Number.isFinite(input.glueM) ? Math.max(1, input.glueM) : 50;
  const prev = input.prevPin;
  if (
    !prev
    || !Number.isFinite(prev.lat)
    || !Number.isFinite(prev.lng)
  ) {
    return true;
  }
  const nextNear = distanceMeters(
    prev.lat,
    prev.lng,
    input.nextFacilityLat,
    input.nextFacilityLng,
  ) <= glue;
  if (nextNear) return true;
  if (
    input.prevFacilityLat != null
    && input.prevFacilityLng != null
    && Number.isFinite(Number(input.prevFacilityLat))
    && Number.isFinite(Number(input.prevFacilityLng))
  ) {
    const gluedToOld = distanceMeters(
      prev.lat,
      prev.lng,
      Number(input.prevFacilityLat),
      Number(input.prevFacilityLng),
    ) <= glue;
    return gluedToOld;
  }
  // Pin exists, no prior facility — treat as user-set drop, keep it.
  return false;
}

/**
 * Resolve stored serial / placement photo path for <img src>.
 * On Capacitor iOS, relative `/api/...` img URLs hit capacitor://localhost and break —
 * rewrite to the native API origin (same as fetch routing).
 */
export function serialPhotoSrc(url: string | null | undefined): string | null {
  if (!url) return null;
  const raw = String(url).trim();
  if (!raw) return null;
  if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    // Still rewrite accidental localhost / capacitor hosts on device
    return resolveNativeApiUrl(raw);
  }
  // photo id only, or absolute app path
  const path = raw.startsWith('/')
    ? raw
    : `/api/waste-management/serial-photo/${encodeURIComponent(raw.replace(/^\/+/, ''))}`;
  return resolveNativeApiUrl(path);
}

/**
 * Authenticated proof-photo loader. Serial/pickup/landfill photos are private
 * (server checks job membership), and identity travels in headers — which a
 * bare <img src> can never send. Fetch with auth → blob object URL, cached.
 * data:/blob: URLs pass straight through (local captures before upload).
 */
const wmPhotoBlobCache = new Map<string, string>();
const wmPhotoBlobPending = new Map<string, Promise<string | null>>();

export async function wmAuthedPhotoObjectUrl(url: string | null | undefined): Promise<string | null> {
  const src = serialPhotoSrc(url);
  if (!src) return null;
  if (src.startsWith('data:') || src.startsWith('blob:')) return src;
  const cached = wmPhotoBlobCache.get(src);
  if (cached) return cached;
  const pending = wmPhotoBlobPending.get(src);
  if (pending) return pending;
  const task = (async (): Promise<string | null> => {
    try {
      const res = await fetch(src, { headers: getHeaders() });
      if (!res.ok) return null;
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      wmPhotoBlobCache.set(src, objectUrl);
      return objectUrl;
    } catch {
      return null;
    } finally {
      wmPhotoBlobPending.delete(src);
    }
  })();
  wmPhotoBlobPending.set(src, task);
  return task;
}

/** Required drop-off photo kinds — customer + company see how the box sits (incl. empty interior). */
export const WM_DROPOFF_PHOTO_KINDS = [
  {
    id: 'full_box' as const,
    label: 'Full box on pad',
    short: '1 Full box',
    hint: 'Show the whole dumpster where it sits on the pad.',
  },
  {
    id: 'doors_face' as const,
    label: 'Doors / open side',
    short: '2 Doors',
    hint: 'Show the door/open end matching the customer door-faces direction.',
  },
  {
    id: 'interior_empty' as const,
    label: 'Interior · empty',
    short: '3 Empty inside',
    hint: 'Open the doors and photograph the empty interior so the customer can confirm the box was dropped empty.',
  },
  {
    id: 'access_path' as const,
    label: 'Truck access path',
    short: '4 Access',
    hint: 'Show the approach the truck used / how to get back for pickup.',
  },
  {
    id: 'serial_plate' as const,
    label: 'Serial plate',
    short: '5 Serial',
    hint: 'Close-up of the dumpster serial plate.',
  },
];

/** Count of required drop placement photos (keeps UI copy in sync). */
export const WM_DROPOFF_PHOTO_COUNT = WM_DROPOFF_PHOTO_KINDS.length;

export type WmDropoffPhotoKind = (typeof WM_DROPOFF_PHOTO_KINDS)[number]['id'];

export type WmDropoffPhotoEntry = {
  kind: WmDropoffPhotoKind | string;
  url: string;
  photoId?: string | null;
};

export function wmDropoffPhotoLabel(kind: string): string {
  const hit = WM_DROPOFF_PHOTO_KINDS.find((k) => k.id === kind);
  return hit?.label || String(kind || 'Photo').replace(/_/g, ' ');
}

function parseProofPhotoJson(
  raw: string | null | undefined,
  fallbackKinds: readonly { id: string }[],
): WmDropoffPhotoEntry[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed) && parsed.length) {
      return parsed
        .map((item, i) => {
          if (item && typeof item === 'object' && (item as { url?: string }).url) {
            const o = item as { kind?: string; url: string; photoId?: string };
            return {
              kind: (o.kind || fallbackKinds[i]?.id || `photo_${i}`) as string,
              url: String(o.url),
              photoId: o.photoId ? String(o.photoId) : null,
            };
          }
          return {
            kind: fallbackKinds[i]?.id || `photo_${i}`,
            url: String(item),
            photoId: null,
          };
        })
        .filter((e) => e.url);
    }
  } catch { /* ignore */ }
  return [];
}

/** Parse dropoff_proof_photo_urls JSON; falls back to single serial plate URL. */
export function wmDropoffProofPhotos(
  job: Pick<WasteDumpsterJob, 'dropoff_proof_photo_urls' | 'dropoff_serial_photo_url'> | null | undefined,
): WmDropoffPhotoEntry[] {
  if (!job) return [];
  const fromJson = parseProofPhotoJson(job.dropoff_proof_photo_urls, WM_DROPOFF_PHOTO_KINDS);
  if (fromJson.length) return fromJson;
  if (job.dropoff_serial_photo_url) {
    return [{ kind: 'serial_plate', url: job.dropoff_serial_photo_url, photoId: null }];
  }
  return [];
}

/** Required pickup photos: 4 exterior + 1 interior. */
export const WM_PICKUP_PHOTO_KINDS = [
  {
    id: 'exterior_front' as const,
    label: 'Exterior · front',
    short: '1 Front',
    hint: 'Outside front of the dumpster before you leave the pad.',
  },
  {
    id: 'exterior_rear' as const,
    label: 'Exterior · rear',
    short: '2 Rear',
    hint: 'Outside rear / opposite end of the box.',
  },
  {
    id: 'exterior_side_a' as const,
    label: 'Exterior · side A',
    short: '3 Side A',
    hint: 'One long side of the dumpster (exterior).',
  },
  {
    id: 'exterior_side_b' as const,
    label: 'Exterior · side B',
    short: '4 Side B',
    hint: 'The other long side (exterior).',
  },
  {
    id: 'interior' as const,
    label: 'Interior',
    short: '5 Interior',
    hint: 'Inside the empty (or as-left) dumpster — company record of load/condition.',
  },
];

export type WmPickupPhotoKind = (typeof WM_PICKUP_PHOTO_KINDS)[number]['id'];

export function wmPickupPhotoLabel(kind: string): string {
  const hit = WM_PICKUP_PHOTO_KINDS.find((k) => k.id === kind);
  if (hit) return hit.label;
  return wmDropoffPhotoLabel(kind);
}

/** Parse pickup_proof_photo_urls; falls back to single pickup_photo_url. */
export function wmPickupProofPhotos(
  job: Pick<WasteDumpsterJob, 'pickup_proof_photo_urls' | 'pickup_photo_url'> | null | undefined,
): WmDropoffPhotoEntry[] {
  if (!job) return [];
  const fromJson = parseProofPhotoJson(job.pickup_proof_photo_urls, WM_PICKUP_PHOTO_KINDS);
  if (fromJson.length) return fromJson;
  if (job.pickup_photo_url) {
    return [{ kind: 'interior', url: job.pickup_photo_url, photoId: null }];
  }
  return [];
}

/**
 * Roll-off dumpster (30/50 yd) + trailer equipment issues drivers can flag.
 * Grouped for field UI: box body, doors, trailer systems.
 */
export const WM_EQUIPMENT_ISSUE_GROUPS = [
  {
    id: 'box',
    label: 'Dumpster box',
    items: [
      { id: 'big_dent', label: 'Big dent' },
      { id: 'multiple_dents', label: 'Multiple dents' },
      { id: 'floor_hole', label: 'Floor hole / rust-through' },
      { id: 'side_crack', label: 'Side crack / split' },
      { id: 'weld_failure', label: 'Weld failure' },
      { id: 'paint_rust', label: 'Heavy rust / paint fail' },
      { id: 'hook_rail_damage', label: 'Hook / rail damage' },
      { id: 'floor_bulge', label: 'Floor bulge / bowed' },
    ],
  },
  {
    id: 'doors',
    label: 'Doors & gates',
    items: [
      { id: 'door_issue', label: 'Door issue' },
      { id: 'door_wont_latch', label: "Door won't latch" },
      { id: 'door_wont_open', label: "Door won't open" },
      { id: 'door_hinge_broken', label: 'Hinge broken / bent' },
      { id: 'door_seal_missing', label: 'Door seal missing / torn' },
      { id: 'door_chain_cable', label: 'Door chain / cable issue' },
    ],
  },
  {
    id: 'trailer',
    label: 'Roll-off trailer',
    items: [
      { id: 'hydraulics_leak', label: 'Leaking hydraulics' },
      { id: 'hydraulics_weak', label: 'Hydraulics weak / slow' },
      { id: 'hydraulics_dead', label: 'Hydraulics not working' },
      { id: 'pump_not_working', label: 'Pump does not work' },
      { id: 'battery_dead', label: 'Battery dead' },
      { id: 'battery_low', label: 'Battery low / weak' },
      { id: 'tires_low', label: 'Tires low / worn' },
      { id: 'tires_flat', label: 'Flat tire(s)' },
      { id: 'axle_issue', label: 'Axle issue' },
      { id: 'brakes_issue', label: 'Brakes issue' },
      { id: 'lights_issue', label: 'Lights / wiring' },
      { id: 'winch_cable', label: 'Winch / cable issue' },
      { id: 'trailer_frame', label: 'Trailer frame damage' },
      { id: 'landing_gear', label: 'Landing gear issue' },
    ],
  },
  {
    id: 'other',
    label: 'Other',
    items: [
      { id: 'contamination_load', label: 'Load contamination concern' },
      { id: 'overweight_feel', label: 'Feels overweight / hard pull' },
      { id: 'unsafe_site', label: 'Unsafe site condition' },
      { id: 'other', label: 'Other (see notes)' },
    ],
  },
] as const;

export type WmEquipmentIssueTagId =
  (typeof WM_EQUIPMENT_ISSUE_GROUPS)[number]['items'][number]['id'];

export type WmEquipmentIssueSeverity = 'low' | 'medium' | 'high' | 'out_of_service';

export type WmEquipmentIssueReport = {
  id: string;
  phase: 'dropoff' | 'pickup' | 'mid_run' | string;
  at: string;
  byUserId?: string | null;
  byUsername?: string | null;
  tags: string[];
  notes?: string | null;
  severity: WmEquipmentIssueSeverity | string;
  /** One proof photo per selected chip — the chip is the caption. */
  photos?: Array<{ tag: string; url: string }>;
};

export const WM_EQUIPMENT_ISSUE_SEVERITIES: Array<{
  id: WmEquipmentIssueSeverity;
  label: string;
}> = [
  { id: 'low', label: 'Low — note only' },
  { id: 'medium', label: 'Medium — schedule shop' },
  { id: 'high', label: 'High — fix soon' },
  { id: 'out_of_service', label: 'Out of service — do not reuse' },
];

const EQUIPMENT_TAG_LABELS: Record<string, string> = Object.fromEntries(
  WM_EQUIPMENT_ISSUE_GROUPS.flatMap((g) => g.items.map((i) => [i.id, i.label])),
);

export function wmEquipmentIssueLabel(tagId: string): string {
  return EQUIPMENT_TAG_LABELS[tagId] || String(tagId || '').replace(/_/g, ' ');
}

/** Whole days since an ISO timestamp; null when missing/invalid. */
export function wmDaysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

function wmDaysAgoText(days: number): string {
  return days <= 0 ? 'today' : `${days}d ago`;
}

/**
 * "How many days" chips for every WM card:
 *  - dropped & still out  → "On site Nd" (the rental-days number everyone watches)
 *  - picked up            → "Dropped Nd ago" + "Picked up Nd ago"
 */
export function wmJobDayChips(
  job: Pick<WasteDumpsterJob, 'dropoff_at' | 'pickup_at'> | null | undefined,
): Array<{ id: 'onsite' | 'dropped' | 'picked'; label: string }> {
  if (!job) return [];
  const dropDays = wmDaysSince(job.dropoff_at);
  const pickDays = wmDaysSince(job.pickup_at);
  if (dropDays != null && pickDays == null) {
    return [{ id: 'onsite', label: dropDays <= 0 ? 'On site today' : `On site ${dropDays}d` }];
  }
  const chips: Array<{ id: 'onsite' | 'dropped' | 'picked'; label: string }> = [];
  if (dropDays != null) chips.push({ id: 'dropped', label: `Dropped ${wmDaysAgoText(dropDays)}` });
  if (pickDays != null) chips.push({ id: 'picked', label: `Picked up ${wmDaysAgoText(pickDays)}` });
  return chips;
}

export function wmParseEquipmentIssues(
  job: Pick<WasteDumpsterJob, 'equipment_issues_json'> | null | undefined,
): WmEquipmentIssueReport[] {
  if (!job?.equipment_issues_json) return [];
  try {
    const raw = typeof job.equipment_issues_json === 'string'
      ? JSON.parse(job.equipment_issues_json)
      : job.equipment_issues_json;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((r) => r && typeof r === 'object')
      .map((r: any) => ({
        id: String(r.id || ''),
        phase: String(r.phase || 'mid_run'),
        at: String(r.at || ''),
        byUserId: r.byUserId ?? r.by_user_id ?? null,
        byUsername: r.byUsername ?? r.by_username ?? null,
        tags: Array.isArray(r.tags) ? r.tags.map(String) : [],
        notes: r.notes ? String(r.notes) : null,
        severity: String(r.severity || 'medium'),
        photos: Array.isArray(r.photos)
          ? r.photos
            .filter((ph: any) => ph && ph.url)
            .map((ph: any) => ({ tag: String(ph.tag || 'other'), url: String(ph.url) }))
          : [],
      }));
  } catch {
    return [];
  }
}

/** All equipment-issue proof photos on a job — chip tag rides along as the caption. */
export function wmEquipmentIssuePhotos(
  job: Pick<WasteDumpsterJob, 'equipment_issues_json'> | null | undefined,
): Array<{ kind: string; url: string }> {
  const out: Array<{ kind: string; url: string }> = [];
  for (const report of wmParseEquipmentIssues(job)) {
    for (const ph of report.photos || []) {
      if (ph.url) out.push({ kind: ph.tag || 'other', url: ph.url });
    }
  }
  return out;
}

/** Latest open issues summary for company cards. */
export function wmLatestEquipmentIssueSummary(
  job: Pick<WasteDumpsterJob, 'equipment_issues_json'> | null | undefined,
): string | null {
  const list = wmParseEquipmentIssues(job);
  if (!list.length) return null;
  const last = list[list.length - 1];
  const tags = last.tags.map(wmEquipmentIssueLabel).slice(0, 3).join(', ');
  const sev = last.severity === 'out_of_service' ? 'OOS' : last.severity;
  return [sev, tags, last.notes?.slice(0, 60)].filter(Boolean).join(' · ') || null;
}

/**
 * Short next-action copy. Drivers rely on the Next step button + automation —
 * keep this minimal (no multi-sentence tutorials).
 */
export function nextActionHint(
  status: WasteJobStatus | string,
  role: WasteJobRole | null,
): string | null {
  const isDriver = role === 'worker' || role === 'crew_lead';
  switch (status) {
    case 'requested':
      if (role === 'customer') return 'Waiting for a crew to accept.';
      if (isDriver || role === null) return 'Select a job and accept.';
      return null;
    case 'assigned':
      // Driver CTA is the Next step button — no extra essay.
      if (isDriver) return null;
      return 'Waiting for driver.';
    case 'en_route_dropoff':
      if (isDriver) return null;
      return 'Driver en route.';
    case 'dropped_off':
      if (role === 'customer' || role === 'crew_lead') return 'On site — request pickup when full.';
      return null;
    case 'pickup_requested':
      if (isDriver) return null;
      return 'Waiting for pickup.';
    case 'en_route_pickup':
      if (isDriver) return null;
      return 'Driver en route for pickup.';
    case 'picked_up':
      return 'Complete.';
    case 'cancelled':
      return 'Cancelled.';
    default:
      return null;
  }
}

/** Client cart line site fields used for multi-dumpster validation. */
export interface WmCartSiteFields {
  siteName?: string | null;
  apiNumber?: string | null;
  serviceAddress?: string | null;
  dropPin?: { lat: number; lng: number } | null;
  size?: WasteDumpsterSize | string | null;
  orientation?: string | null;
}

/** Street / USPS-style address (not a pad or API label). */
export function wmLooksLikeStreetAddress(value?: string | null): boolean {
  const s = String(value || '').trim();
  if (!s) return false;
  if (wmIsFullServiceAddress(s)) return true;
  // House number + street token, or city/state zip
  if (/\d{1,6}\s+[A-Za-z]/.test(s)) return true;
  if (/,\s*[A-Z]{2}\s*\d{5}/i.test(s)) return true;
  if (/\b(street|st\.?|ave\.?|avenue|road|rd\.?|blvd|drive|dr\.?|lane|ln\.?|way|hwy|usa)\b/i.test(s)) {
    return true;
  }
  return false;
}

/**
 * Well / lease / API-style labels that must NOT appear on residential dumpster cards.
 * e.g. "GREEN FEDERAL #001", "API 30-025-…", "FEDERAL COM"
 */
export function wmLooksLikeWellOrApiLabel(value?: string | null): boolean {
  const s = String(value || '').trim();
  if (!s) return false;
  if (/\bAPI\s*#?\s*[\d-]/i.test(s)) return true;
  if (/\b\d{2}-\d{3}-\d{5}\b/.test(s)) return true;
  if (/\b(FEDERAL|STATE COM|COM\b|UNIT\b|#\d{3,}|WELL\b|LEASE\b|TANK BATTERY|SWD)\b/i.test(s)) {
    // Don't flag ordinary street words that happen to include "unit"
    if (wmLooksLikeStreetAddress(s) && !/\bAPI\b|\d{2}-\d{3}-\d{5}|FEDERAL/i.test(s)) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * True when siteName is empty or is just the API number written again
 * ("API 30-025-34615", "30-025-34615"). Used so jobsite cards never print
 * the same API on two stacked lines.
 */
export function wmSiteNameIsApiOnly(
  siteName?: string | null,
  apiNumber?: string | null,
): boolean {
  const site = String(siteName || '').trim();
  if (!site) return true;
  const api = String(apiNumber || '').trim();
  const siteDigits = site.replace(/\D/g, '');
  const apiDigits = api.replace(/\D/g, '');
  if (apiDigits.length >= 5 && siteDigits === apiDigits) return true;
  if (/^API\s*#?\s*[\d-]+$/i.test(site)) return true;
  return false;
}

export function wmJobIsOilfieldDelivery(job: {
  service_kind?: string | null;
  product_line?: string | null;
  requested_notes?: string | null;
}): boolean {
  if (String(job.service_kind || '') === 'oilfield_job') return true;
  const notes = String(job.requested_notes || '');
  const parsed = parseWmCustomerNotes(notes);
  if (parsed.apiNumber && parsed.apiNumber.replace(/\D/g, '').length >= 3) return true;
  if (String(parsed.serviceKind || '').toLowerCase().includes('oilfield')) return true;
  if (/Customer type:\s*Contractor/i.test(notes)) return true;
  if (/Site use:\s*.*oilfield/i.test(notes)) return true;
  return false;
}

/** Pull "Service address: …" from requested_notes (checkout always writes this for street sites). */
export function wmExtractServiceAddressFromNotes(notes?: string | null): string | null {
  const m = String(notes || '').match(/^Service address:\s*(.+)$/im);
  const addr = m ? m[1].trim() : '';
  return addr || null;
}

/**
 * Customer dumpster footprint / pad outline from `highlight_path_json`.
 * Drivers use this on the Drop form map so they place the box where the
 * customer marked it (rectangle or freehand path).
 */
export function wmParseHighlightPath(
  raw: string | Array<{ lat: number; lng: number }> | null | undefined,
): Array<{ lat: number; lng: number }> {
  if (!raw) return [];
  let points: unknown = raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      points = JSON.parse(s);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(points)) return [];
  const out: Array<{ lat: number; lng: number }> = [];
  for (const p of points) {
    if (!p || typeof p !== 'object') continue;
    const lat = Number((p as { lat?: unknown }).lat);
    const lng = Number((p as { lng?: unknown }).lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) out.push({ lat, lng });
  }
  return out;
}

/** Customer request pin + optional footprint for driver placement maps. */
export function wmCustomerDropPinMap(
  job: Pick<
    WasteDumpsterJob,
    | 'requested_lat'
    | 'requested_lng'
    | 'dropoff_lat'
    | 'dropoff_lng'
    | 'highlight_path_json'
    | 'requested_orientation'
    | 'dropoff_orientation'
    | 'dumpster_size'
  > | null | undefined,
): {
  lat: number;
  lng: number;
  path: Array<{ lat: number; lng: number }>;
  orientation: string | null;
  dumpsterSize: string | null;
} | null {
  if (!job) return null;
  const lat = job.requested_lat ?? job.dropoff_lat;
  const lng = job.requested_lng ?? job.dropoff_lng;
  if (lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return null;
  }
  return {
    lat: Number(lat),
    lng: Number(lng),
    path: wmParseHighlightPath(job.highlight_path_json),
    orientation: job.requested_orientation || job.dropoff_orientation || null,
    dumpsterSize: job.dumpster_size != null ? String(job.dumpster_size) : null,
  };
}

function wmDedupeDotParts(value: string): string {
  const parts = String(value || '').split(/\s*·\s*/).map((s) => s.trim()).filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    const key = part.replace(/\s+/g, ' ').toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(part);
  }
  return out.join(' · ');
}

/** Placeholder labels like "Site" — never show these as the drop location. */
function wmIsGenericSiteLabel(value?: string | null): boolean {
  const t = String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  return !t || t === 'site' || t === 'drop site' || t === 'your pad' || t === 'dumpster' || t === 'pad' || t === 'work site';
}

function wmFacilityNameFromNotes(notes?: string | null): string | null {
  const m = String(notes || '').match(/^Facility:\s*(.+)$/im)
    || String(notes || '').match(/^Facility\s+(.+)$/im);
  const name = m?.[1]?.trim() || '';
  if (!name || wmIsGenericSiteLabel(name) || /^API\b/i.test(name)) return null;
  return name;
}

/**
 * Driver / company drop card location line.
 * Residential / commercial: physical street address ONLY (never nearest well / API name).
 * Oilfield: facility/lease name + API # when requested.
 */
export function wmDriverJobLocationLabel(job: {
  site_name?: string | null;
  customer_org?: string | null;
  service_kind?: string | null;
  product_line?: string | null;
  requested_notes?: string | null;
}): string {
  const oilfield = wmJobIsOilfieldDelivery(job);
  const notes = parseWmCustomerNotes(job.requested_notes);
  const addrFromNotes = wmExtractServiceAddressFromNotes(job.requested_notes);
  const rawSite = String(job.site_name || '').trim();
  const parts = rawSite.split(/\s*·\s*/).map((p) => p.trim()).filter(Boolean)
    .filter((p) => !wmIsGenericSiteLabel(p));

  if (oilfield) {
    const api = notes.apiNumber?.trim()
      || (rawSite.match(/\bAPI\s*#?\s*([\d-]+)/i)?.[1])
      || null;
    const pad = parts.find((p) => !wmLooksLikeStreetAddress(p) && !/^API\b/i.test(p))
      || wmFacilityNameFromNotes(job.requested_notes)
      || parts.find((p) => !wmLooksLikeStreetAddress(p))
      || (!wmLooksLikeStreetAddress(rawSite) && !wmIsGenericSiteLabel(rawSite) ? rawSite : '');
    const join = (value: string) => wmDedupeDotParts(value);
    if (pad && api) {
      if (wmSiteNameIsApiOnly(pad, api) || pad.includes(api) || /\bAPI\s*#?\s*[\d-]+/i.test(pad)) {
        return join(pad);
      }
      return join(`${pad} · API ${api}`);
    }
    if (api) return `API ${api}`;
    if (pad) return join(pad);
    if (addrFromNotes) return addrFromNotes;
    return '';
  }

  // Street / residential / commercial — physical address, never "Site" or a well name
  if (addrFromNotes && (wmLooksLikeStreetAddress(addrFromNotes) || wmIsFullServiceAddress(addrFromNotes))) {
    return addrFromNotes;
  }
  const streetFromSite = parts.find((p) => wmLooksLikeStreetAddress(p));
  if (streetFromSite) return streetFromSite;
  if (rawSite && wmLooksLikeStreetAddress(rawSite) && !wmLooksLikeWellOrApiLabel(rawSite) && !wmIsGenericSiteLabel(rawSite)) {
    return rawSite;
  }
  if (rawSite && !wmLooksLikeWellOrApiLabel(rawSite) && !wmIsGenericSiteLabel(rawSite)) return rawSite;
  return addrFromNotes && !wmIsGenericSiteLabel(addrFromNotes) ? addrFromNotes : '';
}

/**
 * Driver board buckets — makes My runs scannable:
 * need pickup vs still on pad waiting for customer vs deliver vs landfill.
 */
export type WmDriverBoardLane = 'pickup' | 'on_site' | 'deliver' | 'landfill' | 'other';

export function wmDriverBoardLane(
  job: Pick<WasteDumpsterJob, 'status' | 'landfill_receipt_at'>,
): WmDriverBoardLane {
  const st = String(job.status || '');
  if (st === 'pickup_requested' || st === 'en_route_pickup') return 'pickup';
  if (st === 'dropped_off') return 'on_site';
  if (st === 'assigned' || st === 'en_route_dropoff') return 'deliver';
  if (st === 'picked_up' && wmNeedsLandfillReceipt(job)) return 'landfill';
  return 'other';
}

export function wmDriverBoardLaneLabel(lane: WmDriverBoardLane): string {
  switch (lane) {
    case 'pickup': return 'Need pickup';
    case 'on_site': return 'On site · waiting';
    case 'deliver': return 'Deliver';
    case 'landfill': return 'Landfill';
    default: return 'Other';
  }
}

/**
 * Strip customer/hauler pricing from driver-visible copy.
 * Drivers see size, waste stream, days, placement — not what the job makes.
 * Amounts stay on hauler/customer surfaces and in event payload_json.
 */
export function wmRedactMoneyForDriver(text: string | null | undefined): string {
  const raw = String(text || '');
  if (!raw) return '';
  return raw
    // " · $957" / " · $957.00" between dots or at end
    .replace(/\s*·\s*\$[\d,]+(?:\.\d{1,2})?/g, '')
    // standalone $ amounts (Payment received · $12.00)
    .replace(/\$[\d,]+(?:\.\d{1,2})?/g, '')
    // collapse leftover separators
    .replace(/\s*·\s*·\s*/g, ' · ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+·\s*$/g, '')
    .replace(/^\s*·\s+/g, '')
    .trim();
}

/** Short status line for driver run cards / switcher. */
export function wmDriverBoardStatusLine(
  job: Pick<WasteDumpsterJob, 'status' | 'landfill_receipt_at' | 'dropoff_serial' | 'pickup_requested_at'>,
): string {
  const st = String(job.status || '');
  switch (st) {
    case 'assigned':
      return 'Ready to deliver';
    case 'en_route_dropoff':
      return 'Driving to drop';
    case 'dropped_off':
      return 'Waiting on customer to request pickup';
    case 'pickup_requested':
      return 'Customer requested pickup — go recover';
    case 'en_route_pickup':
      return 'Driving for pickup';
    case 'picked_up':
      return wmNeedsLandfillReceipt(job) ? 'Recovered · landfill receipts needed' : 'Complete';
    default:
      return wmDriverBoardLaneLabel(wmDriverBoardLane(job));
  }
}

export type WmDriverCustomerContact = {
  name: string | null;
  phone: string | null;
  phoneDisplay: string | null;
  telHref: string | null;
  address: string;
  org: string | null;
  hasDetails: boolean;
};

/** Format 10-digit US phone for driver display. */
export function wmFormatDriverPhoneDisplay(raw?: string | null): string | null {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) {
    const t = String(raw || '').trim();
    return t || null;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Driver-facing customer contact from job notes + account fields.
 * Phone / name stay behind the Customer details control until expanded.
 */
export function wmDriverCustomerContact(job: {
  site_name?: string | null;
  customer_org?: string | null;
  service_kind?: string | null;
  product_line?: string | null;
  requested_notes?: string | null;
  created_by_username?: string | null;
  created_by_user_id?: string | null;
}): WmDriverCustomerContact {
  const notes = parseWmCustomerNotes(job.requested_notes);
  const rawNotes = String(job.requested_notes || '');

  let phone = notes.contactPhone?.trim() || null;
  if (!phone) {
    const m = rawNotes.match(/Phone:\s*([+\d().\s-]{10,20})/i);
    if (m) phone = m[1].trim();
  }
  // Guest identity embeds phone digits
  if (!phone && wmIsGuestUserId(job.created_by_user_id)) {
    const digits = String(job.created_by_user_id || '').replace(/^guest_ph_/, '');
    if (digits.length === 10) phone = digits;
  }

  let name = notes.contactName?.trim() || null;
  const username = String(job.created_by_username || '').trim();
  if (!name && username && !/^guest$/i.test(username) && !wmIsGuestUserId(username)) {
    name = username;
  }

  const address = wmDriverJobLocationLabel(job);
  const org = String(job.customer_org || '').trim() || null;
  const phoneDisplay = wmFormatDriverPhoneDisplay(phone);
  let telHref: string | null = null;
  if (phone) {
    let digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length >= 10) telHref = `tel:+1${digits.slice(-10)}`;
    else if (digits.length > 0) telHref = `tel:${digits}`;
  }

  return {
    name,
    phone: phone || null,
    phoneDisplay,
    telHref,
    address,
    org,
    hasDetails: Boolean(name || phone || address || org),
  };
}

/** "Lea" + "NM" → "Lea County, NM". Leaves "Lea County" alone. */
export function wmFormatCountyState(
  county?: string | null,
  state?: string | null,
): string {
  const c = String(county || '').trim();
  const s = String(state || '').trim();
  if (!c && !s) return '';
  if (!c) return s;
  const countyLabel = /county/i.test(c) ? c : `${c} County`;
  return s ? `${countyLabel}, ${s}` : countyLabel;
}

/**
 * Site label for a cart line / job card.
 * Always prefer the full street address when present so every jobsite shows a
 * complete, verifiable location (not a truncated or partial site-name mirror).
 * Non-oilfield: never prefix well/API-looking names onto a street address.
 */
export function wmCartSiteLabel(
  line: WmCartSiteFields,
  opts?: { oilfield?: boolean },
): string {
  const addr = String(line.serviceAddress || '').trim();
  const site = String(line.siteName || '').trim();
  const api = String(line.apiNumber || '').trim();
  const oilfield = Boolean(opts?.oilfield) || Boolean(api.replace(/\D/g, '').length >= 3);

  if (addr) {
    const siteIsPartialOfAddr = Boolean(
      site
      && (
        addr.toLowerCase() === site.toLowerCase()
        || addr.toLowerCase().startsWith(site.toLowerCase())
        || site.toLowerCase().startsWith(addr.toLowerCase().slice(0, Math.min(10, addr.length)))
      ),
    );
    // Never prefix nearest-well / API names onto a street drop (GREEN FEDERAL · 918 E Santa Fe…)
    if (!oilfield && site && wmLooksLikeWellOrApiLabel(site)) {
      return addr;
    }
    // Building or pad name when distinct from street (Warehouse B · 100 Oak Ave; oilfield pad · street)
    if (
      site
      && !siteIsPartialOfAddr
      && site.length >= 2
      && !wmLooksLikeStreetAddress(site)
    ) {
      return `${site} · ${addr}`;
    }
    return addr;
  }
  if (oilfield && api) {
    const apiDigits = api.replace(/\D/g, '');
    const siteApiMatch = site.match(/\bAPI\s*#?\s*([\d-]+)/i);
    const siteHasApi = Boolean(
      site
      && (
        site.includes(api)
        || (apiDigits.length >= 5 && site.replace(/\D/g, '') === apiDigits)
        || (siteApiMatch && apiDigits.length >= 5 && siteApiMatch[1].replace(/\D/g, '') === apiDigits)
      ),
    );
    if (site && !wmLooksLikeStreetAddress(site) && !wmSiteNameIsApiOnly(site, api)) {
      return siteHasApi ? site : `${site} · API ${api}`;
    }
    return siteHasApi && site ? site : `API ${api}`;
  }
  if (site && (oilfield || !wmLooksLikeWellOrApiLabel(site))) return site;
  if (api) return `API ${api}`;
  return 'Site';
}

/** True when a cart line has enough site identity for the driver. */
export function wmCartLineHasSite(
  line: WmCartSiteFields,
  opts?: { oilfield?: boolean },
): boolean {
  const hasStreet = wmHasPhysicalServiceAddress(line.serviceAddress || '');
  const hasSite = String(line.siteName || '').trim().length >= 2;
  const hasApi = String(line.apiNumber || '').trim().length >= 3;
  const hasPin = Boolean(
    line.dropPin
    && Number.isFinite(line.dropPin.lat)
    && Number.isFinite(line.dropPin.lng),
  );
  if (opts?.oilfield) {
    return hasApi || hasSite || hasStreet || hasPin;
  }
  return hasStreet || hasSite || hasPin;
}

export type WmCartPayIssue = {
  fieldId: string;
  message: string;
  openMap?: boolean;
  lineIndex?: number;
};

/**
 * Cart-aware pay validation (multi-box). Prefer this over form-global checks only.
 * Returns first blocking issue or null when ready to pay.
 */
export function wmGetCartPayIssue(input: {
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  lines: WmCartSiteFields[];
  wasteTypes?: string[] | null;
  acceptTerms?: boolean;
  oilfield?: boolean;
  formServiceAddress?: string | null;
  formSiteName?: string | null;
  formApiNumber?: string | null;
  tonsIncludedLabel?: number | null;
  lockedGateCode?: string | null;
}): WmCartPayIssue | null {
  if (!String(input.contactFirstName || '').trim()) {
    return { fieldId: 'contact_first', message: 'Add a first name so the hauler can reach you on site.' };
  }
  if (!String(input.contactLastName || '').trim()) {
    return { fieldId: 'contact_last', message: 'Add a last name so the hauler can reach you on site.' };
  }
  {
    let digits = String(input.contactPhone || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    const phoneOk = digits.length === 10
      && digits[0] !== '0' && digits[0] !== '1'
      && digits[3] !== '0' && digits[3] !== '1';
    if (!phoneOk) {
      return { fieldId: 'contact_phone', message: 'Enter a valid 10-digit US phone so the driver can call about delivery.' };
    }
  }
  {
    const email = String(input.contactEmail || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { fieldId: 'contact_email', message: 'Add an email so we can send your receipt and dumpster confirmation.' };
    }
  }
  {
    const gate = String(input.lockedGateCode || '').replace(/\D/g, '');
    if (gate && !wmLockedGateCodeIsValid(input.lockedGateCode)) {
      return { fieldId: 'locked_gate', message: 'Enter 4–6 digits' };
    }
  }

  const lines = input.lines?.length
    ? input.lines
    : [{
      siteName: input.formSiteName,
      apiNumber: input.formApiNumber,
      serviceAddress: input.formServiceAddress,
      dropPin: null,
    }];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    // Residential / commercial: partial fragments like "926 east" are not allowed.
    // Require a full street address (Google formatted) when a street site is used.
    if (!input.oilfield) {
      const addr = String(line.serviceAddress || '').trim();
      if (addr && !wmIsFullServiceAddress(addr)) {
        return {
          fieldId: 'service_address',
          message: lines.length > 1
            ? `Box ${i + 1} needs a full street address (street, city, state) — pick a Google suggestion.`
            : 'Enter a full service address (street, city, state). Pick a Google suggestion so the address can be verified.',
          lineIndex: i,
        };
      }
    }
    if (!wmCartLineHasSite(line, { oilfield: input.oilfield })) {
      return {
        fieldId: input.oilfield ? 'api_number' : 'service_address',
        message: lines.length > 1
          ? `Box ${i + 1} needs a site (full address, API #, or facility name).`
          : (input.oilfield
            ? 'Oilfield pads need an API #, facility name, or full street address for this drop.'
            : 'Enter a full service street address (street, city, state) so the driver can verify the drop.'),
        lineIndex: i,
      };
    }
    if (
      !line.dropPin
      || !Number.isFinite(line.dropPin.lat)
      || !Number.isFinite(line.dropPin.lng)
    ) {
      return {
        fieldId: 'drop_pin',
        message: lines.length > 1
          ? `Pin every dumpster on the map (${lines.filter((l) => l.dropPin).length}/${lines.length} set).`
          : 'Open the map and drop a pin (or draw) where the dumpster should sit.',
        openMap: true,
        lineIndex: i,
      };
    }
  }

  if (!input.wasteTypes?.length) {
    return { fieldId: 'waste_types', message: 'Select at least one waste type.' };
  }
  if (!input.acceptTerms) {
    const tons = input.tonsIncludedLabel != null ? input.tonsIncludedLabel : WM_INCLUDED_TONS_FREE;
    return {
      fieldId: 'accept_terms',
      message: `Confirm the package includes delivery, pickup, and up to ${tons} tons before paying.`,
    };
  }
  return null;
}

/**
 * Expand / shrink cart lines so count of `size` equals `qty` (same-site clones of template).
 * Qty 0 removes all of that size. Max 20 total boxes.
 */
export function wmSetCartSizeQuantity<T extends { id: string; size: WasteDumpsterSize | string }>(
  lines: T[],
  size: WasteDumpsterSize | string,
  qty: number,
  cloneLine: (from: T | null, size: WasteDumpsterSize | string) => T,
): T[] {
  const target = Math.max(0, Math.min(20, Math.floor(Number(qty) || 0)));
  const others = lines.filter((l) => l.size !== size);
  const ofSize = lines.filter((l) => l.size === size);
  if (target === 0) {
    return others.length ? others : (lines.length ? [cloneLine(lines[0], size)] : [cloneLine(null, size)]);
  }
  const nextOfSize: T[] = [];
  for (let i = 0; i < target; i += 1) {
    if (ofSize[i]) {
      nextOfSize.push({ ...ofSize[i], size } as T);
    } else {
      const template = ofSize[0] || lines[lines.length - 1] || null;
      nextOfSize.push(cloneLine(template, size));
    }
  }
  const merged = [...others, ...nextOfSize];
  return merged.slice(0, 20);
}

/** Identity for “is this the same pad / house?” — used when swapping the current well. */
export function wmSiteIdentityKey(site: {
  apiNumber?: string | null;
  serviceAddress?: string | null;
  siteName?: string | null;
}): string {
  const api = String(site.apiNumber || '').replace(/\D/g, '');
  if (api.length >= 5) return `api:${api}`;
  const addr = String(site.serviceAddress || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (addr.length >= 8) return `addr:${addr}`;
  const name = String(site.siteName || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (name.length >= 2) return `name:${name}`;
  return '';
}

type WmSyncableCartLine = {
  id: string;
  siteKey: string;
  size: WasteDumpsterSize | string;
  apiNumber?: string | null;
  siteName?: string | null;
  serviceAddress?: string | null;
  facilityLat?: number | null;
  facilityLng?: number | null;
  facilityType?: string | null;
  county?: string | null;
  state?: string | null;
  dropPin?: { lat: number; lng: number } | null;
  highlightPath?: unknown;
};

/**
 * Point the current quote at the Where-step site.
 * Testing a new API / well on a single-site order replaces the old pad
 * (one box) instead of stacking another jobsite price.
 * Multi-site carts (Add jobsite) only rewrite the primary group.
 * Drop pins stay per-box — never copy one placement onto sibling dumpsters.
 */
export function wmSyncQuoteToCurrentSite<T extends WmSyncableCartLine>(
  lines: T[],
  next: T,
): T[] {
  if (!lines.length) return [{ ...next, dropPin: next.dropPin ?? null }];
  const siteKeys = new Set(lines.map((l) => l.siteKey));
  const prevId = wmSiteIdentityKey(lines[0]);
  const nextId = wmSiteIdentityKey(next);
  const padChanged = Boolean(prevId && nextId && prevId !== nextId);
  const paint = (line: T): T => ({
    ...line,
    apiNumber: next.apiNumber ?? line.apiNumber,
    siteName: next.siteName ?? line.siteName,
    serviceAddress: next.serviceAddress ?? line.serviceAddress,
    facilityLat: next.facilityLat !== undefined ? next.facilityLat : line.facilityLat,
    facilityLng: next.facilityLng !== undefined ? next.facilityLng : line.facilityLng,
    facilityType: next.facilityType !== undefined ? next.facilityType : line.facilityType,
    county: next.county !== undefined ? next.county : line.county,
    state: next.state !== undefined ? next.state : line.state,
    dropPin: padChanged ? null : line.dropPin,
    highlightPath: padChanged ? null : line.highlightPath,
  });

  if (siteKeys.size > 1) {
    const primary = lines[0].siteKey;
    return lines.map((line) => (line.siteKey === primary ? paint(line) : line));
  }

  if (padChanged) {
    return [{
      ...paint(lines[0]),
      id: lines[0].id,
      siteKey: lines[0].siteKey,
      size: lines[0].size,
    }];
  }
  const keepKey = lines[0].siteKey;
  return lines.map((line) => paint({ ...line, siteKey: keepKey }));
}

export type WmPinMapLineLike = {
  id?: string | null;
  siteKey?: string | null;
  dropPin?: { lat: number; lng: number } | null;
  highlightPath?: unknown;
  facilityLat?: number | null;
  facilityLng?: number | null;
  apiNumber?: string | null;
  serviceAddress?: string | null;
  siteName?: string | null;
};

function wmFiniteLatLng(
  lat?: number | null,
  lng?: number | null,
): { lat: number; lng: number } | null {
  if (lat == null || lng == null) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/** Same jobsite? siteKey first; never treat two empty streets as a match. */
export function wmPinLinesShareSite(
  a?: WmPinMapLineLike | null,
  b?: WmPinMapLineLike | null,
): boolean {
  if (!a || !b) return false;
  const aKey = String(a.siteKey || '').trim();
  const bKey = String(b.siteKey || '').trim();
  if (aKey && bKey) return aKey === bKey;
  const idA = wmSiteIdentityKey(a);
  const idB = wmSiteIdentityKey(b);
  return Boolean(idA && idB && idA === idB);
}

/**
 * Pin-map camera vs placement for one cart line.
 * initialCoords = this box is already placed (Change).
 * siteLat/Lng = zoom to that line's API / house — never another box's pin.
 */
export function wmPinMapViewForLine(input: {
  pinLine?: WmPinMapLineLike | null;
  lines?: WmPinMapLineLike[];
  formFacilityLat?: number | null;
  formFacilityLng?: number | null;
  formDropPin?: { lat: number; lng: number } | null;
  formDropPinConfirmed?: boolean;
  formApiNumber?: string | null;
  formServiceAddress?: string | null;
  formSiteName?: string | null;
}): {
  siteLat: number | null;
  siteLng: number | null;
  initialCoords: { lat: number; lng: number } | null;
} {
  const pinLine = input.pinLine || null;
  const lines = input.lines || [];
  const formSite: WmPinMapLineLike = {
    siteKey: lines[0]?.siteKey || null,
    apiNumber: input.formApiNumber,
    serviceAddress: input.formServiceAddress,
    siteName: input.formSiteName,
    facilityLat: input.formFacilityLat,
    facilityLng: input.formFacilityLng,
    dropPin: input.formDropPinConfirmed ? input.formDropPin || null : null,
  };

  const ownPin = pinLine ? wmFiniteLatLng(pinLine.dropPin?.lat, pinLine.dropPin?.lng) : null;
  const formPin = input.formDropPinConfirmed
    ? wmFiniteLatLng(input.formDropPin?.lat, input.formDropPin?.lng)
    : null;
  const initialCoords = pinLine ? ownPin : formPin;

  const lineFacility = pinLine
    ? wmFiniteLatLng(pinLine.facilityLat, pinLine.facilityLng)
    : null;
  const formFacility = wmFiniteLatLng(input.formFacilityLat, input.formFacilityLng);
  const sameSite = pinLine ? wmPinLinesShareSite(pinLine, formSite) : true;
  const zoom = lineFacility
    || (sameSite ? formFacility : null)
    || initialCoords
    || null;

  return {
    siteLat: zoom?.lat ?? null,
    siteLng: zoom?.lng ?? null,
    initialCoords,
  };
}

/** Summarize cart for notes / share / review. */
export function wmCartOrderSummary(
  lines: Array<WmCartSiteFields & { size?: string | null }>,
  totalUsd: number,
): string {
  if (!lines.length) return `Dumpster order · $${totalUsd.toFixed(2)}`;
  const parts = lines.map((l, i) => {
    const yds = String(l.size || '30ft').replace('ft', '');
    return `${i + 1}. ${yds} yd @ ${wmCartSiteLabel(l)}`;
  });
  return `${lines.length} dumpster${lines.length === 1 ? '' : 's'} · $${totalUsd.toFixed(2)}\n${parts.join('\n')}`;
}

/** One dumpster placement in a multi-box cart (size + pin + site). */
export interface WmDumpsterCheckoutItem {
  contractorId?: string;
  paymentMode?: string;
  /** Requested calendar day (YYYY-MM-DD); no exact arrival time is promised. */
  deliveryDate?: string | null;
  projectName?: string | null;
  apiNumber?: string | null;
  facilityId?: string | null;
  dumpsterSize: WasteDumpsterSize;
  lat: number;
  lng: number;
  orientation: string;
  notes?: string | null;
  siteName?: string | null;
  customerOrg?: string | null;
  companyName?: string | null;
  crewId?: string | null;
  rentalDays?: number;
  pricingMode?: WmPricingMode | string | null;
  productLine?: WmProductLine | string | null;
  wasteStream?: WmWasteStream | string | null;
  serviceKind?: WmServiceKind | string | null;
  oneWayMiles?: number | null;
  deliveryLead?: string | null;
  /** Optional draw path for driver (JSON array of {lat,lng}). */
  highlightPath?: Array<{ lat: number; lng: number }> | null;
  /** Recurring service (weeks–months): cadence + calendar end date. */
  serviceFrequency?: 'project' | 'recurring' | null;
  recurrenceCadence?: WmRecurrenceCadence | string | null;
  recurrenceEndDate?: string | null;
  /**
   * Exact total shown in the app for this box (cents).
   * Server must charge this amount — never silently higher.
   */
  quotedAmountCents?: number | null;
  amountCents?: number | null;
}

/** Swap cadence for recurring dumpster service. */
export type WmRecurrenceCadence = 'weekly' | 'biweekly' | 'every4weeks' | 'monthly' | 'on_call';

export const WM_RECURRENCE_CADENCES: Array<{
  id: WmRecurrenceCadence;
  label: string;
  hint: string;
}> = [
  { id: 'weekly', label: 'Weekly swap', hint: 'Box swapped every week' },
  { id: 'biweekly', label: 'Every 2 weeks', hint: 'Box swapped every other week' },
  { id: 'monthly', label: 'Monthly', hint: 'About every 4 weeks — one swap a month' },
  { id: 'on_call', label: 'On call', hint: 'You request each swap when the box is full' },
];

export function wmRecurrenceCadenceLabel(id?: string | null): string {
  if (id === 'every4weeks') return 'Monthly';
  const hit = WM_RECURRENCE_CADENCES.find((c) => c.id === id);
  return hit ? hit.label : 'On call';
}

export function buildWmCheckoutBody(
  input: (WmDumpsterCheckoutItem & { successUrl?: string; cancelUrl?: string; items?: WmDumpsterCheckoutItem[] })
    | {
      contractorId?: string;
    paymentMode?: string;
    items: WmDumpsterCheckoutItem[];
      rentalDays?: number;
      pricingMode?: WmPricingMode | string | null;
      productLine?: WmProductLine | string | null;
      wasteStream?: WmWasteStream | string | null;
      serviceKind?: WmServiceKind | string | null;
      serviceFrequency?: 'project' | 'recurring' | null;
      recurrenceCadence?: WmRecurrenceCadence | string | null;
      recurrenceEndDate?: string | null;
      oneWayMiles?: number | null;
      customerOrg?: string | null;
      companyName?: string | null;
      crewId?: string | null;
      notes?: string | null;
      successUrl?: string;
      cancelUrl?: string;
    },
): Record<string, unknown> {
  const anyIn = input as WmDumpsterCheckoutItem & {
    items?: WmDumpsterCheckoutItem[];
    successUrl?: string;
    cancelUrl?: string;
    pricingMode?: WmPricingMode | string | null;
  };
  if (Array.isArray(anyIn.items) && anyIn.items.length > 0) {
    const rentalDays = anyIn.rentalDays ?? anyIn.items[0]?.rentalDays ?? 7;
    const shared = {
      projectName: anyIn.projectName,
      apiNumber: anyIn.apiNumber,
      facilityId: anyIn.facilityId,
      rentalDays,
      pricingMode: wmNormalizePricingMode(
        anyIn.pricingMode
        ?? anyIn.items[0]?.pricingMode
        ?? wmSuggestedPricingMode(Number(rentalDays)),
      ),
      productLine: anyIn.productLine ?? anyIn.items[0]?.productLine,
      wasteStream: anyIn.wasteStream ?? anyIn.items[0]?.wasteStream,
      serviceKind: anyIn.serviceKind ?? anyIn.items[0]?.serviceKind,
      oneWayMiles: anyIn.oneWayMiles ?? anyIn.items[0]?.oneWayMiles ?? null,
      deliveryLead: anyIn.deliveryLead ?? anyIn.items[0]?.deliveryLead ?? null,
      customerOrg: anyIn.customerOrg ?? anyIn.items[0]?.customerOrg ?? null,
      companyName: anyIn.companyName ?? anyIn.items[0]?.companyName ?? null,
      crewId: anyIn.crewId ?? anyIn.items[0]?.crewId ?? null,
      notes: anyIn.notes ?? null,
      serviceFrequency: anyIn.serviceFrequency ?? anyIn.items[0]?.serviceFrequency ?? null,
      recurrenceCadence: anyIn.recurrenceCadence ?? anyIn.items[0]?.recurrenceCadence ?? null,
      recurrenceEndDate: anyIn.recurrenceEndDate ?? anyIn.items[0]?.recurrenceEndDate ?? null,
      successUrl: anyIn.successUrl,
      cancelUrl: anyIn.cancelUrl,
    };
    const items = anyIn.items.map((it) => {
      const quotedRaw = it.quotedAmountCents ?? it.amountCents;
      const quotedAmountCents =
        quotedRaw != null && Number.isFinite(Number(quotedRaw)) && Number(quotedRaw) > 0
          ? Math.round(Number(quotedRaw))
          : null;
      return {
        // Always canonical size ids (30ft/50ft) — server rejects labels / empties.
        dumpsterSize: normalizeWmDumpsterSize(it.dumpsterSize),
        requestedLat: it.lat,
        requestedLng: it.lng,
        requestedOrientation: it.orientation,
        deliveryDate: it.deliveryDate ?? null,
        siteName: it.siteName ?? null,
        notes: it.notes ?? shared.notes,
        customerOrg: it.customerOrg ?? shared.customerOrg,
        companyName: it.companyName ?? shared.companyName,
        crewId: it.crewId ?? shared.crewId,
        rentalDays: it.rentalDays ?? shared.rentalDays,
        pricingMode: wmNormalizePricingMode(it.pricingMode ?? shared.pricingMode),
        productLine: it.productLine ?? shared.productLine,
        wasteStream: it.wasteStream ?? shared.wasteStream,
        serviceKind: it.serviceKind ?? shared.serviceKind,
        oneWayMiles: it.oneWayMiles ?? shared.oneWayMiles,
        highlightPath: it.highlightPath ?? null,
        // Hard rule: charge only what the app displayed.
        quotedAmountCents,
        amountCents: quotedAmountCents,
      };
    });
    // Older production checkout only read top-level dumpsterSize (ignored items[0]).
    // Mirror the first cart line so 30 yd pay never 400s as "missing size".
    const first = items[0];
    return {
      ...shared,
      dumpsterSize: first.dumpsterSize,
      requestedLat: first.requestedLat,
      requestedLng: first.requestedLng,
      requestedOrientation: first.requestedOrientation,
      siteName: first.siteName,
      items,
    };
  }
  // Legacy single-box
  const rentalDays = anyIn.rentalDays ?? 7;
  const quotedRaw = anyIn.quotedAmountCents ?? anyIn.amountCents;
  const quotedAmountCents =
    quotedRaw != null && Number.isFinite(Number(quotedRaw)) && Number(quotedRaw) > 0
      ? Math.round(Number(quotedRaw))
      : null;
  return {
    dumpsterSize: normalizeWmDumpsterSize(anyIn.dumpsterSize),
    projectName: anyIn.projectName,
    apiNumber: anyIn.apiNumber,
    facilityId: anyIn.facilityId,
    requestedLat: anyIn.lat,
    requestedLng: anyIn.lng,
    requestedOrientation: anyIn.orientation,
    deliveryDate: anyIn.deliveryDate ?? null,
    notes: anyIn.notes,
    siteName: anyIn.siteName,
    customerOrg: anyIn.customerOrg,
    companyName: anyIn.companyName,
    crewId: anyIn.crewId,
    rentalDays,
    pricingMode: wmNormalizePricingMode(anyIn.pricingMode ?? wmSuggestedPricingMode(rentalDays)),
    productLine: anyIn.productLine,
    wasteStream: anyIn.wasteStream,
    serviceKind: anyIn.serviceKind,
    oneWayMiles: anyIn.oneWayMiles,
    highlightPath: anyIn.highlightPath ?? null,
    serviceFrequency: anyIn.serviceFrequency ?? null,
    recurrenceCadence: anyIn.recurrenceCadence ?? null,
    recurrenceEndDate: anyIn.recurrenceEndDate ?? null,
    quotedAmountCents,
    amountCents: quotedAmountCents,
    successUrl: anyIn.successUrl,
    cancelUrl: anyIn.cancelUrl,
  };
}

export const wasteManagementService = {
  /** Load this hauler's rate card (platform guide if none saved). */
  async getRateCard(opts?: { crewId?: string | null }): Promise<{
    rateCard: WmRateCard;
    source: 'company' | 'platform';
    ownerUserId?: string | null;
  }> {
    const params = new URLSearchParams();
    if (opts?.crewId) params.set('crewId', opts.crewId);
    const q = params.toString() ? `?${params.toString()}` : '';
    const data = await requestJson<{
      rateCard?: Partial<WmRateCard>;
      source?: 'company' | 'platform';
      ownerUserId?: string | null;
    }>(`/rate-card${q}`);
    return {
      rateCard: wmNormalizeRateCard(data.rateCard),
      source: data.source === 'company' ? 'company' : 'platform',
      ownerUserId: data.ownerUserId ?? null,
    };
  },

  /** Company desk: save week prices + temporary sale + marketplace listing. */
  async saveRateCard(input: {
    rateCard: Partial<WmRateCard>;
    crewId?: string | null;
    companyName?: string | null;
  }): Promise<{ rateCard: WmRateCard; source: 'company' }> {
    // First save defaults to listed on marketplace (haulers appear for customers)
    const rateCard = wmNormalizeRateCard({
      ...input.rateCard,
      listOnMarketplace: input.rateCard?.listOnMarketplace !== false,
    });
    const data = await requestJson<{ rateCard?: Partial<WmRateCard> }>('/rate-card', {
      method: 'POST',
      body: JSON.stringify({
        rateCard,
        crewId: input.crewId || null,
        companyName: input.companyName || null,
      }),
    });
    return {
      rateCard: wmNormalizeRateCard(data.rateCard ?? rateCard),
      source: 'company',
    };
  },

  /**
   * Customer multi-hauler list: every registered company that lists on marketplace
   * (+ platform default). Each row includes quote $ for size/days.
   */
  async listBookableHaulers(opts?: {
    size?: string | null;
    days?: number | null;
    wasteStream?: string | null;
    serviceKind?: string | null;
    productLine?: string | null;
  }): Promise<{ haulers: WmBookableHauler[] }> {
    const params = new URLSearchParams();
    if (opts?.size) params.set('size', String(opts.size));
    if (opts?.days != null) params.set('days', String(opts.days));
    if (opts?.wasteStream) params.set('wasteStream', String(opts.wasteStream));
    if (opts?.serviceKind) params.set('serviceKind', String(opts.serviceKind));
    if (opts?.productLine) params.set('productLine', String(opts.productLine));
    const q = params.toString() ? `?${params.toString()}` : '';
    const data = await requestJson<{ haulers?: Array<Partial<WmBookableHauler> & { rateCard?: Partial<WmRateCard> }> }>(
      `/haulers${q}`,
    );
    const haulers: WmBookableHauler[] = (data.haulers || []).map((h) => {
      const rateCard = wmNormalizeRateCard(h.rateCard);
      const quoted = Number(h.quotedTotalUsd);
      return {
        id: String(h.id || h.crewId || 'platform'),
        crewId: h.crewId != null && String(h.crewId) ? String(h.crewId) : null,
        companyName: String(h.companyName || DEFAULT_WM_HAULER_NAME).slice(0, 120),
        source: h.source === 'company' ? 'company' : 'platform',
        rateCard,
        quotedTotalUsd: Number.isFinite(quoted) && quoted > 0
          ? Math.round(quoted * 100) / 100
          : wmEstimateQuote({
            days: opts?.days ?? 7,
            size: opts?.size || '30ft',
            wasteStream: opts?.wasteStream,
            serviceKind: opts?.serviceKind,
            productLine: opts?.productLine,
            rateCard,
          }).totalUsd,
        sizesOffered: Array.isArray(h.sizesOffered) && h.sizesOffered.length > 0
          ? h.sizesOffered.map((s) => normalizeWmDumpsterSize(s))
          : (rateCard.sizesOffered || [...WM_DUMPSTER_SIZES]),
        promoLabel: rateCard.promoLabel,
        promoPercentOff: rateCard.promoPercentOff,
        isDefault: Boolean(h.isDefault) || h.id === DEFAULT_WM_HAULER_ID,
      };
    });
    return { haulers };
  },

  async listJobs(options?: { status?: string; view?: WasteJobListView }): Promise<WasteDumpsterJob[]> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.view) params.set('view', options.view);
    const q = params.toString() ? `?${params.toString()}` : '';
    const data = await requestJson<{ jobs: WasteDumpsterJob[] }>(`/jobs${q}`);
    return data.jobs || [];
  },

  /**
   * The Available (open) board, plus WHY it is empty when it is.
   *
   * listJobs drops everything but `jobs`, so the driver screen could not tell a
   * genuinely quiet board from "you are in no crew, so the server refused to
   * look" — and showed the same dead-end message for both while paid
   * marketplace jobs waited unseen. `noCrew` carries that reason through.
   */
  async listOpenBoard(): Promise<{ jobs: WasteDumpsterJob[]; noCrew: boolean }> {
    const data = await requestJson<{ jobs: WasteDumpsterJob[]; noCrew?: boolean }>('/jobs?view=open');
    return { jobs: data.jobs || [], noCrew: data.noCrew === true };
  },

  async getJob(jobId: string): Promise<WasteJobBundle> {
    // Guard: an empty id would hit GET /jobs/ and surface the API fallthrough
    // 404 ("Supported: GET/POST …") as a scary banner on the driver screen.
    if (!jobId || !String(jobId).trim()) {
      throw new Error('Job id missing — reopen this job from the list.');
    }
    return requestJson<WasteJobBundle>(`/jobs/${encodeURIComponent(jobId)}`);
  },

  /**
   * One physical dumpster placement, or multi-box cart via `items[]`.
   * Multi-dumpster creates one job per item with shared order_group_id when 2+.
   */
  async requestDumpster(input: WmDumpsterCheckoutItem | {
    projectName?: string | null;
    apiNumber?: string | null;
    facilityId?: string | null;
    contractorId?: string;
    paymentMode?: string;
    items: WmDumpsterCheckoutItem[];
    rentalDays?: number;
    pricingMode?: WmPricingMode | string | null;
    productLine?: WmProductLine | string | null;
    wasteStream?: WmWasteStream | string | null;
    serviceKind?: WmServiceKind | string | null;
    serviceFrequency?: 'project' | 'recurring' | null;
    recurrenceCadence?: WmRecurrenceCadence | string | null;
    recurrenceEndDate?: string | null;
    oneWayMiles?: number | null;
    customerOrg?: string | null;
    companyName?: string | null;
    crewId?: string | null;
    notes?: string | null;
  }): Promise<WasteJobBundle & {
    jobs?: WasteJobBundle[];
    jobIds?: string[];
    orderGroupId?: string | null;
    boxCount?: number;
  }> {
    const body = buildWmCheckoutBody(input as WmDumpsterCheckoutItem & { items?: WmDumpsterCheckoutItem[] });
    const data = await requestJson<{
      job: WasteJobBundle;
      jobs?: WasteJobBundle[];
      jobIds?: string[];
      orderGroupId?: string | null;
      boxCount?: number;
    }>('/jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return {
      ...data.job,
      jobs: data.jobs,
      jobIds: data.jobIds,
      orderGroupId: data.orderGroupId,
      boxCount: data.boxCount,
    };
  },

  /**
   * Create dumpster job(s) + Stripe Checkout session (physical service — external pay allowed).
   * Single box or multi-dumpster cart (`items[]`): one Stripe session, N jobs, shared order_group_id.
   * Webhook marks all jobs paid when checkout completes.
   */
  async createCheckout(input: WmDumpsterCheckoutItem & {
    budgetFunding?: import('../../shared/operatorBudgets').BudgetFunding;
    successUrl?: string;
    cancelUrl?: string;
    guestPhone?: string | null;
    contactPhone?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
  } | {
    budgetFunding?: import('../../shared/operatorBudgets').BudgetFunding;
    projectName?: string | null;
    apiNumber?: string | null;
    facilityId?: string | null;
    contractorId?: string;
    paymentMode?: string;
    items: WmDumpsterCheckoutItem[];
    rentalDays: number;
    pricingMode?: WmPricingMode | string | null;
    productLine?: WmProductLine | string | null;
    wasteStream?: WmWasteStream | string | null;
    serviceKind?: WmServiceKind | string | null;
    oneWayMiles?: number | null;
    deliveryLead?: string | null;
    customerOrg?: string | null;
    companyName?: string | null;
    crewId?: string | null;
    notes?: string | null;
    successUrl?: string;
    cancelUrl?: string;
    guestPhone?: string | null;
    contactPhone?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
  }): Promise<{
    job: WasteJobBundle;
    jobs?: WasteJobBundle[];
    jobIds?: string[];
    orderGroupId?: string | null;
    boxCount?: number;
    funded?: boolean;
    awaitingAcceptance?: boolean;
    checkoutUrl: string;
    sessionId: string;
    amountCents: number;
    rentalDays: number;
    guestCheckout?: boolean;
    guestPhone?: string | null;
    saveAccountSuggested?: boolean;
  }> {
    const phone = normalizeWmGuestPhone(
      (input as { guestPhone?: string; contactPhone?: string }).guestPhone
      || (input as { contactPhone?: string }).contactPhone,
    );
    const contactName = (input as { contactName?: string | null }).contactName || null;
    const contactEmailRaw = String(
      (input as { contactEmail?: string | null }).contactEmail || '',
    ).trim();
    const body = {
      ...buildWmCheckoutBody(input),
      budgetFunding: input.budgetFunding,
      guestPhone: phone,
      contactPhone: phone,
      contactName: contactName || undefined,
      contactEmail: contactEmailRaw || undefined,
    };
    return requestJson('/checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    }, { guestPhone: phone, contactName });
  },

  /**
   * Accept job. Optional hauler re-quote locks haul + delivery + add'l days (A+).
   */
  async acceptJob(
    jobId: string,
    crewId?: string | null,
    quote?: {
      haulUsd?: number | null;
      deliveryUsd?: number | null;
      addlUsageUsd?: number | null;
      amountCents?: number | null;
      haulerQuoteNotes?: string | null;
      truckNumber?: string | null;
      driverName?: string | null;
    },
  ) {
    // Never send catalog placeholder (local:thompson-hauling) — server treats that as hard linked-crew 403.
    const realCrewId = isDefaultWmHaulerId(crewId) ? null : (crewId || null);
    return requestJson<{ job: WasteJobBundle }>(`/jobs/${encodeURIComponent(jobId)}/accept`, {
      method: 'POST',
      body: JSON.stringify({
        crewId: realCrewId,
        haulUsd: quote?.haulUsd,
        deliveryUsd: quote?.deliveryUsd,
        addlUsageUsd: quote?.addlUsageUsd,
        amountCents: quote?.amountCents,
        haulerQuoteNotes: quote?.haulerQuoteNotes,
        truckNumber: quote?.truckNumber || undefined,
        driverName: quote?.driverName || undefined,
      }),
    });
  },

  async assignWorker(
    jobId: string,
    workerUserId: string,
    workerUsername?: string | null,
    opts?: {
      /** Yard inventory unit id (preferred) */
      inventoryUnitId?: string | null;
      /** Plate serial the driver must load from the yard */
      dumpsterSerial?: string | null;
    },
  ) {
    return requestJson<{ job: WasteJobBundle }>(`/jobs/${encodeURIComponent(jobId)}/assign`, {
      method: 'POST',
      body: JSON.stringify({
        userId: workerUserId,
        username: workerUsername,
        inventoryUnitId: opts?.inventoryUnitId || undefined,
        dumpsterSerial: opts?.dumpsterSerial || undefined,
      }),
    });
  },

  async markEnRoute(
    jobId: string,
    leg: 'dropoff' | 'pickup' = 'dropoff',
    tracking?: {
      truckNumber?: string | null;
      trailerNumber?: string | null;
      driverName?: string | null;
    },
  ) {
    return requestJson<{ job: WasteJobBundle }>(`/jobs/${encodeURIComponent(jobId)}/en-route`, {
      method: 'POST',
      body: JSON.stringify({
        leg,
        truckNumber: tracking?.truckNumber || undefined,
        trailerNumber: tracking?.trailerNumber || undefined,
        driverName: tracking?.driverName || undefined,
      }),
    });
  },

  /** Driver arrived at drop or pickup pin — notifies customer + job group. */
  async markArrived(
    jobId: string,
    coords?: {
      lat?: number | null;
      lng?: number | null;
      accuracyM?: number | null;
      truckNumber?: string | null;
      trailerNumber?: string | null;
      driverName?: string | null;
    },
  ) {
    return requestJson<{ job: WasteJobBundle; alreadyArrived?: boolean }>(
      `/jobs/${encodeURIComponent(jobId)}/arrived`,
      {
        method: 'POST',
        body: JSON.stringify({
          lat: coords?.lat,
          lng: coords?.lng,
          accuracyM: coords?.accuracyM,
          truckNumber: coords?.truckNumber || undefined,
          trailerNumber: coords?.trailerNumber || undefined,
          driverName: coords?.driverName || undefined,
        }),
      },
    );
  },

  async addMember(jobId: string, userId: string, role: WasteJobRole, username?: string | null) {
    return requestJson<{ job: WasteJobBundle }>(`/jobs/${encodeURIComponent(jobId)}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, role, username }),
    });
  },

  /** Upload serial-plate image to R2; returns photoId + URL path (not a data URL). */
  async uploadSerialPhoto(imageBase64OrDataUrl: string, jobId?: string | null): Promise<{ photoId: string; url: string }> {
    const data = await requestJson<{ photoId: string; url: string }>('/serial-photo', {
      method: 'POST',
      body: JSON.stringify({
        imageBase64: imageBase64OrDataUrl,
        jobId: jobId || null,
      }),
    });
    return { photoId: data.photoId, url: data.url };
  },

  /**
   * Worker: drop dumpster — GPS + serial + orientation + 5 placement photos (R2 ids).
   * dropPhotos: full_box, doors_face, interior_empty, access_path, serial_plate.
   */
  async markDropoff(jobId: string, input: {
    lat: number;
    lng: number;
    serial: string;
    /** @deprecated prefer dropPhotos with kind serial_plate */
    serialPhotoId?: string;
    serialPhotoUrl?: string;
    dropPhotos?: Array<{ kind: string; photoId: string }>;
    orientation: string;
    accuracyM?: number | null;
    /** Field exception: allow drop when customer has not paid yet. */
    allowUnpaidDrop?: boolean;
    /** After driver confirms far-from-pin or coarse GPS. */
    forceDistance?: boolean;
    forceCoarseGps?: boolean;
    truckNumber?: string | null;
    trailerNumber?: string | null;
    driverName?: string | null;
    /** Dumpster box / roll-off trailer issues (optional) */
    equipmentIssueTags?: string[];
    equipmentIssueNotes?: string | null;
    equipmentIssueSeverity?: string | null;
    /** One proof photo per selected issue chip (R2 ids) — chip = gallery caption. */
    equipmentIssuePhotos?: Array<{ tag: string; photoId: string }>;
  }) {
    return requestJson<{ job: WasteJobBundle; photoCount?: number; equipmentIssue?: unknown }>(
      `/jobs/${encodeURIComponent(jobId)}/dropoff`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  },

  /**
   * Active dumpsters/requests with GPS for the live map.
   * - customer: only this customer's dumpster jobs (never hauler fleet / other pads)
   * - ops: creator + job members + hauler crew jobs (drivers / company)
   */
  async listMapPins(scope: 'customer' | 'ops' = 'ops'): Promise<WasteMapPin[]> {
    const q = scope === 'customer' ? '?scope=customer' : '?scope=ops';
    const data = await requestJson<{ pins: WasteMapPin[] }>(`/jobs/map${q}`);
    return (data.pins || []).map((pin) => {
      const daysOnSite = pin.daysOnSite != null
        ? pin.daysOnSite
        : wmDaysOnSite(pin.dropoffAt);
      const readyForPickup = pin.readyForPickup
        ?? (pin.status === 'pickup_requested'
          || pin.status === 'en_route_pickup'
          || (pin.status === 'dropped_off'
            && pin.rentalDays != null
            && daysOnSite != null
            && daysOnSite >= pin.rentalDays));
      return { ...pin, daysOnSite, readyForPickup: Boolean(readyForPickup) };
    });
  },

  /**
   * Heartbeat live truck GPS while the employee is assigned / en route in-app.
   * Powers the truck pin for customer + crew lead on the Google fleet map.
   */
  async postDriverLocation(
    jobId: string,
    input: {
      lat: number;
      lng: number;
      accuracyM?: number | null;
      truckNumber?: string | null;
      trailerNumber?: string | null;
      driverName?: string | null;
    },
  ) {
    // Never heartbeat junk coords: a GPS-denied device reports (0,0), which
    // would paint the truck at Null Island (Gulf of Guinea) on the fleet map.
    if (
      !Number.isFinite(input.lat)
      || !Number.isFinite(input.lng)
      || (input.lat === 0 && input.lng === 0)
    ) {
      return Promise.reject(new Error('No GPS fix — truck location not sent'));
    }
    return requestJson<{
      success: boolean;
      jobId: string;
      driverLat: number;
      driverLng: number;
      driverLocationAt: string;
    }>(`/jobs/${encodeURIComponent(jobId)}/driver-location`, {
      method: 'POST',
      body: JSON.stringify({
        lat: input.lat,
        lng: input.lng,
        accuracyM: input.accuracyM ?? null,
        truckNumber: input.truckNumber ?? null,
        trailerNumber: input.trailerNumber ?? null,
        driverName: input.driverName ?? null,
      }),
    });
  },

  /**
   * Customer requests removal. Optionally start Stripe checkout for another rental
   * week so the driver leaves a fresh dumpster when recovering the full one.
   */
  /** Customer accepts drop placement (optional). Does NOT release company pay. */
  async acceptDrop(jobId: string) {
    return requestJson<{ job: WasteJobBundle; alreadyAccepted?: boolean; message?: string }>(
      `/jobs/${encodeURIComponent(jobId)}/accept-drop`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  /** Customer reports bad placement — holds company payout. */
  async reportDropProblem(jobId: string, note: string) {
    return requestJson<{ job: WasteJobBundle; message?: string }>(
      `/jobs/${encodeURIComponent(jobId)}/report-drop-problem`,
      { method: 'POST', body: JSON.stringify({ note }) },
    );
  },

  /** Driver refuses haul — banned items in the dumpster. */
  async rejectLoad(
    jobId: string,
    input: { items: string[]; photoId: string; note?: string | null },
  ) {
    return requestJson<{ job: WasteJobBundle; message?: string }>(
      `/jobs/${encodeURIComponent(jobId)}/reject-load`,
      {
        method: 'POST',
        body: JSON.stringify({
          items: input.items,
          photoId: input.photoId,
          note: input.note || null,
        }),
      },
    );
  },

  /** Customer confirms banned items are out so pickup can continue. */
  async remediateLoad(jobId: string) {
    return requestJson<{ job: WasteJobBundle; message?: string }>(
      `/jobs/${encodeURIComponent(jobId)}/remediate-load`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  async requestPickup(
    jobId: string,
    opts: {
      wantReplacement?: boolean;
      replacementRentalDays?: number;
      successUrl?: string;
      cancelUrl?: string;
    } = {},
  ) {
    return requestJson<{
      job: WasteJobBundle;
      notifiedDrivers?: number;
      notifiedHaulerTeam?: number;
      wantReplacement?: boolean;
      replacementDays?: number | null;
      replacementQuote?: {
        rentalDays: number;
        amountCents: number;
        size: string;
        tonsIncluded?: number;
      } | null;
      checkoutUrl?: string | null;
      sessionId?: string | null;
      address?: string | null;
    }>(`/jobs/${encodeURIComponent(jobId)}/pickup-request`, {
      method: 'POST',
      body: JSON.stringify({
        wantReplacement: Boolean(opts.wantReplacement),
        replacementRentalDays: opts.replacementRentalDays ?? 7,
        successUrl: opts.successUrl || undefined,
        cancelUrl: opts.cancelUrl || undefined,
      }),
    });
  },

  /**
   * Complete pickup — A+: GPS required + serial re-confirm matching drop-off plate.
   */
  /**
   * After pad recovery: LCLF dump + required dumpster serial +
   * green scale ticket photo + Payment Authorized debit slip photo.
   * Company desk is notified with both images.
   */
  async submitLandfillReceipt(
    jobId: string,
    input: {
      serial: string;
      scaleTicketPhotoId: string;
      paymentSlipPhotoId: string;
      /** Required: landfill scale tons (decimals OK, e.g. 1.75). First 1 ton free. */
      scaleWeightTons: number;
      /** Required: green scale ticket # */
      ticketNumber: string;
      /** Required: Scale 2 gross / tare / net lb from green slip */
      grossWtLb: number;
      tareWtLb: number;
      netWtLb: number;
      unit?: string | null;
      description?: string | null;
      rate?: number | string | null;
      rateCents?: number | null;
      extensionFee?: number | string | null;
      extensionCents?: number | null;
      totalNetAmount?: number | string | null;
      totalNetCents?: number | null;
      paymentConfirmation?: string | null;
      notes?: string | null;
      lat?: number | null;
      lng?: number | null;
      truckNumber?: string | null;
      driverName?: string | null;
    },
  ) {
    return requestJson<{
      job: WasteJobBundle;
      photoUrls: string[];
      serial: string;
      overage?: { tons: number | null; cents: number | null; tonsIncluded: number };
      customerInvoiceRequired?: boolean;
      overageCheckoutUrl?: string | null;
      scaleWeightTons?: number;
      ticketNumber?: string;
      grossLb?: number;
      tareLb?: number;
      netLb?: number;
    }>(
      `/jobs/${encodeURIComponent(jobId)}/landfill-receipt`,
      {
        method: 'POST',
        body: JSON.stringify({
          serial: input.serial,
          scaleTicketPhotoId: input.scaleTicketPhotoId,
          paymentSlipPhotoId: input.paymentSlipPhotoId,
          scaleWeightTons: input.scaleWeightTons,
          ticketNumber: input.ticketNumber,
          grossWtLb: input.grossWtLb,
          tareWtLb: input.tareWtLb,
          netWtLb: input.netWtLb,
          unit: input.unit ?? null,
          description: input.description ?? null,
          rate: input.rate ?? null,
          rateCents: input.rateCents ?? null,
          extensionFee: input.extensionFee ?? null,
          extensionCents: input.extensionCents ?? null,
          totalNetAmount: input.totalNetAmount ?? null,
          totalNetCents: input.totalNetCents ?? null,
          paymentConfirmation: input.paymentConfirmation ?? null,
          notes: input.notes ?? null,
          lat: input.lat ?? null,
          lng: input.lng ?? null,
          truckNumber: input.truckNumber ?? null,
          driverName: input.driverName ?? null,
        }),
      },
    );
  },

  async completePickup(
    jobId: string,
    input: {
      lat: number;
      lng: number;
      serial: string;
      accuracyM?: number | null;
      /** @deprecated prefer pickupPhotos (5 required) */
      pickupPhotoId?: string | null;
      /** 4 exterior + 1 interior R2 photo ids */
      pickupPhotos?: Array<{ kind: string; photoId: string }>;
      scaleWeightTons?: number | null;
      overageTons?: number | null;
      forceDistance?: boolean;
      forceCoarseGps?: boolean;
      /** Shown on customer Apple + Telegram: "Shaun (truck #12) picked up…" */
      truckNumber?: string | null;
      trailerNumber?: string | null;
      driverName?: string | null;
      equipmentIssueTags?: string[];
      equipmentIssueNotes?: string | null;
      equipmentIssueSeverity?: string | null;
      /** One proof photo per selected issue chip (R2 ids) — chip = gallery caption. */
      equipmentIssuePhotos?: Array<{ tag: string; photoId: string }>;
    },
  ) {
    return requestJson<{
      job: WasteJobBundle;
      overage?: { tons: number; cents: number; tonsIncluded: number } | null;
      photoCount?: number;
      equipmentIssue?: unknown;
    }>(
      `/jobs/${encodeURIComponent(jobId)}/pickup`,
      {
        method: 'POST',
        body: JSON.stringify({
          lat: input.lat,
          lng: input.lng,
          serial: input.serial,
          accuracyM: input.accuracyM ?? null,
          pickupPhotoId: input.pickupPhotoId || null,
          pickupPhotos: input.pickupPhotos || undefined,
          scaleWeightTons: input.scaleWeightTons,
          overageTons: input.overageTons,
          forceDistance: input.forceDistance,
          forceCoarseGps: input.forceCoarseGps,
          truckNumber: input.truckNumber || undefined,
          trailerNumber: input.trailerNumber || undefined,
          driverName: input.driverName || undefined,
          equipmentIssueTags: input.equipmentIssueTags || undefined,
          equipmentIssueNotes: input.equipmentIssueNotes || undefined,
          equipmentIssueSeverity: input.equipmentIssueSeverity || undefined,
          equipmentIssuePhotos: input.equipmentIssuePhotos?.length ? input.equipmentIssuePhotos : undefined,
        }),
      },
    );
  },

  /** Company fleet board: truck # ↔ trailer # ↔ driver pairings. */
  /** Yard inventory: hauler-owned dumpsters (counts + serials + status). */
  async listInventory(): Promise<WmInventoryUnit[]> {
    const data = await requestJson<{ units?: WmInventoryUnit[] }>('/inventory');
    return data.units || [];
  },

  async addInventoryUnit(input: {
    size: WasteDumpsterSize;
    /** Welded plate / QR serial — required for every physical box. */
    serialNumber: string;
    notes?: string | null;
  }): Promise<WmInventoryUnit | null> {
    const serial = String(input.serialNumber || '').trim();
    if (!serial) {
      throw new Error('Serial number required — enter the welded plate or QR code on the dumpster');
    }
    const data = await requestJson<{ success: boolean; unit?: WmInventoryUnit }>(
      '/inventory',
      {
        method: 'POST',
        body: JSON.stringify({
          size: input.size,
          serialNumber: serial,
          notes: input.notes ?? null,
        }),
      },
    );
    return data.unit || null;
  },

  async updateInventoryUnit(id: string, patch: {
    size?: WasteDumpsterSize;
    /** Welded plate / QR — required when provided (cannot clear). */
    serialNumber?: string;
    status?: WmInventoryStatus;
    notes?: string | null;
  }): Promise<WmInventoryUnit | null> {
    const data = await requestJson<{ success: boolean; unit?: WmInventoryUnit }>(
      `/inventory/${encodeURIComponent(id)}`,
      { method: 'POST', body: JSON.stringify(patch) },
    );
    return data.unit || null;
  },

  async removeInventoryUnit(id: string): Promise<boolean> {
    const data = await requestJson<{ success: boolean }>(
      `/inventory/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    return Boolean(data.success);
  },

  /**
   * After guest checkout + sign-in: attach guest_ph_* rentals to this account.
   * Matches phone / email / explicit guestUserId from checkout.
   */
  async claimGuestRentals(input: {
    phone?: string | null;
    email?: string | null;
    guestUserId?: string | null;
  }): Promise<{ claimedCount: number; claimed: Array<{ id: string }>; message?: string }> {
    const data = await requestJson<{
      success?: boolean;
      claimedCount?: number;
      claimed?: Array<{ id: string }>;
      message?: string;
    }>('/claim-guest-rentals', {
      method: 'POST',
      body: JSON.stringify({
        phone: input.phone || null,
        email: input.email || null,
        guestUserId: input.guestUserId || null,
      }),
    });
    return {
      claimedCount: Number(data.claimedCount) || 0,
      claimed: data.claimed || [],
      message: data.message,
    };
  },

  async listFleet(): Promise<{ pairings: WmFleetPairing[]; activeJobs: WmFleetPairing[] }> {
    const data = await requestJson<{
      pairings?: WmFleetPairing[];
      activeJobs?: WmFleetPairing[];
    }>('/fleet');
    return {
      pairings: data.pairings || [],
      activeJobs: data.activeJobs || [],
    };
  },

  async upsertFleetPairing(input: {
    truckNumber: string;
    trailerNumber?: string | null;
    /** Crew roster user id when known */
    driverUserId?: string | null;
    /** Display name (required when pairing a driver without roster id) */
    driverName?: string | null;
    dumpsterSerial?: string | null;
    jobId?: string | null;
    crewId?: string | null;
    status?: string | null;
  }) {
    return requestJson<{
      success: boolean;
      pairingId?: string;
      truckNumber?: string;
      trailerNumber?: string | null;
      driverUserId?: string | null;
      driverName?: string | null;
    }>(
      '/fleet',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  },

  /** Company driver roster: haulers create drivers and assign rigs. */
  async listCompanyDrivers(): Promise<WmCompanyDriver[]> {
    const data = await requestJson<{ drivers?: WmCompanyDriver[] }>('/fleet/drivers');
    return data.drivers || [];
  },

  async createCompanyDriver(input: {
    driverName: string;
    driverUserId?: string | null;
    username?: string | null;
    email?: string | null;
    truckNumber?: string | null;
    trailerNumber?: string | null;
    crewId?: string | null;
  }): Promise<WmCompanyDriver> {
    const data = await requestJson<{ driver: WmCompanyDriver }>(
      '/fleet/drivers',
      { method: 'POST', body: JSON.stringify(input) },
    );
    return data.driver;
  },

  /** Empty string clears a truck/trailer assignment; undefined keeps it. */
  async updateCompanyDriver(id: string, input: {
    driverName?: string;
    driverUserId?: string | null;
    email?: string | null;
    truckNumber?: string | null;
    trailerNumber?: string | null;
  }): Promise<WmCompanyDriver> {
    const data = await requestJson<{ driver: WmCompanyDriver }>(
      `/fleet/drivers/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    );
    return data.driver;
  },

  async removeCompanyDriver(id: string): Promise<void> {
    await requestJson<{ success: boolean }>(
      `/fleet/drivers/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },

  /** Edit a saved pairing in place (by id, so changing the truck # keeps one row). */
  async updateFleetPairing(id: string, input: {
    truckNumber?: string | null;
    trailerNumber?: string | null;
    driverName?: string | null;
    driverUserId?: string | null;
  }): Promise<void> {
    await requestJson<{ success: boolean }>(
      `/fleet/pairings/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    );
  },

  /** Remove a saved truck ↔ trailer ↔ driver pairing from the fleet board. */
  async deleteFleetPairing(id: string): Promise<void> {
    await requestJson<{ success: boolean }>(
      `/fleet/pairings/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },

  /**
   * Email a roster driver an invite link. The reply carries only a MASKED
   * address — the raw one is never echoed, so a shared desk can confirm "yes,
   * that's them" without exposing the full email.
   */
  async sendDriverInvite(id: string, email?: string): Promise<{
    success: boolean; sent: boolean; email: string;
    /** 'resend' | 'cf_email' | 'smtp' | 'none' — 'none' means no email provider is configured. */
    transport?: string;
    /** Present ONLY when the email did not send: the raw link, to forward by hand. */
    link?: string;
    sendError?: string | null;
  }> {
    return requestJson(
      `/fleet/drivers/${encodeURIComponent(id)}/invite`,
      { method: 'POST', body: JSON.stringify(email ? { email } : {}) },
    );
  },

  /** The driver side: spend a stashed invite token to link the signed-in account. */
  async redeemDriverInvite(token: string): Promise<{ success: boolean; linked: boolean; companyName: string | null }> {
    return requestJson<{ success: boolean; linked: boolean; companyName: string | null }>(
      '/fleet/driver-invite/redeem',
      { method: 'POST', body: JSON.stringify({ token }) },
    );
  },

  /** Mid-run equipment issue (box dent, door, trailer hydraulics/tires…). */
  async reportEquipmentIssue(
    jobId: string,
    input: {
      tags: string[];
      notes?: string | null;
      severity?: string | null;
      phase?: string | null;
    },
  ) {
    return requestJson<{ job: WasteJobBundle; equipmentIssue: unknown }>(
      `/jobs/${encodeURIComponent(jobId)}/equipment-issue`,
      {
        method: 'POST',
        body: JSON.stringify({
          equipmentIssueTags: input.tags,
          equipmentIssueNotes: input.notes || null,
          equipmentIssueSeverity: input.severity || 'medium',
          phase: input.phase || 'mid_run',
        }),
      },
    );
  },

  /** Ops: mark hauler share paid out of band (until Stripe Connect). */
  async markHaulerPayout(
    jobId: string,
    input: { status?: 'paid_out' | 'pending_ops' | 'ready_for_payout' | 'hold'; note?: string | null } = {},
  ) {
    return requestJson<{ job: WasteJobBundle }>(`/jobs/${encodeURIComponent(jobId)}/payout`, {
      method: 'POST',
      body: JSON.stringify({
        status: input.status || 'paid_out',
        note: input.note || null,
      }),
    });
  },

  /** Ops retry when the automatic cancel refund failed at Stripe. */
  async retryCancelRefund(jobId: string) {
    return requestJson<{ success: boolean; refundId?: string; job?: WasteJobBundle }>(
      `/jobs/${encodeURIComponent(jobId)}/refund`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  /** Ops board: paid jobs awaiting hauler settlement. */
  async listPayouts(view: 'pending' | 'paid_out' | 'all' = 'pending'): Promise<{
    payouts: Array<{
      jobId: string;
      siteName?: string | null;
      size?: string | null;
      contractorPayoutCents?: number | null;
      haulerPayoutStatus?: string | null;
      amountCents?: number | null;
      status?: string | null;
    }>;
    totalPendingCents: number;
    count: number;
  }> {
    const data = await requestJson<{
      payouts?: Array<Record<string, unknown>>;
      totalPendingCents?: number;
      count?: number;
    }>(`/payouts?view=${encodeURIComponent(view)}`);
    return {
      payouts: (data.payouts || []).map((p) => ({
        jobId: String(p.jobId || p.id || ''),
        siteName: (p.siteName as string) || null,
        size: (p.size as string) || null,
        contractorPayoutCents: p.contractorPayoutCents != null ? Number(p.contractorPayoutCents) : null,
        haulerPayoutStatus: (p.haulerPayoutStatus as string) || 'pending_ops',
        amountCents: p.amountCents != null ? Number(p.amountCents) : null,
        status: (p.status as string) || null,
      })),
      totalPendingCents: Number(data.totalPendingCents) || 0,
      count: Number(data.count) || 0,
    };
  },

  /** Stripe Checkout for scale overage (physical service — external pay). */
  async createOverageCheckout(
    jobId: string,
    successUrl?: string,
    cancelUrl?: string,
    opts?: { sendEmail?: boolean; forceNew?: boolean; customerEmail?: string | null },
  ) {
    return requestJson<{
      success: boolean;
      checkoutUrl: string;
      sessionId: string;
      amountCents: number;
      emailed?: boolean;
      email?: string | null;
      emailError?: string | null;
      reused?: boolean;
    }>(`/jobs/${encodeURIComponent(jobId)}/overage-checkout`, {
      method: 'POST',
      body: JSON.stringify({
        successUrl,
        cancelUrl,
        sendEmail: opts?.sendEmail,
        forceNew: opts?.forceNew,
        customerEmail: opts?.customerEmail,
      }),
    });
  },

  /** Customer: reject overage when charge does not match green scale ticket. */
  async rejectOverage(jobId: string, reason: string) {
    return requestJson<{
      success: boolean;
      alreadyDisputed?: boolean;
      status?: string;
      job: WasteJobBundle;
    }>(`/jobs/${encodeURIComponent(jobId)}/overage-reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /** Desk: waive | reaffirm | adjust after customer dispute. */
  async resolveOverage(
    jobId: string,
    input: {
      action: 'waive' | 'reaffirm' | 'adjust';
      note?: string | null;
      overageCents?: number | null;
      overageTons?: number | null;
    },
  ) {
    return requestJson<{
      success: boolean;
      status?: string;
      checkoutUrl?: string | null;
      emailed?: boolean;
      email?: string | null;
      job: WasteJobBundle;
    }>(`/jobs/${encodeURIComponent(jobId)}/overage-resolve`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async listInbox(unread = true): Promise<Array<{
    id: string;
    job_id: string | null;
    event_type: string;
    title: string;
    body: string;
    created_at: string;
    read_at: string | null;
  }>> {
    const data = await requestJson<{ notifications?: any[] }>(
      `/inbox?unread=${unread ? '1' : '0'}&limit=20`,
    );
    return data.notifications || [];
  },

  async markInboxRead(ids: string[]) {
    if (!ids.length) return;
    await requestJson('/inbox', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  },

  async sendMessage(jobId: string, message: string) {
    return requestJson<{ job: WasteJobBundle }>(`/jobs/${encodeURIComponent(jobId)}/message`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },

  async cancelJob(
    jobId: string,
    reason?: string,
  ): Promise<{
    job: WasteJobBundle;
    cancel?: {
      tier: WmCancelTier;
      cancelFeeCents: number;
      platformFeeCents: number;
      contractorFeeCents: number;
      stripeFeeCents: number;
      refundCents: number;
    };
  }> {
    return requestJson(`/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'Cancelled by user' }),
    });
  },

  /** Preview cancel fee before confirming (client-side; server recomputes). */
  previewCancel(job: {
    status?: string | null;
    amount_cents?: number | null;
    stripe_fee_cents?: number | null;
    delivery_date?: string | null;
    payment_status?: string | null;
  }) {
    const tier = wmCancelTier(job);
    const paid = String(job.payment_status || '').toLowerCase() === 'paid'
      ? Math.floor(Number(job.amount_cents) || 0)
      : 0;
    const settlement = paid > 0
      ? wmCancelSettlementCents({
        amountPaidCents: paid,
        tier,
        stripeFeeCents: job.stripe_fee_cents,
      })
      : {
        tier,
        cancelFeeCents: Math.round(wmCancelFeeUsd(tier) * 100),
        platformFeeCents: Math.floor((Math.round(wmCancelFeeUsd(tier) * 100) * WM_PLATFORM_FEE_BPS) / 10000),
        contractorFeeCents: 0,
        stripeFeeCents: 0,
        refundCents: 0,
        keptCents: Math.round(wmCancelFeeUsd(tier) * 100),
      };
    if (paid <= 0) {
      settlement.contractorFeeCents = settlement.cancelFeeCents - settlement.platformFeeCents;
    }
    return settlement;
  },

  async reassignWorker(jobId: string, workerUserId: string, workerUsername?: string | null) {
    return requestJson<{ job: WasteJobBundle }>(`/jobs/${encodeURIComponent(jobId)}/reassign`, {
      method: 'POST',
      body: JSON.stringify({ userId: workerUserId, username: workerUsername }),
    });
  },

  /**
   * WM fab badge count — paid open work (claimable + pickup ready + assigned),
   * not en-route noise. Customers may still see unpaid self-checkout items.
   * Server `/jobs/action-count` already applies the paid-open badge rules.
   */
  async getActionCount(): Promise<number> {
    try {
      // Demo / guest sessions have no jobs — skip the 401 noise.
      const { hasAppAuthMaterial } = await import('./appAuthMaterial');
      if (!hasAppAuthMaterial()) return 0;
      const data = await requestJson<{ count: number; badge?: string }>('/jobs/action-count');
      return Number(data.count) || 0;
    } catch {
      return 0;
    }
  },
};
